import { Fragment, useState, useMemo, useEffect, useCallback, useRef } from "react";
import { PublicClientApplication, InteractionRequiredAuthError } from "@azure/msal-browser";

// ─── SEARCHABLE SELECT (sorted, type-to-filter combobox) ───────────────────────
// Drop-in replacement for native <select> on long, data-driven lists.
// Options: [{ value, label, sortKey? }]. onChange receives the chosen value.
//  • options are sorted alphabetically (case-insensitive) by sortKey ?? label
//  • typing filters by case-insensitive substring of label — multiple chars,
//    the typed text stays in the input and the list filters as you type
//  • shows "No results found" when nothing matches; click a row to select
function SearchableSelect({ value, onChange, options, placeholder = "Select…", disabled = false, style }) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);

  // Sorted copy — never mutate the source array (it's often React state).
  const sorted = useMemo(
    () => [...options].sort((a, b) =>
      (a.sortKey ?? a.label).localeCompare(b.sortKey ?? b.label, undefined, { sensitivity: "base" })),
    [options]
  );
  const selected = sorted.find(o => o.value === value) || null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? sorted.filter(o => o.label.toLowerCase().includes(q)) : sorted;
  }, [sorted, query]);

  // Close (and clear the search) when clicking outside the widget.
  useEffect(() => {
    if (!open) return;
    const onDoc = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) { setOpen(false); setQuery(""); }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const choose = o => { onChange(o.value); setOpen(false); setQuery(""); };

  return (
    <div ref={wrapRef} style={{ position: "relative", ...style }}>
      <input
        className="inp"
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="off"
        // While open the input mirrors the live search text; when closed it shows
        // the selected label. This is what lets the user type freely without the
        // value snapping back after one character.
        value={open ? query : (selected ? selected.label : "")}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { if (!disabled) { setQuery(""); setOpen(true); } }}
      />
      {open && !disabled && (
        <div style={{ position:"absolute",top:"calc(100% + 4px)",left:0,right:0,background:"#fff",border:"1px solid #D8DCE8",borderRadius:8,boxShadow:"0 6px 20px rgba(20,30,60,.12)",maxHeight:240,overflowY:"auto",zIndex:60 }}>
          {filtered.length === 0
            ? <div style={{ padding:"9px 13px",fontSize:13,color:"#8A93AA" }}>No results found</div>
            : filtered.map(o => {
                const isSel = o.value === value;
                return (
                  // onMouseDown (not onClick) fires before the input blur, so the
                  // selection isn't lost to the outside-click handler.
                  <div key={o.value}
                    onMouseDown={e => { e.preventDefault(); choose(o); }}
                    style={{ padding:"9px 13px",fontSize:13,cursor:"pointer",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",background:isSel?"#EBF0FF":"transparent",color:isSel?"#1D4ED8":"#1A1F36" }}
                    onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = "#F4F6FA"; }}
                    onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = "transparent"; }}>
                    {o.label}
                  </div>
                );
              })}
        </div>
      )}
    </div>
  );
}

// ─── MSAL CONFIG ─────────────────────────────────────────────────────────────
const MSAL_CONFIG = {
  auth: {
    clientId:    "e3cc76d0-0331-4564-9fd6-5711b87a4c06",
    authority:   "https://login.microsoftonline.com/0b2e0a2e-6165-4d0f-9243-04be61c53b86",
    redirectUri: window.location.origin,
  },
  // storeAuthStateInCookie keeps the auth-redirect state in a cookie as well as
  // sessionStorage, so it survives the KMSI / cross-page redirect in browsers with
  // partitioned or restricted storage (mitigates AADSTS50140 sign-in hangs).
  cache: { cacheLocation:"sessionStorage", storeAuthStateInCookie:true },
};
const msalInstance = new PublicClientApplication(MSAL_CONFIG);
await msalInstance.initialize();

// Only need User.Read now — the Function API handles SharePoint with its own identity
const LOGIN_SCOPES = ["User.Read"];
// Backend Function API base URL — overridable per environment.
// Dev: set REACT_APP_API_BASE=http://localhost:7071/api (see .env.development.local)
// Prod fallback: the deployed Function App
const API_BASE = process.env.REACT_APP_API_BASE || "https://licenseops-api-bpe6cdbncrhsgzca.westeurope-01.azurewebsites.net/api";

// Fired when the session is unrecoverable (silent renewal dead, or the API rejects
// our token). The App component listens and drops the UI back to the login screen —
// getApiToken/apiFetch are module-level so they can't reach React state directly.
const SESSION_EXPIRED_EVENT = "lo-session-expired";
const notifySessionExpired = () => window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));

// Acquire an ID token to send to our Function API
async function getApiToken() {
  const accounts = msalInstance.getAllAccounts();
  if (!accounts.length) { notifySessionExpired(); throw new Error("Not signed in"); }
  try {
    // Request an ID token (our API validates the ID token audience = our client ID)
    let res = await msalInstance.acquireTokenSilent({ scopes:LOGIN_SCOPES, account:accounts[0] });
    // MSAL only renews when the ACCESS token is stale — it happily returns a cached
    // ID token that has already expired, which our API then 401s. Check the ID
    // token's own exp and force a renewal when it's expired (or about to).
    const exp = res.idTokenClaims?.exp;
    if (exp && exp * 1000 < Date.now() + 60000) {
      res = await msalInstance.acquireTokenSilent({ scopes:LOGIN_SCOPES, account:accounts[0], forceRefresh:true });
    }
    return res.idToken;
  } catch(e) {
    if (e instanceof InteractionRequiredAuthError) {
      // Entra session still recoverable interactively → full-page redirect to sign
      // back in. If even that fails to start, fall through to the logout below.
      try { await msalInstance.acquireTokenRedirect({ scopes:LOGIN_SCOPES }); } catch { notifySessionExpired(); }
    } else {
      notifySessionExpired();
    }
    throw e;
  }
}

// ─── FUNCTION API CLIENT ──────────────────────────────────────────────────────
async function apiFetch(path, opts={}) {
  const token = await getApiToken();
  const res   = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { Authorization:`Bearer ${token}`, "Content-Type":"application/json", ...(opts.headers||{}) }
  });
  if (res.status === 401) {
    // The API no longer accepts our token — session expired server-side.
    notifySessionExpired();
    throw new Error("Your session has expired — please sign in again");
  }
  if (!res.ok) {
    let msg = `API ${opts.method||"GET"} ${path} → ${res.status}`;
    try { const e = await res.json(); if (e.error) msg = e.error; } catch {}
    throw new Error(msg);
  }
  return res.status===204 ? null : res.json();
}

// Fetch all data in one call
async function fetchAllData() {
  return apiFetch("/data");
}
// Fetch Entra users (sync)
async function fetchEntraUsers() {
  return apiFetch("/entra-users");
}
// Entra object ids of the caller's direct reports (scopes a Manager's approval queue)
async function fetchMyReports() {
  return apiFetch("/my-reports");
}
// Atlassian org snapshot — products owned + per-user product access (Admin/ITManager)
async function fetchAtlassian() {
  return apiFetch("/atlassian");
}
// Slack workspace snapshot — billable members mapped to the configured tier (Admin/ITManager)
async function fetchSlack() {
  return apiFetch("/slack");
}
// GitHub org snapshot — members (login + best-effort email) mapped to the configured
// tier (Admin/ITManager). Matched to users by the per-user GitHub field, not email.
async function fetchGitHub() {
  return apiFetch("/github");
}
// Recent license-change history for reporting (Admin/ITManager/Finance).
async function fetchActivity() {
  return apiFetch("/activity");
}

// Microsoft license SKUs (GUID → friendly product name). GUIDs are global Microsoft
// constants (from the published licensing reference), so a user's assignedLicenses
// can be mapped to a product name with no extra Graph permission. Extend as needed.
const MS_SKUS = [
  { id:"05e9a617-0261-4cee-bb44-138d3ef5d965", name:"Microsoft 365 E3" },
  { id:"06ebc4ee-1bb5-47dd-8120-11324bc54e06", name:"Microsoft 365 E5" },
  { id:"66b55226-6b4f-492c-910c-a3b7a3c9d993", name:"Microsoft 365 F3" },
  { id:"3b555118-da6a-4418-894f-7df1e2096870", name:"Microsoft 365 Business Basic" },
  { id:"f245ecc8-75af-4f8e-b61f-27d8114de5f3", name:"Microsoft 365 Business Standard" },
  { id:"cbdc14ab-d96c-4c30-b9f4-6ada7cdc1d46", name:"Microsoft 365 Business Premium" },
  { id:"c2273bd0-dff7-4215-9ef5-2c7bcfb06425", name:"Microsoft 365 Apps for Enterprise" },
  { id:"cdd28e44-67e3-425e-be4c-737fab2899d3", name:"Microsoft 365 Apps for Business" },
  { id:"18181a46-0d4e-45cd-891e-60aabd171b4e", name:"Office 365 E1" },
  { id:"6fd2c87f-b296-42f0-b197-1e91e994b900", name:"Office 365 E3" },
  { id:"c7df2760-2c81-4ef7-b578-5b5392b571df", name:"Office 365 E5" },
  { id:"4b585984-651b-448a-9e53-3b10f069cf7f", name:"Office 365 F3" },
  { id:"4b9405b0-7788-4568-add1-99614e613b69", name:"Exchange Online (Plan 1)" },
  { id:"19ec0d23-8335-4cbd-94ac-6050e30712fa", name:"Exchange Online (Plan 2)" },
  { id:"f8a1db68-be16-40ed-86d5-cb42ce701560", name:"Power BI Pro" },
  { id:"53818b1b-4a27-454b-8896-0dba576410e6", name:"Project Plan 3" },
  { id:"4b244418-9658-4451-a2b8-b5e2b364e9bd", name:"Visio Plan 1" },
  { id:"c5928f49-12ba-48f7-ada3-0d743a3601d5", name:"Visio Plan 2" },
  { id:"efccb6f7-5641-4e0e-bd10-b4976e1bf68e", name:"Enterprise Mobility + Security E3" },
  { id:"b05e124f-c7cc-45a0-a6aa-8cf78c946968", name:"Enterprise Mobility + Security E5" },
  { id:"e6778190-713e-4e4f-9119-8b8238de25df", name:"OneDrive for Business (Plan 1)" },
  { id:"ed01faf2-1d88-4947-ae91-45ca18703a96", name:"OneDrive for Business (Plan 2)" },
  { id:"fde42873-30b6-436b-b361-21af5a6b84ae", name:"Microsoft Teams Essentials" },
  { id:"36a0f3b3-adb5-49ea-bf66-762134cf063a", name:"Microsoft Teams Premium" },
];
const MS_SKU_NAME = Object.fromEntries(MS_SKUS.map(s=>[s.id, s.name]));

// Microsoft license snapshot (Admin/ITManager). Returns each user's assigned SKU ids.
async function fetchMicrosoft() {
  return apiFetch("/microsoft");
}
// Transform the Graph license data into the standard { products, users } payload by
// mapping each user's SKU ids to the catalog tiers the admin tagged with that SKU
// (tier.msSkuId). Assignment-only — the tiers already exist in the catalog.
async function fetchMicrosoftPayload(catalog) {
  const { users } = await fetchMicrosoft();
  const products = catalog.flatMap(a => (a.tiers||[]).filter(t=>t.msSkuId&&!t.visibilityOnly).map(t => ({
    key: t.msSkuId, appId: a.id, name: a.name, icon: a.icon, category: a.category, priced: true, tier: t,
  })));
  const mapped = new Set(products.map(p=>p.key));
  const outUsers = (users||[]).map(u => ({ email:u.email, name:u.name, products:(u.skuIds||[]).filter(id=>mapped.has(id)) }));
  return { products, users: outUsers };
}

// Vendor sync providers. Each emits the same { products, users } payload; `ownsApp`
// identifies the catalog apps that provider manages (used for revocation drift).
// Microsoft maps SKUs to existing tiers, so its owned set comes from the synced
// product tiers (ownsApp:false avoids flagging unmapped tiers of the same app).
//
// Matching: by default a vendor account is matched to a LicenseOps user by email.
// A provider may override `loKeys` (identities to match a LO user on) and `srcKeys`
// (identities a vendor account carries) — GitHub matches on the per-user `github`
// field (username or email), falling back to corporate email.
const SYNC_PROVIDERS = [
  { key:"atlassian", label:"Atlassian", fetch:fetchAtlassian, ownsApp:a=>a.id.startsWith("atlassian-") },
  { key:"slack",     label:"Slack",     fetch:fetchSlack,     ownsApp:a=>a.id==="slack" },
  { key:"github",    label:"GitHub",    fetch:fetchGitHub,    ownsApp:a=>a.id==="github",
    loKeys:  u  => [u.github, u.email],
    srcKeys: su => [su.login, su.email] },
  { key:"microsoft", label:"Microsoft", fetch:fetchMicrosoftPayload, ownsApp:()=>false },
];
// List item CRUD — go through the function
async function createListItem(listName, fields) {
  return apiFetch(`/list/${listName}`, { method:"POST", body:JSON.stringify({ fields }) });
}
async function updateListItem(listName, itemId, fields) {
  return apiFetch(`/list/${listName}/${itemId}`, { method:"PATCH", body:JSON.stringify({ fields }) });
}
async function deleteListItem(listName, itemId) {
  return apiFetch(`/list/${listName}/${itemId}/delete`, { method:"DELETE" });
}

// ─── DATA MAPPERS ─────────────────────────────────────────────────────────────
const spToUser = item => ({
  id:     item.fields.EntraId || item.id,
  spId:   item.id,
  name:   item.fields.Title,
  email:  item.fields.Email || "",
  dept:   item.fields.Dept  || "",
  manager: item.fields.Manager || "",   // manager display name, synced from Entra
  github: item.fields.GitHubId || "",   // username or email used on GitHub (for sync match)
  tiers:  JSON.parse(item.fields.Tiers || "[]"),
});

const spToOrphan = item => ({
  spId:    item.id,
  email:   item.fields.Title || "",
  vendor:  item.fields.Vendor || "",
  name:    item.fields.UserName || "",
  products:item.fields.Products || "",
  appIds:  (item.fields.AppIds || "").split(",").map(s=>s.trim()).filter(Boolean),
  estMonthly: Number(item.fields.EstMonthly || 0),
  status:  item.fields.Status || "Open",
  date:    item.fields.DetectedDate || "",
  comment: item.fields.Comment || "",
});

const spToApp = item => {
  try { return { ...JSON.parse(item.fields.AppData), spId:item.id }; }
  catch { return null; }
};

const spToRequest = item => {
  const f = item.fields;
  // NewApp: proposed-software details for a request whose app isn't in the catalog
  // yet — { name, tier, options, link }. Kept after the admin attaches a real
  // catalog app (appId/tierId filled in) as a record of what was asked for.
  let newApp = null;
  try { if (f.NewApp) newApp = JSON.parse(f.NewApp); } catch {}
  return {
    id: f.RequestId || item.id, spId: item.id,
    userId: f.UserId, appId: f.AppId||"", tierId: f.TierId||"",
    justification: f.Justification||"", by: f.By||"",
    date: f.ReqDate||"", status: f.Status||"Pending",
    approvalLevel: f.ApprovalLevel||"", note: f.Note||"", newApp,
  };
};

// ─── SHAREPOINT DATA HOOK ────────────────────────────────────────────────────
// spMap (SharePoint item IDs) is kept in a ref so the save/remove callbacks are
// STABLE — they never change identity, which prevents re-render/request loops.
function useSharePointData() {
  const [users,        setUsers]        = useState([]);
  const [catalog,      setCatalog]      = useState(CATALOG);
  const [requests,     setRequests]     = useState([]);
  const [deptDefaults, setDeptDefaults] = useState({});
  const [orphans,      setOrphans]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [spError,      setSpError]      = useState(null);
  const [config,       setConfig]       = useState(DEFAULT_APPROVAL_CFG);
  // Org-wide seat counts (flat-price divisor) supplied by the API for scoped roles
  // whose `users` is filtered server-side. null = compute locally from `users`.
  const [serverSeatCounts, setServerSeatCounts] = useState(null);

  // spId lookup maps live in a ref, not state — mutating them never triggers a render.
  // config holds the single LO_Config row's item id (null until first save).
  // orphans keyed by vendor|email → spId, for dedup/resolve.
  const spMap = useRef({ users:{}, catalog:{}, requests:{}, depts:{}, config:null, orphans:{} });

  useEffect(() => {
    (async () => {
      try {
        const { users:uItems, catalog:cItems, requests:rItems, deptDefaults:dItems, config:cfgItems, orphans:oItems, seatCounts:srvSeats } = await fetchAllData();
        if (srvSeats) setServerSeatCounts(srvSeats);
        const mappedUsers    = uItems.map(spToUser).filter(Boolean);
        const mappedCatalog  = cItems.map(spToApp).filter(Boolean);
        const mappedRequests = rItems.map(spToRequest).filter(Boolean);
        const mappedDepts = {};
        dItems.forEach(item => {
          const dept    = item.fields.Title || item.fields.Dept || "";
          const tierIds = JSON.parse(item.fields.TierIds||"[]");
          if (dept) { mappedDepts[dept] = tierIds; spMap.current.depts[dept] = item.id; }
        });
        if (mappedUsers.length)    setUsers(mappedUsers);
        if (mappedCatalog.length)  setCatalog(mappedCatalog);
        if (mappedRequests.length) setRequests(mappedRequests);
        setDeptDefaults(mappedDepts);
        // LO_Config: a single row holding the approval thresholds. Absent → defaults.
        const cfgItem = (cfgItems || [])[0];
        if (cfgItem) {
          spMap.current.config = cfgItem.id;
          const loaded = {
            autoMax: Number(cfgItem.fields.AutoMax ?? DEFAULT_APPROVAL_CFG.autoMax),
            mgrMax:  Number(cfgItem.fields.MgrMax  ?? DEFAULT_APPROVAL_CFG.mgrMax),
          };
          APPROVAL_CFG = loaded;
          setConfig(loaded);
        }
        const mappedOrphans = (oItems || []).map(spToOrphan);
        setOrphans(mappedOrphans);
        mappedUsers.forEach(u    => spMap.current.users[u.id]    = u.spId);
        mappedCatalog.forEach(a  => spMap.current.catalog[a.id]  = a.spId);
        mappedRequests.forEach(r => spMap.current.requests[r.id] = r.spId);
        mappedOrphans.forEach(o  => spMap.current.orphans[`${o.vendor}|${o.email.toLowerCase()}`] = o.spId);
      } catch(e) {
        setSpError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const saveUser = useCallback(async (user) => {
    const fields = { Title:user.name, EntraId:String(user.id), Email:user.email, Dept:user.dept, Tiers:JSON.stringify(user.tiers) };
    // Only send GitHubId once it has a value — otherwise a tenant whose LO_Users list
    // hasn't had the GitHubId column added yet would reject every user save.
    if (user.github) fields.GitHubId = user.github;
    // Same guard for Manager: only send it once populated, so a list without the
    // Manager column doesn't reject every user save.
    if (user.manager) fields.Manager = user.manager;
    const spId   = spMap.current.users[user.id];
    if (spId) { await updateListItem("LO_Users", spId, fields); }
    else      { const item = await createListItem("LO_Users", fields); spMap.current.users[user.id] = item.id; }
  }, []);

  const saveApp = useCallback(async (app) => {
    const { spId:_, ...appData } = app;
    const fields = { Title:app.name, AppId:app.id, AppData:JSON.stringify(appData) };
    const spId   = spMap.current.catalog[app.id];
    if (spId) { await updateListItem("LO_Catalog", spId, fields); }
    else      { const item = await createListItem("LO_Catalog", fields); spMap.current.catalog[app.id] = item.id; }
  }, []);

  const saveRequest = useCallback(async (req) => {
    const fields = { Title:String(req.id), RequestId:String(req.id), UserId:String(req.userId), AppId:req.appId, TierId:req.tierId, Justification:req.justification, By:req.by, ReqDate:req.date, Status:req.status, ApprovalLevel:req.approvalLevel, Note:req.note||"" };
    // Only send NewApp when set — a tenant whose LO_Requests list doesn't have the
    // NewApp column yet would otherwise reject every request save (same guard as
    // GitHubId/Manager on LO_Users).
    if (req.newApp) fields.NewApp = JSON.stringify(req.newApp);
    const spId   = spMap.current.requests[req.id];
    if (spId) { await updateListItem("LO_Requests", spId, fields); }
    else      { const item = await createListItem("LO_Requests", fields); spMap.current.requests[req.id] = item.id; }
  }, []);

  const saveDeptDefault = useCallback(async (dept, tierIds) => {
    const fields = { Title:dept, Dept:dept, TierIds:JSON.stringify(tierIds) };
    const spId   = spMap.current.depts[dept];
    if (spId) { await updateListItem("LO_DeptDefaults", spId, fields); }
    else      { const item = await createListItem("LO_DeptDefaults", fields); spMap.current.depts[dept] = item.id; }
    setDeptDefaults(p => ({ ...p, [dept]:tierIds }));
  }, []);

  const removeDeptDefault = useCallback(async (dept) => {
    const spId = spMap.current.depts[dept];
    if (spId) await deleteListItem("LO_DeptDefaults", spId);
    delete spMap.current.depts[dept];
    setDeptDefaults(p => { const n={...p}; delete n[dept]; return n; });
  }, []);

  const saveConfig = useCallback(async (next) => {
    const cfg    = { autoMax:Number(next.autoMax), mgrMax:Number(next.mgrMax) };
    const fields = { Title:"thresholds", AutoMax:cfg.autoMax, MgrMax:cfg.mgrMax };
    const spId   = spMap.current.config;
    if (spId) { await updateListItem("LO_Config", spId, fields); }
    else      { const item = await createListItem("LO_Config", fields); spMap.current.config = item.id; }
    APPROVAL_CFG = cfg;   // module-level source of truth read by approvalTier
    setConfig(cfg);       // state change re-renders everything that shows a tier
  }, []);

  const removeAppFromSP = useCallback(async (appId) => {
    const spId = spMap.current.catalog[appId];
    if (spId) await deleteListItem("LO_Catalog", spId);
    delete spMap.current.catalog[appId];
  }, []);

  const removeUser = useCallback(async (userId) => {
    const spId = spMap.current.users[userId];
    if (spId) await deleteListItem("LO_Users", spId);
    delete spMap.current.users[userId];
    setUsers(p => p.filter(u => u.id !== userId));
  }, []);

  // Log orphans found during a sync. Dedups by vendor|email — an existing entry
  // (any status) is left as-is so a resolved one isn't reopened. Best-effort:
  // if LO_Orphans doesn't exist yet the writes fail silently.
  const logOrphans = useCallback(async (vendor, list) => {
    const created = []; let failed = 0;
    for (const o of list) {
      const key = `${vendor}|${(o.email||"").toLowerCase()}`;
      if (spMap.current.orphans[key]) continue;             // already logged
      const fields = { Title:o.email||"", Vendor:vendor, UserName:o.name||"", Products:(o.products||[]).join(", "), AppIds:(o.appIds||[]).join(","), EstMonthly:Number(o.estMonthly||0), Status:"Open", DetectedDate:TODAY.toISOString().split("T")[0] };
      try {
        const item = await createListItem("LO_Orphans", fields);
        spMap.current.orphans[key] = item.id;
        created.push({ spId:item.id, email:o.email||"", vendor, name:o.name||"", products:(o.products||[]).join(", "), appIds:o.appIds||[], estMonthly:Number(o.estMonthly||0), status:"Open", date:fields.DetectedDate, comment:"" });
      } catch(e) { console.error("logOrphans", e); failed++; }
    }
    if (created.length) setOrphans(p => [...p, ...created]);
    return { logged: created.length, failed };
  }, []);

  const resolveOrphan = useCallback(async (orphan) => {
    const spId = orphan.spId || spMap.current.orphans[`${orphan.vendor}|${(orphan.email||"").toLowerCase()}`];
    if (spId) await updateListItem("LO_Orphans", spId, { Status:"Resolved" }).catch(console.error);
    setOrphans(p => p.map(o => o.spId===spId ? { ...o, status:"Resolved" } : o));
  }, []);

  // Upsert Entra users that have NO department as vendor="Entra" orphans, called on
  // every Entra sync. Unlike logOrphans this also UPDATES: refreshes a changed name,
  // and auto-resolves an open entry once its user gains a department (or leaves
  // Entra). A manually-resolved entry is never reopened; comments are never touched.
  // currentOrphans is passed in so this callback stays stable (empty deps).
  const syncEntraOrphans = useCallback(async (currentOrphans, noDeptUsers) => {
    const today = TODAY.toISOString().split("T")[0];
    const byEmail = {};
    currentOrphans.filter(o => o.vendor === "Entra").forEach(o => { byEmail[(o.email||"").toLowerCase()] = o; });
    const stillNoDept = new Set(noDeptUsers.map(u => (u.email||"").toLowerCase()));
    const created = [], renamed = {}, resolvedIds = []; let failed = 0;
    for (const u of noDeptUsers) {
      const existing = byEmail[(u.email||"").toLowerCase()];
      try {
        if (!existing) {
          const fields = { Title:u.email, Vendor:"Entra", UserName:u.name||"", Products:"No department", EstMonthly:0, Status:"Open", DetectedDate:today };
          const item = await createListItem("LO_Orphans", fields);
          spMap.current.orphans[`Entra|${u.email.toLowerCase()}`] = item.id;
          created.push({ spId:item.id, email:u.email, vendor:"Entra", name:u.name||"", products:"No department", appIds:[], estMonthly:0, status:"Open", date:today, comment:"" });
        } else if ((existing.name||"") !== (u.name||"")) {
          await updateListItem("LO_Orphans", existing.spId, { UserName:u.name||"" });
          renamed[existing.spId] = u.name||"";
        }
      } catch(e) { console.error("syncEntraOrphans", e); failed++; }
    }
    // Auto-resolve: the user now has a department, or is gone from Entra entirely.
    for (const o of currentOrphans) {
      if (o.vendor !== "Entra" || o.status === "Resolved" || stillNoDept.has((o.email||"").toLowerCase())) continue;
      await updateListItem("LO_Orphans", o.spId, { Status:"Resolved" }).catch(console.error);
      resolvedIds.push(o.spId);
    }
    setOrphans(p => [
      ...p.map(o => {
        let n = o;
        if (renamed[o.spId] !== undefined) n = { ...n, name:renamed[o.spId] };
        if (resolvedIds.includes(o.spId))  n = { ...n, status:"Resolved" };
        return n;
      }),
      ...created,
    ]);
    return { logged:created.length, resolved:resolvedIds.length, failed };
  }, []);

  const saveOrphanComment = useCallback(async (spId, comment) => {
    await updateListItem("LO_Orphans", spId, { Comment: comment });
    setOrphans(p => p.map(o => o.spId===spId ? { ...o, comment } : o));
  }, []);

  return { users, setUsers, catalog, setCatalog, requests, setRequests, deptDefaults, orphans, config, serverSeatCounts, loading, spError, saveUser, saveApp, saveRequest, saveDeptDefault, removeDeptDefault, removeAppFromSP, removeUser, saveConfig, logOrphans, resolveOrphan, syncEntraOrphans, saveOrphanComment };
}

const TODAY = new Date();
const daysUntil = d => Math.round((new Date(d) - TODAY) / 86400000);
const fmtDate   = d => new Date(d).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"});
const monthlyEq = l => l.billing==="monthly" ? l.costPerUnit : l.billing==="annual" ? l.costPerUnit/12 : 0;
// GitHub orphans are keyed by username — link the handle to its profile so an admin
// can see who it is. Returns null for anything that looks like an email.
const ghProfileUrl = handle => (handle && !String(handle).includes("@")) ? `https://github.com/${encodeURIComponent(handle)}` : null;
const eur       = n  => `€${Number(n).toFixed(2)}`;
const eurInt    = n  => `€${Math.round(n)}`;
const ul        = t  => t?.billing==="annual"?"/yr":t?.billing==="monthly"?"/mo":" one-time";

// Auto-approve thresholds (monthly-equivalent €). Admin-editable, loaded from the
// LO_Config SharePoint list. Held at module scope so approvalTier — called from
// several components — needs no extra plumbing; useSharePointData updates it on
// load and on admin save (paired with a state change so the UI re-renders).
const DEFAULT_APPROVAL_CFG = { autoMax: 15, mgrMax: 50 };
let APPROVAL_CFG = { ...DEFAULT_APPROVAL_CFG };

const approvalTier = (c, cfg = APPROVAL_CFG) => {
  if (c < cfg.autoMax) return { label:"Auto-Approve", color:"#10B981", threshold:`< €${cfg.autoMax}/mo` };
  if (c < cfg.mgrMax)  return { label:"Manager",      color:"#F59E0B", threshold:`€${cfg.autoMax}–€${cfg.mgrMax}/mo` };
  return                      { label:"IT + Finance",  color:"#EF4444", threshold:`> €${cfg.mgrMax}/mo` };
};

const CATALOG = [
  { id:"m365",    name:"Microsoft 365",        category:"Productivity",  isDefault:true,  icon:"🟦", tiers:[
    { tierId:"m365-biz",    label:"Business Basic",   billing:"monthly",  seatModel:"per-seat", costPerUnit:6.00,   type:"Standard" },
    { tierId:"m365-e3",     label:"E3",                billing:"monthly",  seatModel:"per-seat", costPerUnit:36.00,  type:"Standard" },
    { tierId:"m365-e5",     label:"E5",                billing:"monthly",  seatModel:"per-seat", costPerUnit:57.00,  type:"Standard" },
  ]},
  { id:"visio",   name:"Visio",                category:"Productivity",  isDefault:false, icon:"📐", tiers:[
    { tierId:"visio-p1",    label:"Plan 1",            billing:"monthly",  seatModel:"per-seat", costPerUnit:5.00,   type:"Standard" },
    { tierId:"visio-p2",    label:"Plan 2",            billing:"annual",   seatModel:"per-seat", costPerUnit:599.88, renewalDate:"2026-11-01", type:"Standard" },
    { tierId:"visio-std",   label:"Standard 2021",     billing:"one-time", seatModel:"per-seat", costPerUnit:309.99, type:"Standard" },
  ]},
  { id:"powerbi", name:"Power BI",             category:"Analytics",     isDefault:false, icon:"📊", tiers:[
    { tierId:"pbi-free",    label:"Free",              billing:"monthly",  seatModel:"per-seat", costPerUnit:0,      type:"Standard" },
    { tierId:"pbi-pro",     label:"Pro",               billing:"monthly",  seatModel:"per-seat", costPerUnit:10.00,  type:"Standard" },
    { tierId:"pbi-prem",    label:"Premium Per User",  billing:"monthly",  seatModel:"per-seat", costPerUnit:20.00,  type:"Standard" },
  ]},
  { id:"copilot", name:"GitHub Copilot",       category:"Development",   isDefault:false, icon:"🤖", tiers:[
    { tierId:"ghcp-ind",    label:"Individual",        billing:"monthly",  seatModel:"per-seat", costPerUnit:10.00,  type:"Standard" },
    { tierId:"ghcp-biz",    label:"Business",          billing:"monthly",  seatModel:"per-seat", costPerUnit:19.00,  type:"Standard" },
    { tierId:"ghcp-ent",    label:"Enterprise",        billing:"monthly",  seatModel:"per-seat", costPerUnit:39.00,  type:"Standard" },
  ]},
  { id:"claude",  name:"Claude AI",            category:"AI Tools",      isDefault:false, icon:"✦",  tiers:[
    { tierId:"claude-free", label:"Free",              billing:"monthly",  seatModel:"per-seat", costPerUnit:0,      type:"Non-Standard" },
    { tierId:"claude-pro",  label:"Pro",               billing:"monthly",  seatModel:"per-seat", costPerUnit:20.00,  type:"Non-Standard" },
    { tierId:"claude-team", label:"Team",              billing:"monthly",  seatModel:"per-seat", costPerUnit:30.00,  type:"Non-Standard" },
  ]},
  { id:"adobe",   name:"Adobe Creative Cloud", category:"Design",        isDefault:false, icon:"🅰",  tiers:[
    { tierId:"adobe-sing",  label:"Single App",        billing:"annual",   seatModel:"per-seat", costPerUnit:263.88, renewalDate:"2026-08-15", type:"Non-Standard" },
    { tierId:"adobe-all",   label:"All Apps",          billing:"annual",   seatModel:"per-seat", costPerUnit:659.88, renewalDate:"2026-08-15", type:"Non-Standard" },
  ]},
  { id:"zoom",    name:"Zoom",                 category:"Communication", isDefault:true,  icon:"📹", tiers:[
    { tierId:"zoom-pro",    label:"Pro",               billing:"monthly",  seatModel:"per-seat", costPerUnit:13.32,  type:"Standard" },
    { tierId:"zoom-biz",    label:"Business",          billing:"monthly",  seatModel:"per-seat", costPerUnit:16.66,  type:"Standard" },
  ]},
  { id:"slack",   name:"Slack",               category:"Communication", isDefault:false, icon:"💬", tiers:[
    { tierId:"slack-pro",   label:"Pro",               billing:"annual",   seatModel:"per-seat", costPerUnit:87.00,  renewalDate:"2027-01-10", type:"Non-Standard" },
    { tierId:"slack-biz",   label:"Business+",         billing:"annual",   seatModel:"per-seat", costPerUnit:150.00, renewalDate:"2027-01-10", type:"Non-Standard" },
  ]},
  { id:"docusign",name:"DocuSign",             category:"Legal",         isDefault:false, icon:"✍",  tiers:[
    { tierId:"docu-per",    label:"Personal",          billing:"annual",   seatModel:"per-seat", costPerUnit:120.00, renewalDate:"2026-09-22", type:"Non-Standard" },
    { tierId:"docu-biz",    label:"Business Pro",      billing:"annual",   seatModel:"flat",     costPerUnit:480.00, renewalDate:"2026-09-22", type:"Non-Standard" },
  ]},
];

// Users, requests and dept defaults are loaded from SharePoint — no hardcoded seed data

// A user is assignable/requestable only once their department is set. Users with a
// blank department are excluded from every pick-a-user surface (they show up in the
// Users tab so they can be fixed, but you can't assign licenses to them).
const hasDept = u => !!(u.dept && u.dept.trim());

// Per-user cost. Per-seat tiers cost each holder the full unit price. "Flat"
// (org/team-wide) tiers — e.g. €1k/yr for the whole license — are a single lump
// split evenly across everyone assigned: each holder's share = total ÷ assignees,
// so adding/removing users re-allocates automatically (3 users → 33.3% each,
// 4 → 25%). seatCounts maps tierId → org-wide assignee count (the divisor).
function userCosts(user, allTiersArr, seatCounts={}) {
  let opex=0, capex=0;
  user.tiers.forEach(tid => {
    const t = allTiersArr.find(x => x.tierId===tid); if (!t) return;
    const divisor = t.seatModel==="flat" ? Math.max(1, seatCounts[tid]||1) : 1;
    if (t.billing==="one-time") capex += t.costPerUnit/divisor; else opex += monthlyEq(t)/divisor;
  });
  return { opex, capex };
}

// isDefaultTier is now a closure inside Dashboard using dynamic deptDefaults from SharePoint

const BILLING_COLOR = { monthly:"#4A6FFF", annual:"#A855F7", "one-time":"#10B981" };
const STATUS_COLOR  = { Pending:"#F59E0B", Approved:"#10B981", Denied:"#EF4444" };
const APPR_COLOR    = { "Auto-Approve":"#10B981", Manager:"#F59E0B", "IT + Finance":"#EF4444", "Designated Approver":"#7C5FFF", "New Software":"#0EA5E9" };

// Sentinel appId in the request form meaning "software not in the catalog yet".
// Such a request is stored with an empty appId/tierId plus a NewApp payload, is
// never auto-approved, and can't be approved until IT adds the app to the catalog
// and attaches it (which fills in appId/tierId).
const NEW_APP_ID = "__new__";
// True while a request still points at no catalog tier — approval is blocked.
const isUnattachedNewApp = r => !!r.newApp && !r.tierId;

// Resolve how a request routes. A per-app designated approver OVERRIDES the cost
// tiers entirely — it never auto-approves, even for a cheap license. Otherwise we
// fall back to the cost-based approvalTier. Returns { label, color, auto, approverId }.
const routeRequest = (app, monthlyCost) => {
  if (app?.approverId) {
    return { label:"Designated Approver", color:APPR_COLOR["Designated Approver"], auto:false, approverId:app.approverId };
  }
  const ap = approvalTier(monthlyCost);
  return { ...ap, auto:ap.label==="Auto-Approve", approverId:null };
};

const Badge = ({label,bg,color,small}) => (
  <span style={{ display:"inline-block",padding:small?"1px 7px":"3px 9px",borderRadius:20,fontSize:small?10:11,fontWeight:700,background:bg,color }}>{label}</span>
);
const BillingBadge = ({b}) => <Badge label={{monthly:"Monthly",annual:"Annual","one-time":"One-Time"}[b]} bg={`${BILLING_COLOR[b]}33`} color={BILLING_COLOR[b]} small/>;
const Mono = ({children,color}) => <span style={{ fontFamily:"'DM Mono',monospace",color:color||"#1D4ED8",fontSize:13 }}>{children}</span>;

const ROLES = { Admin:"Admin", ITManager:"ITManager", Manager:"Manager", Finance:"Finance", ReadOnly:"ReadOnly" };

const can = (user, action) => {
  if (!user) return false;
  const r = user.role;
  switch(action) {
    case "viewDashboard":       return true;
    case "viewUsers":           return true;
    case "editUsers":           return [ROLES.Admin,ROLES.ITManager].includes(r);
    case "assignLicenses":      return [ROLES.Admin,ROLES.ITManager].includes(r);
    case "viewCatalog":         return [ROLES.Admin,ROLES.ITManager,ROLES.Finance].includes(r);
    case "editCatalog":         return [ROLES.Admin,ROLES.ITManager].includes(r);
    case "viewSupplierDetails": return [ROLES.Admin,ROLES.ITManager,ROLES.Finance].includes(r);
    case "submitRequest":       return [ROLES.Admin,ROLES.ITManager,ROLES.Manager].includes(r);
    case "approveRequest":      return [ROLES.Admin,ROLES.ITManager,ROLES.Manager].includes(r);
    case "viewDeptDefaults":    return [ROLES.Admin,ROLES.ITManager,ROLES.Manager,ROLES.Finance].includes(r);
    case "editConfig":          return [ROLES.Admin,ROLES.ITManager].includes(r);
    default: return false;
  }
};

const ROLE_META = {
  Admin:     { label:"Admin",      color:"#EF4444", bg:"#FEF2F2" },
  ITManager: { label:"IT Manager", color:"#2563EB", bg:"#EFF6FF" },
  Manager:   { label:"Manager",    color:"#D97706", bg:"#FFFBEB" },
  ReadOnly:  { label:"Read Only",  color:"#6B7280", bg:"#F9FAFB" },
  Finance:   { label:"Finance",    color:"#16A34A", bg:"#F0FDF4" },
};

// ─── BRAND ────────────────────────────────────────────────────────────────────
// Loqus branding for the sign-in screen. Set BRAND.primary to the exact Loqus
// hex to apply the colour scheme (drives the button accent/hover + sign-in title).
const BRAND = {
  logo:    "https://loqusgroup.com/img/logo.png",
  primary: "#2563EB",   // TODO: replace with the official Loqus primary hex
  bg:      "#F4F6FA",
};

function LoginScreen({ onMsalLogin, notice }) {
  return (
    <div style={{ minHeight:"100vh",background:BRAND.bg,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Sans','Segoe UI',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');*{box-sizing:border-box;margin:0;padding:0}`}</style>
      <div style={{ width:"100%",maxWidth:420,padding:"0 24px" }}>
        <div style={{ textAlign:"center",marginBottom:32 }}>
          <img src={BRAND.logo} alt="Loqus" style={{ height:48,maxWidth:240,objectFit:"contain",marginBottom:16 }} />
          <h1 style={{ fontSize:20,fontWeight:700,color:"#1A1F36" }}>LicenseOps</h1>
          <p style={{ fontSize:13,color:"#8A93AA",marginTop:4 }}>Software License Management</p>
        </div>
        <div style={{ background:"#FFFFFF",border:"1px solid #E2E6F0",borderRadius:16,padding:"32px",boxShadow:"0 4px 24px #00000010",borderTop:`3px solid ${BRAND.primary}` }}>
          <h2 style={{ fontSize:17,fontWeight:700,color:"#1A1F36",marginBottom:6 }}>Sign in</h2>
          <p style={{ fontSize:13,color:"#8A93AA",marginBottom:24 }}>Use your company Microsoft account to continue.</p>
          {notice&&<div style={{ background:"#FFF7ED",border:"1px solid #FED7AA",color:"#B45309",borderRadius:8,padding:"9px 12px",fontSize:12.5,marginBottom:16 }}>⚠ {notice}</div>}
          <button onClick={onMsalLogin} style={{ width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:12,background:"#FFFFFF",border:"1.5px solid #D0D5DD",borderRadius:10,padding:"12px 20px",cursor:"pointer",fontFamily:"inherit",fontWeight:600,fontSize:14,color:"#1A1F36",transition:"all .15s",boxShadow:"0 1px 4px #00000008" }}
            onMouseOver={e=>e.currentTarget.style.borderColor=BRAND.primary} onMouseOut={e=>e.currentTarget.style.borderColor="#D0D5DD"}>
            <svg width="20" height="20" viewBox="0 0 21 21"><rect x="1" y="1" width="9" height="9" fill="#F25022"/><rect x="11" y="1" width="9" height="9" fill="#7FBA00"/><rect x="1" y="11" width="9" height="9" fill="#00A4EF"/><rect x="11" y="11" width="9" height="9" fill="#FFB900"/></svg>
            Sign in with Microsoft
          </button>
        </div>
        <p style={{ textAlign:"center",fontSize:11,color:"#C0C8D8",marginTop:16 }}>Secured by Microsoft Entra ID · Single Sign-On</p>
      </div>
    </div>
  );
}

const BLANK_APP  = { name:"", category:"", icon:"📦", isDefault:false, description:"", group:"" };
const BLANK_TIER = { label:"", billing:"monthly", seatModel:"per-seat", costPerUnit:"", type:"Standard", seats:"", msSkuId:"", msSkuName:"", renewalDate:"", supplier:"", supplierContact:"", purchaseDate:"", contractRef:"", renewalOwner:"", renewalOwnerName:"", purchaseUrl:"", visibilityOnly:false };

// ─── DEPT DEFAULTS TAB ───────────────────────────────────────────────────────
function DeptDefaultsTab({ allDepts, deptDefaults, users, allTiers, seatCounts, tierByIdDyn, saveDeptDefault, removeDeptDefault, canEdit, onDeptClick }) {
  const [editingDept,  setEditingDept]  = useState(null);
  const [expandedDept, setExpandedDept] = useState(null); // dept with users expanded
  const [pendingTiers, setPendingTiers] = useState([]);
  const [saving,       setSaving]       = useState(false);

  const openEdit = (dept) => {
    setEditingDept(dept);
    setPendingTiers([...(deptDefaults[dept]||[])]);
  };

  const togglePendingTier = (tierId) => {
    setPendingTiers(p => p.includes(tierId) ? p.filter(t=>t!==tierId) : [...p, tierId]);
  };

  const saveEdit = async () => {
    setSaving(true);
    await saveDeptDefault(editingDept, pendingTiers);
    setSaving(false);
    setEditingDept(null);
  };

  // Depts with no defaults yet — shown as "unconfigured"
  const unconfigured = allDepts.filter(d => !deptDefaults[d]?.length);

  return (
    <div>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4 }}>
        <h1 style={{ fontSize:20,fontWeight:700 }}>Department Default Bundles</h1>
      </div>
      <p style={{ color:"#8A93AA",fontSize:13,marginBottom:6 }}>
        Default licenses auto-assigned to every user in a department. Departments sync automatically from Entra.
      </p>
      {unconfigured.length>0&&(
        <div style={{ background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:10,padding:"10px 16px",fontSize:12,color:"#92400E",marginBottom:20,display:"flex",alignItems:"center",gap:8 }}>
          ⚠ <strong>{unconfigured.length} department{unconfigured.length!==1?"s":""} have no default bundle:</strong> {unconfigured.join(", ")}
          {canEdit&&<span style={{ color:"#D97706" }}> — click Edit to configure</span>}
        </div>
      )}
      <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(340px,1fr))",gap:16 }}>
        {allDepts.map(dept=>{
          const tids       = deptDefaults[dept]||[];
          const deptUsers  = users.filter(u=>u.dept===dept);
          const bundleCost = tids.reduce((sum,tid)=>{ const t=tierByIdDyn(tid); return sum+(t?monthlyEq(t):0); },0);
          // Actual monthly spend for the department — sum of every user's assigned-license
          // OpEx (flat licenses already split across all their assignees).
          const deptMonthly = deptUsers.reduce((sum,u)=>sum+userCosts(u,allTiers,seatCounts).opex,0);
          const isEditing  = editingDept===dept;
          return (
            <div key={dept} className="card" style={{ border:isEditing?"2px solid #2563EB":"1px solid #E2E6F0" }}>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14 }}>
                <div>
                  <div style={{ fontWeight:700,fontSize:15 }}>{dept}</div>
                  <div style={{ fontSize:11,color:"#8A93AA",marginTop:2 }}>
                    {deptUsers.length} user{deptUsers.length!==1?"s":""} · <span style={{ color:"#1D4ED8",fontWeight:600 }}>{eur(deptMonthly)}/mo</span>
                    <span style={{ color:"#C0C8D8" }}> · {eur(deptMonthly*12)}/yr</span>
                  </div>
                </div>
                <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                  {tids.length>0&&<div style={{ textAlign:"right" }}><Mono color="#1D4ED8">{eur(bundleCost)}</Mono><div style={{ fontSize:10,color:"#8A93AA" }}>per user/mo</div></div>}
                  {canEdit&&!isEditing&&<button className="btn btnG btnSm" onClick={()=>openEdit(dept)}>Edit</button>}
                  {canEdit&&isEditing&&<button className="btn btnG btnSm" onClick={()=>setEditingDept(null)}>Cancel</button>}
                </div>
              </div>

              {/* Current tiers */}
              {!isEditing&&tids.length===0&&(
                <p style={{ fontSize:12,color:"#C0C8D8",fontStyle:"italic" }}>No defaults configured</p>
              )}
              {!isEditing&&tids.map(tid=>{ const t=tierByIdDyn(tid); if(!t) return null; return (
                <div key={tid} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:"1px solid #E8EBF3" }}>
                  <div style={{ fontSize:13 }}>{t.appIcon} {t.appName} <span style={{ color:"#5A637A",fontSize:12 }}>{t.label}</span></div>
                  <div style={{ display:"flex",alignItems:"center",gap:8 }}><BillingBadge b={t.billing}/><Mono color="#5A637A">{eur(t.costPerUnit)}{ul(t)}</Mono></div>
                </div>
              ); })}

              {/* Edit panel */}
              {isEditing&&(
                <div>
                  <p style={{ fontSize:10,fontWeight:700,color:"#8A93AA",letterSpacing:".06em",marginBottom:10 }}>SELECT DEFAULT TIERS</p>
                  <div style={{ maxHeight:300,overflowY:"auto",display:"flex",flexDirection:"column",gap:6,marginBottom:14 }}>
                    {allTiers.map(t=>{
                      const checked = pendingTiers.includes(t.tierId);
                      return (
                        <label key={t.tierId} style={{ display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderRadius:8,border:`1.5px solid ${checked?"#93AFEF":"#E2E6F0"}`,background:checked?"#EFF6FF":"#F8F9FC",cursor:"pointer" }}>
                          <input type="checkbox" checked={checked} onChange={()=>togglePendingTier(t.tierId)} style={{ accentColor:"#2563EB",width:14,height:14,cursor:"pointer" }}/>
                          <span style={{ fontSize:12 }}>{t.appIcon} {t.appName}</span>
                          <span style={{ fontSize:11,color:"#8A93AA" }}>{t.label}</span>
                          <span style={{ marginLeft:"auto" }}><Mono color="#5A637A">{eur(t.costPerUnit)}{ul(t)}</Mono></span>
                        </label>
                      );
                    })}
                  </div>
                  <div style={{ display:"flex",gap:8,alignItems:"center",borderTop:"1px solid #E8EBF3",paddingTop:12 }}>
                    <button className="btn btnP btnSm" onClick={saveEdit} disabled={saving}>{saving?"Saving…":"Save Defaults"}</button>
                    <button className="btn btnG btnSm" onClick={()=>setEditingDept(null)}>Cancel</button>
                    <span style={{ fontSize:11,color:"#8A93AA",marginLeft:4 }}>{pendingTiers.length} tier{pendingTiers.length!==1?"s":""} selected</span>
                  </div>
                </div>
              )}

              {!isEditing&&tids.length>0&&(
                <div style={{ marginTop:12,padding:"8px 12px",background:"#EBF0FF",borderRadius:8,display:"flex",justifyContent:"space-between",alignItems:"center" }}>
                  <span style={{ fontSize:11.5,color:"#2563EB" }}>Total ({deptUsers.length} users)</span>
                  <Mono color="#2563EB">{eur(bundleCost*deptUsers.length)}/mo</Mono>
                </div>
              )}

              {/* User list toggle */}
              {!isEditing&&deptUsers.length>0&&(
                <div style={{ marginTop:10 }}>
                  <button onClick={()=>setExpandedDept(expandedDept===dept?null:dept)} style={{ background:"none",border:"none",cursor:"pointer",fontSize:11.5,color:"#2563EB",fontFamily:"inherit",fontWeight:600,padding:"4px 0",display:"flex",alignItems:"center",gap:5 }}>
                    {expandedDept===dept?"▲":"▼"} {expandedDept===dept?"Hide":"Show"} {deptUsers.length} user{deptUsers.length!==1?"s":""}
                  </button>
                  {expandedDept===dept&&(
                    <div style={{ marginTop:8,display:"flex",flexDirection:"column",gap:5 }}>
                      {deptUsers.map(u=>{
                        // Same calc as the Users & Licenses page — flat-priced tiers are
                        // split across all their assignees (via seatCounts), so this matches
                        // that page and sums to the department total above.
                        const c = userCosts(u, allTiers, seatCounts).opex;
                        return (
                          <div key={u.id} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 10px",background:"#F8F9FC",borderRadius:8,border:"1px solid #E2E6F0" }}>
                            <div>
                              <div style={{ fontSize:12.5,fontWeight:600,color:"#1A1F36" }}>{u.name}</div>
                              <div style={{ fontSize:10.5,color:"#8A93AA" }}>{u.email}</div>
                            </div>
                            <Mono color="#1D4ED8" style={{ fontSize:11 }}>{eur(c)}/mo</Mono>
                          </div>
                        );
                      })}
                      <button onClick={()=>onDeptClick(dept)} style={{ marginTop:4,background:"none",border:"1px solid #2563EB",borderRadius:7,cursor:"pointer",fontSize:11.5,color:"#2563EB",fontFamily:"inherit",fontWeight:600,padding:"6px 12px",transition:"all .15s" }}
                        onMouseOver={e=>e.currentTarget.style.background="#EFF6FF"} onMouseOut={e=>e.currentTarget.style.background="none"}>
                        View in Users & Licenses →
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {allDepts.length===0&&(
          <div style={{ gridColumn:"1/-1",textAlign:"center",padding:60,color:"#8A93AA" }}>
            <div style={{ fontSize:32,marginBottom:12 }}>👥</div>
            <p style={{ fontSize:14,fontWeight:600,marginBottom:6 }}>No users synced yet</p>
            <p style={{ fontSize:12 }}>Go to Users & Licenses and click "Sync from Entra" to pull your team.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── SETTINGS TAB ─────────────────────────────────────────────────────────────
// Admin-editable auto-approve thresholds. Draft state is local; saved via saveConfig
// which persists to LO_Config and updates the module-level APPROVAL_CFG.
function SettingsTab({ config, saveConfig }) {
  const [autoMax, setAutoMax] = useState(String(config.autoMax));
  const [mgrMax,  setMgrMax]  = useState(String(config.mgrMax));
  const [saving,  setSaving]  = useState(false);
  const [savedMsg,setSavedMsg]= useState(false);
  const [error,   setError]   = useState(null);

  const a = Number(autoMax), m = Number(mgrMax);
  const valid = autoMax!=="" && mgrMax!=="" && a>=0 && m>a;
  const dirty = a!==config.autoMax || m!==config.mgrMax;

  const save = async () => {
    if (!valid) return;
    setSaving(true); setError(null);
    try {
      await saveConfig({ autoMax:a, mgrMax:m });
      setSavedMsg(true); setTimeout(()=>setSavedMsg(false),2500);
    } catch(e) { setError(e.message); }
    setSaving(false);
  };

  return (
    <div>
      <h1 style={{ fontSize:20,fontWeight:700,marginBottom:4 }}>Settings</h1>
      <p style={{ color:"#8A93AA",fontSize:13,marginBottom:22 }}>Approval routing thresholds (monthly-equivalent cost per license).</p>

      <div className="card" style={{ maxWidth:560 }}>
        <p className="sl">AUTO-APPROVE THRESHOLDS</p>
        <p style={{ fontSize:12.5,color:"#5A637A",marginBottom:18,lineHeight:1.5 }}>
          Requests are routed by the requested license's monthly cost. Below the first threshold they are
          approved automatically; between the two they go to the requester's manager; above the second they
          go to IT&nbsp;+&nbsp;Finance.
        </p>

        <div style={{ display:"flex",gap:16,marginBottom:16 }}>
          <div style={{ flex:1 }}>
            <label style={{ fontSize:10,fontWeight:700,color:"#8A93AA",display:"block",marginBottom:6 }}>AUTO-APPROVE BELOW (€/mo)</label>
            <input className="inp" type="number" min="0" step="1" value={autoMax} onChange={e=>setAutoMax(e.target.value)} />
          </div>
          <div style={{ flex:1 }}>
            <label style={{ fontSize:10,fontWeight:700,color:"#8A93AA",display:"block",marginBottom:6 }}>MANAGER APPROVAL BELOW (€/mo)</label>
            <input className="inp" type="number" min="0" step="1" value={mgrMax} onChange={e=>setMgrMax(e.target.value)} />
          </div>
        </div>

        {valid&&(
          <div style={{ display:"flex",flexDirection:"column",gap:6,marginBottom:18 }}>
            {[
              {c:"#10B981",t:`Auto-Approve — under €${a}/mo`},
              {c:"#F59E0B",t:`Manager — €${a}–€${m}/mo`},
              {c:"#EF4444",t:`IT + Finance — over €${m}/mo`},
            ].map(row=>(
              <div key={row.t} style={{ display:"flex",alignItems:"center",gap:9,fontSize:12.5,color:"#374060" }}>
                <span style={{ width:8,height:8,borderRadius:"50%",background:row.c }}/>{row.t}
              </div>
            ))}
          </div>
        )}
        {!valid&&<p style={{ fontSize:12,color:"#EF4444",marginBottom:14 }}>Manager threshold must be greater than the auto-approve threshold.</p>}
        {error&&<p style={{ fontSize:12,color:"#EF4444",marginBottom:14 }}>{error}</p>}

        <div style={{ display:"flex",alignItems:"center",gap:12 }}>
          <button className="btn btnP" disabled={!valid||!dirty||saving} onClick={save}
            style={{ opacity:(!valid||!dirty||saving)?0.5:1,cursor:(!valid||!dirty||saving)?"default":"pointer" }}>
            {saving?"Saving…":"Save thresholds"}
          </button>
          {savedMsg&&<span style={{ fontSize:12.5,color:"#10B981",fontWeight:600 }}>✓ Saved</span>}
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [msalReady,   setMsalReady]   = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Session-expiry watchdog: getApiToken/apiFetch dispatch this when silent renewal
  // is dead or the API 401s our token. Clear the MSAL cache and drop back to the
  // login screen — without this the UI just sat on stale data showing fetch errors.
  useEffect(() => {
    const onExpired = async () => {
      try { await msalInstance.clearCache(); } catch (e) { console.error(e); }
      setSessionExpired(true);
      setCurrentUser(null);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  // Handle MSAL redirect response on page load
  useEffect(() => {
    msalInstance.handleRedirectPromise().then(resp => {
      if (resp?.account) {
        const acct = resp.account;
        // Map Entra token claims to our user shape
        // Role comes from Entra App Role claim "roles" — falls back to ReadOnly
        const roleMap = { Admin:"Admin", ITManager:"ITManager", Manager:"Manager", Finance:"Finance", ReadOnly:"ReadOnly" };
        const roleRaw = (acct.idTokenClaims?.roles||[])[0] || "ReadOnly";
        setCurrentUser({
          id:     acct.localAccountId,
          name:   acct.name || acct.username,
          email:  acct.username,
          role:   roleMap[roleRaw] || ROLES.ReadOnly,
          dept:   acct.idTokenClaims?.department || "IT",
          avatar: (acct.name||acct.username).split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase(),
        });
      }
      // Check if already signed in from a previous session
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length && !resp?.account) {
        const acct = accounts[0];
        const roleMap = { Admin:"Admin", ITManager:"ITManager", Manager:"Manager", Finance:"Finance", ReadOnly:"ReadOnly" };
        const roleRaw = (acct.idTokenClaims?.roles||[])[0] || "ReadOnly";
        setCurrentUser({
          id:     acct.localAccountId,
          name:   acct.name || acct.username,
          email:  acct.username,
          role:   roleMap[roleRaw] || ROLES.ReadOnly,
          dept:   acct.idTokenClaims?.department || "IT",
          avatar: (acct.name||acct.username).split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase(),
        });
      }
      setMsalReady(true);
    }).catch(() => setMsalReady(true));
  }, []);

  const handleMsalLogin = () => msalInstance.loginRedirect({ scopes:LOGIN_SCOPES });
  // App-only sign-out: clear THIS app's MSAL cache (tokens + account) without hitting
  // the Azure AD end-session endpoint, so the browser-wide Microsoft session stays
  // intact (other M365 apps / tabs remain signed in). logoutRedirect() would sign the
  // user out of Microsoft entirely — not what we want here.
  const handleSignOut   = async () => {
    try { await msalInstance.clearCache(); } catch (e) { console.error(e); }
    setCurrentUser(null);
  };

  if (!msalReady) return (
    <div style={{ minHeight:"100vh",background:"#F4F6FA",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Sans',sans-serif" }}>
      <div style={{ textAlign:"center" }}>
        <img src={BRAND.logo} alt="Loqus" style={{ height:40,maxWidth:200,objectFit:"contain",marginBottom:14 }} />
        <p style={{ color:"#5A637A",fontSize:14 }}>Loading LicenseOps…</p>
      </div>
    </div>
  );

  if (!currentUser) return <LoginScreen onMsalLogin={handleMsalLogin} notice={sessionExpired?"Your session has expired — please sign in again.":null}/>;
  // ReadOnly users get the simplified self-service view; everyone else gets the full dashboard
  if (currentUser.role === ROLES.ReadOnly) return <MySoftwareView currentUser={currentUser} onSignOut={handleSignOut}/>;
  return <Dashboard currentUser={currentUser} onSignOut={handleSignOut}/>;
}

// ─── READ-ONLY SELF-SERVICE VIEW ──────────────────────────────────────────────
// What a regular staff member sees: their own software, no costs, ability to request
function MySoftwareView({ currentUser, onSignOut }) {
  const { users, setUsers, catalog, setRequests, loading:spLoading, spError, saveRequest, saveUser } = useSharePointData();
  const [showReqForm, setShowReqForm] = useState(false);
  const [newReq, setNewReq] = useState({ appId:"", tierId:"", justification:"" });
  const [toastMsg, setToastMsg] = useState(null);

  const allTiers = useMemo(()=>catalog.flatMap(app=>app.tiers.map(t=>({...t,appId:app.id,appName:app.name,appIcon:app.icon,category:app.category}))),[catalog]);
  const tierByIdDyn = id => allTiers.find(t=>t.tierId===id);
  const appByIdDyn  = id => catalog.find(a=>a.id===id);

  // Find the logged-in user's record by Entra ID or email
  const me = useMemo(()=>users.find(u => u.id===currentUser.id || u.email?.toLowerCase()===currentUser.email?.toLowerCase()), [users, currentUser]);
  const myTiers    = me ? me.tiers.map(tid=>tierByIdDyn(tid)).filter(Boolean) : [];

  const toast = msg => { setToastMsg(msg); setTimeout(()=>setToastMsg(null),3000); };

  const submitRequest = async () => {
    if (!me) return;
    // Software not in the catalog yet — record the proposed details, always Pending
    // (no cost is known, so it can never auto-approve). IT adds it to the catalog
    // and attaches it before it can be approved.
    if (newReq.appId === NEW_APP_ID) {
      if (!newReq.newAppName?.trim() || !newReq.justification.trim()) return;
      const entry = { id:Date.now(), userId:me.id, appId:"", tierId:"",
        newApp:{ name:newReq.newAppName.trim(), tier:(newReq.newAppTier||"").trim(), options:(newReq.newAppOptions||"").trim(), link:(newReq.newAppLink||"").trim() },
        justification:newReq.justification.trim(), by:me.name, date:TODAY.toISOString().split("T")[0], status:"Pending", approvalLevel:"New Software", note:"" };
      await saveRequest(entry).catch(console.error);
      setRequests(p=>[...p,entry]);
      setNewReq({ appId:"", tierId:"", justification:"" });
      setShowReqForm(false);
      toast("Request submitted — IT will review the new software");
      return;
    }
    if (!newReq.tierId) return;
    const t = tierByIdDyn(newReq.tierId); if (!t) return;
    const ap = routeRequest(appByIdDyn(t.appId), monthlyEq(t));
    const auto = ap.auto;
    const entry = { id:Date.now(), userId:me.id, appId:t.appId, tierId:newReq.tierId, justification:newReq.justification.trim(), by:me.name, date:TODAY.toISOString().split("T")[0], status:auto?"Approved":"Pending", approvalLevel:ap.label, note:"" };
    await saveRequest(entry).catch(console.error);
    setRequests(p=>[...p,entry]);
    if (auto && me) { const updated={...me,tiers:[...new Set([...me.tiers,newReq.tierId])]}; setUsers(p=>p.map(u=>u.id===me.id?updated:u)); saveUser(updated).catch(console.error); }
    setNewReq({ appId:"", tierId:"", justification:"" });
    setShowReqForm(false);
    toast(auto ? "Request auto-approved" : "Request submitted for approval");
  };

  return (
    <div style={{ fontFamily:"'DM Sans','Segoe UI',sans-serif",background:"#F4F6FA",minHeight:"100vh",color:"#1A1F36" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        .btn{border:none;cursor:pointer;font-family:inherit;font-weight:600;border-radius:8px;transition:all .15s;font-size:13px}
        .btnP{background:#2563EB;color:#fff;padding:9px 18px}.btnP:hover{background:#1D4ED8}
        .btnG{background:#EEF1F7;color:#5A637A;padding:9px 16px}.btnG:hover{background:#E2E6F0}
        .inp{background:#F8F9FC;border:1px solid #D8DCE8;border-radius:8px;padding:9px 13px;color:#1A1F36;font-size:13px;outline:none;width:100%}
        .inp:focus{border-color:#2563EB}
        .card{background:#FFFFFF;border:1px solid #E2E6F0;border-radius:14px;padding:22px}
      `}</style>

      {/* NAV */}
      <div style={{ background:"#FFFFFF",borderBottom:"1px solid #E8EBF3",padding:"0 28px" }}>
        <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",maxWidth:900,margin:"0 auto",height:54 }}>
          <div style={{ display:"flex",alignItems:"center",gap:10 }}>
            <img src={BRAND.logo} alt="Loqus" style={{ height:26,maxWidth:120,objectFit:"contain" }} />
            <span style={{ color:"#D8DCE8" }}>|</span>
            <span style={{ fontWeight:700,fontSize:14 }}>LicenseOps</span>
            <span style={{ color:"#8A93AA",fontSize:11 }}>· My Software</span>
          </div>
          <div style={{ display:"flex",alignItems:"center",gap:10 }}>
            <div style={{ display:"flex",alignItems:"center",gap:8,background:"#F8F9FC",border:"1px solid #E2E6F0",borderRadius:20,padding:"5px 12px 5px 6px" }}>
              <div style={{ width:26,height:26,borderRadius:"50%",background:"linear-gradient(135deg,#2563EB,#7C9EFF)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:"#fff" }}>{currentUser.avatar}</div>
              <div style={{ fontSize:12,fontWeight:600,color:"#1A1F36" }}>{currentUser.name}</div>
            </div>
            <button onClick={onSignOut} style={{ background:"none",border:"none",cursor:"pointer",color:"#8A93AA",fontSize:12,fontFamily:"inherit" }}>Sign out</button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth:900,margin:"0 auto",padding:"28px" }}>
        {spLoading&&<p style={{ color:"#8A93AA",fontSize:13,marginBottom:16 }}>Loading your software…</p>}
        {spError&&<p style={{ color:"#EF4444",fontSize:13,marginBottom:16 }}>Couldn't load data: {spError}</p>}

        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6 }}>
          <h1 style={{ fontSize:22,fontWeight:700 }}>My Software</h1>
          <button className="btn btnP" onClick={()=>{ setNewReq({appId:"",tierId:"",justification:""}); setShowReqForm(!showReqForm); }}>+ Request Software</button>
        </div>
        <p style={{ color:"#8A93AA",fontSize:13,marginBottom:22 }}>Software currently assigned to you, and the ability to request more.</p>

        {/* Request form */}
        {showReqForm&&(
          <div className="card" style={{ marginBottom:20 }}>
            <h3 style={{ fontWeight:700,fontSize:15,marginBottom:16 }}>Request Software</h3>
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:13 }}>
              <div>
                <label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:6,fontWeight:700 }}>APPLICATION</label>
                <SearchableSelect
                  placeholder="Select app…"
                  value={newReq.appId}
                  onChange={val=>setNewReq(p=>({...p,appId:val,tierId:""}))}
                  options={[{ value:NEW_APP_ID, label:"➕ New software (not in the list)…", sortKey:"" }, ...catalog.map(a=>({ value:a.id, label:`${a.icon} ${a.name}`, sortKey:a.name }))]}
                />
              </div>
              {newReq.appId===NEW_APP_ID ? (
                <>
                  <div>
                    <label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:6,fontWeight:700 }}>APPLICATION NAME *</label>
                    <input className="inp" placeholder="e.g. Figma" value={newReq.newAppName||""} onChange={e=>setNewReq(p=>({...p,newAppName:e.target.value}))}/>
                  </div>
                  <div>
                    <label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:6,fontWeight:700 }}>TIER / PLAN <span style={{ color:"#C0C8D8",fontWeight:400 }}>(if known)</span></label>
                    <input className="inp" placeholder="e.g. Professional" value={newReq.newAppTier||""} onChange={e=>setNewReq(p=>({...p,newAppTier:e.target.value}))}/>
                  </div>
                  <div>
                    <label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:6,fontWeight:700 }}>OPTIONS / ADD-ONS <span style={{ color:"#C0C8D8",fontWeight:400 }}>(optional)</span></label>
                    <input className="inp" placeholder="e.g. Dev Mode seat, 2 editors" value={newReq.newAppOptions||""} onChange={e=>setNewReq(p=>({...p,newAppOptions:e.target.value}))}/>
                  </div>
                  <div style={{ gridColumn:"1/-1" }}>
                    <label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:6,fontWeight:700 }}>LINK TO PRODUCT <span style={{ color:"#C0C8D8",fontWeight:400 }}>(optional)</span></label>
                    <input className="inp" type="url" placeholder="https://…" value={newReq.newAppLink||""} onChange={e=>setNewReq(p=>({...p,newAppLink:e.target.value}))}/>
                  </div>
                </>
              ) : (
                <div>
                  <label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:6,fontWeight:700 }}>PLAN / TIER</label>
                  <SearchableSelect
                    placeholder="Select…"
                    disabled={!newReq.appId}
                    value={newReq.tierId}
                    onChange={val=>setNewReq(p=>({...p,tierId:val}))}
                    options={((newReq.appId&&appByIdDyn(newReq.appId)?.tiers)||[]).filter(t=>!t.visibilityOnly).map(t=>({ value:t.tierId, label:t.label }))}
                  />
                </div>
              )}
              <div style={{ gridColumn:"1/-1" }}>
                <label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:6,fontWeight:700 }}>WHY DO YOU NEED THIS?{newReq.appId===NEW_APP_ID&&" *"}</label>
                <input className="inp" placeholder="Business justification…" value={newReq.justification} onChange={e=>setNewReq(p=>({...p,justification:e.target.value}))}/>
              </div>
            </div>
            {newReq.appId===NEW_APP_ID&&(
              <div style={{ marginTop:14,padding:"10px 14px",borderRadius:9,background:`${APPR_COLOR["New Software"]}11`,border:`1px solid ${APPR_COLOR["New Software"]}44`,display:"flex",alignItems:"center",gap:10 }}>
                <div style={{ width:8,height:8,borderRadius:"50%",background:APPR_COLOR["New Software"] }}/>
                <span style={{ fontSize:12.5,fontWeight:600,color:APPR_COLOR["New Software"] }}>New Software</span>
                <span style={{ fontSize:12,color:"#5A637A" }}>— IT will review it and add it to the catalog before it can be approved</span>
              </div>
            )}
            <div style={{ display:"flex",gap:10,marginTop:16 }}>
              <button className="btn btnP" onClick={submitRequest}>Submit Request</button>
              <button className="btn btnG" onClick={()=>setShowReqForm(false)}>Cancel</button>
            </div>
          </div>
        )}

        {/* My details */}
        <div className="card" style={{ marginBottom:20 }}>
          <p style={{ fontSize:10.5,fontWeight:700,letterSpacing:".1em",color:"#8A93AA",marginBottom:14 }}>MY DETAILS</p>
          <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:14 }}>
            <div><div style={{ fontSize:10,color:"#8A93AA",fontWeight:700,marginBottom:3 }}>NAME</div><div style={{ fontSize:13.5,fontWeight:600 }}>{currentUser.name}</div></div>
            <div><div style={{ fontSize:10,color:"#8A93AA",fontWeight:700,marginBottom:3 }}>EMAIL</div><div style={{ fontSize:13.5 }}>{currentUser.email||<span style={{color:"#C0C8D8"}}>—</span>}</div></div>
            <div><div style={{ fontSize:10,color:"#8A93AA",fontWeight:700,marginBottom:3 }}>DEPARTMENT</div><div style={{ fontSize:13.5 }}>{me?.dept||<span style={{color:"#C0C8D8"}}>—</span>}</div></div>
          </div>
        </div>

        {/* My assigned software */}
        <div className="card">
          <p style={{ fontSize:10.5,fontWeight:700,letterSpacing:".1em",color:"#8A93AA",marginBottom:14 }}>ASSIGNED TO ME</p>
          {!me&&!spLoading&&<p style={{ fontSize:13,color:"#8A93AA" }}>We couldn't find your profile yet. Contact IT if this persists.</p>}
          {me&&myTiers.length===0&&<p style={{ fontSize:13,color:"#8A93AA" }}>No software assigned to you yet.</p>}
          <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:10 }}>
            {myTiers.map(t=>(
              <div key={t.tierId} style={{ display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:"#F8F9FC",border:"1px solid #E2E6F0",borderRadius:10 }}>
                <span style={{ fontSize:22 }}>{t.appIcon}</span>
                <div>
                  <div style={{ fontSize:13.5,fontWeight:600 }}>{t.appName}</div>
                  <div style={{ fontSize:11.5,color:"#5A637A" }}>{t.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {toastMsg&&(
        <div style={{ position:"fixed",bottom:28,left:"50%",transform:"translateX(-50%)",background:"#1A1F36",color:"#fff",padding:"11px 22px",borderRadius:10,fontSize:13,fontWeight:600,boxShadow:"0 4px 20px #00000030",display:"flex",alignItems:"center",gap:8 }}>
          <span style={{ color:"#10B981" }}>✓</span> {toastMsg}
        </div>
      )}
    </div>
  );
}

function Dashboard({ currentUser, onSignOut }) {
  const isMgr = currentUser.role===ROLES.Manager;
  // Finance keeps its org-wide view but, if it manages people, ALSO gets a
  // manager-style approval queue for its own reports (resolved from Entra below).
  const isFinance = currentUser.role===ROLES.Finance;

  // SharePoint-backed state
  const { users, setUsers, catalog, setCatalog, requests, setRequests,
          deptDefaults, orphans, config, serverSeatCounts, loading:spLoading, spError,
          saveUser, saveApp, saveRequest, saveDeptDefault, removeDeptDefault, removeAppFromSP, removeUser, saveConfig, logOrphans, resolveOrphan, syncEntraOrphans, saveOrphanComment
        } = useSharePointData();

  // Managers are scoped to their org sub-tree, resolved live on load. Two sets:
  //  · myReports — DIRECT reports only, scopes the approval queue (routing unchanged)
  //  · myOrg     — the WHOLE sub-tree (transitive reports), scopes what they can SEE
  // Admin/ITManager are not isMgr → they see everything. Finance also resolves its
  // reports (for the approval queue) but is NOT scoped — it keeps the org-wide view.
  // null = not yet loaded (show nothing until it resolves, rather than everything).
  const [myReports, setMyReports] = useState(null);
  const [myOrg,     setMyOrg]     = useState(null);
  useEffect(() => {
    if (!isMgr && !isFinance) return;
    fetchMyReports()
      // The manager themselves is part of what they can SEE (their own record + apps),
      // even though they're not their own report — so include self in myOrg.
      .then(({ direct, all }) => { setMyReports(new Set(direct)); setMyOrg(new Set([...all, currentUser.id])); })
      .catch(() => { setMyReports(new Set()); setMyOrg(new Set([currentUser.id])); }); // on error, scope to self only — fail closed
  }, [isMgr, isFinance, currentUser.id]);

  // Dynamic dept defaults lookup (replaces hardcoded DEPT_DEFAULTS)
  const isDefaultTier = (dept, tierId) => (deptDefaults[dept]||[]).includes(tierId);

  const [tab,          setTab]          = useState("dashboard");
  const [search,       setSearch]       = useState("");
  const [deptFilter,   setDeptFilter]   = useState("All");
  const [editUser,     setEditUser]     = useState(null);
  const [showReqForm,  setShowReqForm]  = useState(false);
  const [licSearch,    setLicSearch]    = useState("");
  const [licCategory,  setLicCategory]  = useState("All");
  const [descExpand,   setDescExpand]   = useState(null);   // appId whose description panel is open
  const [expandApp,    setExpandApp]    = useState(null);
  const [approveModal, setApproveModal] = useState(null);
  const [attachModal,  setAttachModal]  = useState(null);   // add-to-catalog form for a new-software request
  const [showAddApp,   setShowAddApp]   = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});   // parent-group name → collapsed?
  const [newApp,       setNewApp]       = useState(BLANK_APP);
  const [editAppId,    setEditAppId]    = useState(null);
  const [editAppData,  setEditAppData]  = useState({});
  const [addTierAppId, setAddTierAppId] = useState(null);
  const [newTier,      setNewTier]      = useState(BLANK_TIER);
  const [deleteConfirm,setDeleteConfirm]= useState(null);
  const [editTier,     setEditTier]     = useState(null);
  const [editTierData, setEditTierData] = useState({});
  const [assignTierId,   setAssignTierId]   = useState(null);
  const [assignSearch,   setAssignSearch]   = useState("");
  const [assignSelected, setAssignSelected] = useState({});
  const [bulkModal,      setBulkModal]      = useState(null);
  const [bulkTierId,     setBulkTierId]     = useState("");
  const [bulkSelected,   setBulkSelected]   = useState({});
  const [assignToast,    setAssignToast]    = useState(null);
  const [newReq, setNewReq] = useState({ userId:"", appId:"", tierId:"", justification:"" });

  // Entra people for the designated-approver picker — lazy-loaded the first time the
  // Catalog tab opens (Admin/ITManager only reach that tab).
  const [entraPeople, setEntraPeople] = useState([]);
  useEffect(() => {
    if (tab==="catalog" && !entraPeople.length) fetchEntraUsers().then(setEntraPeople).catch(()=>{});
  }, [tab, entraPeople.length]);

  // License-change history for the Reporting tab — loaded the first time it opens.
  // null = not loaded yet, [] = loaded/empty.
  const [activity, setActivity] = useState(null);
  useEffect(() => {
    if (tab==="reporting" && activity===null) fetchActivity().then(r=>setActivity(r.events||[])).catch(()=>setActivity([]));
  }, [tab, activity]);

  const visibleUsers = useMemo(()=>isMgr?users.filter(u=>myOrg?.has(u.id)):users,[users,isMgr,myOrg]);
  // Departments derived from the *visible* users — so a scoped role (Manager/Senior)
  // only ever sees the departments within their own sub-tree.
  const allDepts = useMemo(()=>[...new Set(visibleUsers.map(u=>u.dept).filter(Boolean))].sort(),[visibleUsers]);
  // Users eligible to be assigned licenses / picked in a request — department required.
  const assignableUsers  = useMemo(()=>users.filter(hasDept).sort((a,b)=>(a.name||"").localeCompare(b.name||"")),[users]);
  const requestableUsers = useMemo(()=>visibleUsers.filter(hasDept),[visibleUsers]);
  const depts = ["All",...Array.from(new Set(visibleUsers.map(u=>u.dept)))];
  const filteredUsers = useMemo(()=>visibleUsers.filter(u=>(deptFilter==="All"||u.dept===deptFilter)&&(u.name.toLowerCase().includes(search.toLowerCase())||u.email.toLowerCase().includes(search.toLowerCase())||(u.manager||"").toLowerCase().includes(search.toLowerCase()))),[visibleUsers,search,deptFilter]);
  const allTiers = useMemo(()=>catalog.flatMap(app=>app.tiers.map(t=>({...t,appId:app.id,appName:app.name,appIcon:app.icon,category:app.category}))),[catalog]);
  // Org-wide assignee count per tier — the divisor for splitting flat-priced licenses.
  // Always over ALL users (a flat cost is shared by everyone holding it). For a
  // scoped Manager, `users` is filtered server-side, so the API supplies the true
  // org-wide counts (serverSeatCounts) to keep the divisor correct; otherwise we
  // compute them locally from the full user list.
  const seatCounts = useMemo(()=>{ if(serverSeatCounts) return serverSeatCounts; const m={}; users.forEach(u=>u.tiers.forEach(tid=>{ m[tid]=(m[tid]||0)+1; })); return m; },[users,serverSeatCounts]);
  // Users & Licenses table sort — click a header to toggle asc/desc on that column.
  const [userSort, setUserSort] = useState({ col:"User", dir:"asc" });
  const toggleUserSort = col => setUserSort(s => ({ col, dir: s.col===col && s.dir==="asc" ? "desc" : "asc" }));
  const sortedUsers = useMemo(()=>{
    const dir = userSort.dir==="asc" ? 1 : -1;
    const key = u => { const c=userCosts(u,allTiers,seatCounts); switch(userSort.col){
      case "Manager":  return (u.manager||"").toLowerCase();
      case "Dept":     return (u.dept||"").toLowerCase();
      case "Licenses": return u.tiers.length;
      case "OpEx/mo":  return c.opex;
      case "CapEx":    return c.capex;
      default:         return (u.name||"").toLowerCase();   // "User"
    } };
    return [...filteredUsers].sort((a,b)=>{ const x=key(a),y=key(b); return x<y?-dir:x>y?dir:0; });
  },[filteredUsers,userSort,allTiers,seatCounts]);
  const tierByIdDyn = id => allTiers.find(t=>t.tierId===id);
  const appByIdDyn  = id => catalog.find(a=>a.id===id);
  // Existing parent-group names (for the add/edit autocomplete).
  const groupOptions = useMemo(()=>[...new Set(catalog.map(a=>(a.group||"").trim()).filter(Boolean))].sort(),[catalog]);
  // Dashboard figures are scoped to what the viewer can see (visibleUsers): the whole
  // org for Admin/ITManager, the manager's sub-tree for a Manager.
  const orgStats = useMemo(()=>{ let opex=0,capex=0; visibleUsers.forEach(u=>{const c=userCosts(u,allTiers,seatCounts);opex+=c.opex;capex+=c.capex;}); return {opex,capex,opexAnnual:opex*12}; },[visibleUsers,allTiers,seatCounts]);
  const renewalsSoon = useMemo(()=>allTiers.filter(t=>t.renewalDate&&daysUntil(t.renewalDate)<=90&&daysUntil(t.renewalDate)>=0&&visibleUsers.some(u=>u.tiers.includes(t.tierId))).sort((a,b)=>daysUntil(a.renewalDate)-daysUntil(b.renewalDate)),[allTiers,visibleUsers]);
  // Admin/ITManager see all. A Manager sees their direct reports' requests PLUS any
  // request for an app they're the designated approver of.
  const ownsApp = useCallback(appId => catalog.find(a=>a.id===appId)?.approverId===currentUser.id, [catalog, currentUser.id]);
  // App ownership (non-IT app managers). An owner is the app's Designated Approver
  // OR the Renewal Owner of any of its tiers (both id-based). ownsAnyApp gates the
  // catalog tab for them; canEditApp gates per-app edit controls (IT edits all).
  // Match a stored owner value against the current user by ANY identifier — id,
  // email, or display name (case-insensitive). This tolerates legacy name-based
  // Renewal Owner values and tenants where the login id != the directory object id.
  const ownerMatch = useCallback(v => {
    if (v==null || v==="") return false;
    const s = String(v).toLowerCase();
    return v===currentUser.id || s===(currentUser.email||"").toLowerCase() || s===(currentUser.name||"").toLowerCase();
  }, [currentUser.id, currentUser.email, currentUser.name]);
  const isAppOwner = useCallback(a => !!a && (ownerMatch(a.approverId) || (a.tiers||[]).some(t=>ownerMatch(t.renewalOwner))), [ownerMatch]);
  const ownsAnyApp = useMemo(()=>catalog.some(isAppOwner), [catalog, isAppOwner]);
  const canEditApp = useCallback(a => can(currentUser,"editCatalog") || isAppOwner(a), [currentUser, isAppOwner]);
  // Catalog rows for the App Catalog tab: the visible/filtered apps, optionally
  // wrapped in collapsible parent-group sections. If no app has a group yet, we fall
  // back to a flat list (no headers) — so the view only changes once the admin starts
  // grouping. Collapsed groups still show their header (apps omitted).
  const GROUP_NONE = "Ungrouped";
  const catalogRows = useMemo(()=>{
    const q = licSearch.toLowerCase();
    const apps = catalog.filter(a => (can(currentUser,"viewCatalog")||isAppOwner(a)) && a.name.toLowerCase().includes(q) && (licCategory==="All"||a.category===licCategory));
    if (!apps.some(a => (a.group||"").trim())) return apps.map(a => ({ type:"app", app:a }));
    const byGroup = {};
    apps.forEach(a => { const g=(a.group||"").trim()||GROUP_NONE; (byGroup[g] = byGroup[g]||[]).push(a); });
    const names = Object.keys(byGroup).sort((x,y)=> x===GROUP_NONE?1 : y===GROUP_NONE?-1 : x.localeCompare(y));
    const rows = [];
    names.forEach(name => {
      const ga = byGroup[name];
      rows.push({ type:"header", name, appCount:ga.length,
        tierCount: ga.reduce((s,a)=>s+a.tiers.length,0),
        monthly:   ga.reduce((s,a)=>s+a.tiers.reduce((ss,t)=>ss+monthlyEq(t),0),0) });
      if (!collapsedGroups[name]) ga.forEach(a => rows.push({ type:"app", app:a }));
    });
    return rows;
  },[catalog,licSearch,licCategory,collapsedGroups,currentUser,isAppOwner]);
  // Orphan log visibility: Admin/IT see all; an app owner sees orphans tagged with
  // an app they own. Open ones first.
  const isITAdmin = [ROLES.Admin,ROLES.ITManager].includes(currentUser.role);
  const visibleOrphans = useMemo(()=>{
    const list = isITAdmin ? orphans : orphans.filter(o=>(o.appIds||[]).some(id=>isAppOwner(catalog.find(a=>a.id===id))));
    return [...list].sort((a,b)=>(a.status==="Resolved"?1:0)-(b.status==="Resolved"?1:0));
  },[orphans,isITAdmin,catalog,isAppOwner]);
  const openOrphanCount = visibleOrphans.filter(o=>o.status!=="Resolved").length;
  // Orphans page: optional filter by product (products is a comma-separated string).
  const [orphanProduct, setOrphanProduct] = useState("All");
  const orphanProducts = useMemo(()=>[...new Set(visibleOrphans.flatMap(o=>(o.products||"").split(",").map(s=>s.trim()).filter(Boolean)))].sort(),[visibleOrphans]);
  const filteredOrphans = useMemo(()=>orphanProduct==="All"?visibleOrphans:visibleOrphans.filter(o=>(o.products||"").split(",").map(s=>s.trim()).includes(orphanProduct)),[visibleOrphans,orphanProduct]);
  const visibleRequests = useMemo(()=>{ if(!isMgr) return requests; return requests.filter(r=>myReports?.has(r.userId)||ownsApp(r.appId)); },[requests,isMgr,myReports,ownsApp]);
  const pendingCount = visibleRequests.filter(r=>r.status==="Pending").length;
  // A people-manager has at least one direct report. Drives the Requests tab for a
  // Finance manager (Manager role already has it via approveRequest permission).
  const isPeopleManager = (myReports?.size||0) > 0;
  // Who can approve a given request: full approvers (Admin/IT/Manager) approve any
  // request they can see; a people-manager (e.g. Finance) may approve a request from
  // one of their direct reports, or for an app they own. Mirrored server-side.
  const canApproveReq = useCallback(r => can(currentUser,"approveRequest") || (!!myReports&&myReports.has(r.userId)) || ownsApp(r.appId), [currentUser,myReports,ownsApp]);
  const approvablePending = visibleRequests.filter(r=>r.status==="Pending"&&canApproveReq(r)).length;

  const toast = msg => { setAssignToast(msg); setTimeout(()=>setAssignToast(null),3000); };

  const submitNewApp = () => {
    if (!newApp.name.trim()) return;
    const id  = newApp.name.toLowerCase().replace(/[^a-z0-9]/g,"").slice(0,12)+Date.now().toString().slice(-4);
    const app = {id,name:newApp.name.trim(),category:newApp.category.trim()||"Other",icon:newApp.icon||"📦",isDefault:false,description:newApp.description?.trim()||"",group:(newApp.group||"").trim(),tiers:[]};
    setCatalog(p=>[...p,app]);
    saveApp(app).catch(console.error);
    setNewApp(BLANK_APP); setShowAddApp(false); setExpandApp(id); setAddTierAppId(id);
  };
  // Add a requested (not-yet-catalogued) app to the catalog and attach it to the
  // request in one step: creates the app with a single tier prefilled from the
  // proposal, then points the request at the new appId/tierId. The request keeps
  // its NewApp payload as a record of what was asked for, and re-routes by the
  // now-known cost — but attaching never auto-approves; approval stays manual.
  const openAttachModal = r => setAttachModal({ reqId:r.id, name:r.newApp?.name||"", category:"", icon:"📦", description:r.newApp?.options||"", tierLabel:r.newApp?.tier||"", tierType:"Non-Standard", billing:"monthly", seatModel:"per-seat", costPerUnit:"" });
  const attachNewApp = () => {
    const m=attachModal; if (!m||!m.name.trim()||m.costPerUnit==="") return;
    const req=requests.find(r=>r.id===m.reqId); if (!req) return;
    const id=m.name.trim().toLowerCase().replace(/[^a-z0-9]/g,"").slice(0,12)+Date.now().toString().slice(-4);
    const tierId=id+"-"+(m.tierLabel.trim()||"standard").toLowerCase().replace(/[^a-z0-9]/g,"").slice(0,8)+Date.now().toString().slice(-3);
    const tier={tierId,label:m.tierLabel.trim()||"Standard",billing:m.billing,seatModel:m.seatModel,costPerUnit:parseFloat(m.costPerUnit)||0,type:m.tierType,...(req.newApp?.link?{purchaseUrl:req.newApp.link}:{})};
    const app={id,name:m.name.trim(),category:m.category.trim()||"Other",icon:m.icon||"📦",isDefault:false,description:m.description?.trim()||"",group:"",tiers:[tier]};
    setCatalog(p=>[...p,app]);
    saveApp(app).catch(console.error);
    const ap=routeRequest(app,monthlyEq(tier));
    const updated={...req,appId:id,tierId,approvalLevel:ap.label};
    setRequests(p=>p.map(r=>r.id===req.id?updated:r));
    saveRequest(updated).catch(console.error);
    setAttachModal(null);
    toast(`${app.name} added to catalog and attached — the request can now be approved`);
  };
  const submitNewTier = appId => {
    if (!newTier.label.trim()||newTier.costPerUnit==="") return;
    const tierId=appId+"-"+newTier.label.toLowerCase().replace(/[^a-z0-9]/g,"").slice(0,8)+Date.now().toString().slice(-3);
    const tier={tierId,label:newTier.label.trim(),billing:newTier.billing,seatModel:newTier.seatModel,costPerUnit:parseFloat(newTier.costPerUnit)||0,type:newTier.type,...(newTier.seats!==""&&newTier.seats!=null?{seats:parseInt(newTier.seats,10)||0}:{}),...(newTier.msSkuId?{msSkuId:newTier.msSkuId,msSkuName:newTier.msSkuName||""}:{}),...(newTier.renewalDate?{renewalDate:newTier.renewalDate}:{}),...(newTier.renewalOwner?{renewalOwner:newTier.renewalOwner,renewalOwnerName:newTier.renewalOwnerName||""}:{}),...(newTier.visibilityOnly?{visibilityOnly:true}:{})};
    setCatalog(p=>{ const updated=p.map(a=>a.id!==appId?a:{...a,tiers:[...a.tiers,tier]}); saveApp(updated.find(a=>a.id===appId)).catch(console.error); return updated; });
    setNewTier(BLANK_TIER); setAddTierAppId(null);
  };
  const deleteTier = (appId,tierId) => {
    setCatalog(p=>{ const updated=p.map(a=>a.id!==appId?a:{...a,tiers:a.tiers.filter(t=>t.tierId!==tierId)}); saveApp(updated.find(a=>a.id===appId)).catch(console.error); return updated; });
    setUsers(p=>p.map(u=>{
      if (!u.tiers.includes(tierId)) return u;            // unchanged — skip save
      const updated = {...u, tiers:u.tiers.filter(t=>t!==tierId)};
      saveUser(updated).catch(console.error);             // only save affected users
      return updated;
    }));
    setDeleteConfirm(null);
  };
  const deleteApp = appId => {
    const tids=catalog.find(a=>a.id===appId)?.tiers.map(t=>t.tierId)||[];
    removeAppFromSP(appId).catch(console.error);
    setCatalog(p=>p.filter(a=>a.id!==appId));
    setUsers(p=>p.map(u=>{
      const hadAny = u.tiers.some(t=>tids.includes(t));
      if (!hadAny) return u;                              // unchanged — skip save
      const updated = {...u, tiers:u.tiers.filter(t=>!tids.includes(t))};
      saveUser(updated).catch(console.error);             // only save affected users
      return updated;
    }));
    setDeleteConfirm(null);
  };
  const saveTierDetails = (appId,tierId) => {
    // Normalize purchased-seats: blank → drop the field; otherwise store as a number.
    const patch = {...editTierData, seats: (editTierData.seats===""||editTierData.seats==null) ? undefined : (parseInt(editTierData.seats,10)||0), visibilityOnly: editTierData.visibilityOnly ? true : undefined};
    setCatalog(p=>{ const updated=p.map(a=>a.id!==appId?a:{...a,tiers:a.tiers.map(t=>t.tierId!==tierId?t:{...t,...patch})}); saveApp(updated.find(a=>a.id===appId)).catch(console.error); return updated; });
    setEditTier(null); setEditTierData({});
  };

  const saveEditApp = (appId) => {
    if (!editAppData.name?.trim()) return;
    setCatalog(p=>{ const updated=p.map(a=>a.id!==appId?a:{...a,name:editAppData.name.trim(),category:editAppData.category.trim()||a.category,icon:editAppData.icon||a.icon,description:editAppData.description?.trim()||"",group:(editAppData.group||"").trim(),approverId:editAppData.approverId||"",approverName:editAppData.approverName||""}); saveApp(updated.find(a=>a.id===appId)).catch(console.error); return updated; });
    setEditAppId(null); setEditAppData({});
  };

  const openInlineAssign = tierId => {
    if (assignTierId===tierId){setAssignTierId(null);return;}
    const sel={}; users.forEach(u=>{sel[u.id]=u.tiers.includes(tierId);}); setAssignSelected(sel); setAssignSearch(""); setAssignTierId(tierId);
  };
  const saveInlineAssign = tierId => {
    let a=0,r=0;
    setUsers(prev=>{ const updated=prev.map(u=>{ const was=u.tiers.includes(tierId),now=!!assignSelected[u.id]; if(was===now) return u; if(now){a++;return{...u,tiers:[...u.tiers,tierId]};} r++;return{...u,tiers:u.tiers.filter(t=>t!==tierId)}; }); updated.filter(u=>{ const was=prev.find(p=>p.id===u.id); return JSON.stringify(was?.tiers)!==JSON.stringify(u.tiers); }).forEach(u=>saveUser(u).catch(console.error)); return updated; });
    setAssignTierId(null); toast(`${a} assigned, ${r} removed`);
  };
  const openBulkModal = appId => {
    const app=catalog.find(a=>a.id===appId), ft=app?.tiers.find(t=>!t.visibilityOnly)?.tierId||"";
    const sel={}; users.forEach(u=>{sel[u.id]=u.tiers.includes(ft);}); setBulkTierId(ft); setBulkSelected(sel); setBulkModal({appId});
  };
  const onBulkTierChange = tierId => { const sel={}; users.forEach(u=>{sel[u.id]=u.tiers.includes(tierId);}); setBulkTierId(tierId); setBulkSelected(sel); };
  const saveBulkAssign = () => {
    if (!bulkTierId) return; let a=0,r=0;
    setUsers(prev=>{ const updated=prev.map(u=>{ const was=u.tiers.includes(bulkTierId),now=!!bulkSelected[u.id]; if(was===now) return u; if(now){a++;return{...u,tiers:[...u.tiers,bulkTierId]};} r++;return{...u,tiers:u.tiers.filter(t=>t!==bulkTierId)}; }); updated.filter(u=>{ const was=prev.find(p=>p.id===u.id); return JSON.stringify(was?.tiers)!==JSON.stringify(u.tiers); }).forEach(u=>saveUser(u).catch(console.error)); return updated; });
    setBulkModal(null); toast(`${a} assigned, ${r} removed`);
  };
  const toggleTier = (uid,tid) => setUsers(p=>p.map(u=>{ if(u.id!==uid) return u; const updated={...u,tiers:u.tiers.includes(tid)?u.tiers.filter(x=>x!==tid):[...u.tiers,tid]}; saveUser(updated).catch(console.error); return updated; }));
  const openReqForm = (uid=null) => { setNewReq({userId:uid??"",appId:"",tierId:"",justification:""}); setShowReqForm(true); setTab("requests"); };
  const submitRequest = () => {
    if (!newReq.userId) return;
    const userId=newReq.userId, u=visibleUsers.find(u=>u.id===userId);
    if (!u) return;
    // Software not in the catalog yet — no cost is known, so it never auto-approves;
    // it stays Pending until IT adds the app to the catalog and attaches it.
    if (newReq.appId===NEW_APP_ID) {
      if (!newReq.newAppName?.trim()||!newReq.justification.trim()) return;
      const entry={id:Date.now(),userId,appId:"",tierId:"",
        newApp:{ name:newReq.newAppName.trim(), tier:(newReq.newAppTier||"").trim(), options:(newReq.newAppOptions||"").trim(), link:(newReq.newAppLink||"").trim() },
        justification:newReq.justification.trim(),by:u.name,date:TODAY.toISOString().split("T")[0],status:"Pending",approvalLevel:"New Software",note:""};
      setRequests(p=>[...p,entry]);
      saveRequest(entry).catch(console.error);
      setNewReq({userId:"",appId:"",tierId:"",justification:""}); setShowReqForm(false);
      return;
    }
    if (!newReq.tierId) return;
    const t=tierByIdDyn(newReq.tierId);
    if (!t) return;
    const ap=routeRequest(appByIdDyn(t.appId),monthlyEq(t)), auto=ap.auto;
    const entry={id:Date.now(),userId,appId:t.appId,tierId:newReq.tierId,justification:newReq.justification.trim(),by:u.name,date:TODAY.toISOString().split("T")[0],status:auto?"Approved":"Pending",approvalLevel:ap.label,note:""};
    setRequests(p=>[...p,entry]);
    saveRequest(entry).catch(console.error);
    if (auto) setUsers(p=>p.map(u=>{ if(u.id!==userId) return u; const updated={...u,tiers:[...new Set([...u.tiers,newReq.tierId])]}; saveUser(updated).catch(console.error); return updated; }));
    setNewReq({userId:"",appId:"",tierId:"",justification:""}); setShowReqForm(false);
  };
  const approveRequest = (rid,note="") => {
    const req=visibleRequests.find(r=>r.id===rid); if (!req) return;
    if (!req.tierId) return;   // unattached new-software request — nothing to assign yet
    setRequests(p=>p.map(r=>{ if(r.id!==rid) return r; const updated={...r,status:"Approved",note:note.trim()}; saveRequest(updated).catch(console.error); return updated; }));
    setUsers(p=>p.map(u=>{ if(u.id!==req.userId) return u; const updated={...u,tiers:[...new Set([...u.tiers,req.tierId])]}; saveUser(updated).catch(console.error); return updated; }));
    setApproveModal(null);
  };
  const denyRequest = rid => {
    if (!visibleRequests.some(r=>r.id===rid)) return;
    setRequests(p=>p.map(r=>{ if(r.id!==rid) return r; const updated={...r,status:"Denied"}; saveRequest(updated).catch(console.error); return updated; }));
  };
  const exportCSV = () => {
    const rows=[["Name","Email","Manager","Dept","Licenses","Monthly OpEx (EUR)","CapEx (EUR)"]];
    users.forEach(u=>{ const c=userCosts(u,allTiers,seatCounts); rows.push([u.name,u.email,u.manager||"",u.dept,u.tiers.map(tid=>{const t=tierByIdDyn(tid);return t?`${t.appName} ${t.label}`:tid;}).join("; "),c.opex.toFixed(2),c.capex.toFixed(2)]); });
    const csv=rows.map(r=>r.map(c=>`"${c}"`).join(",")).join("\n");
    const a=document.createElement("a"); a.href=URL.createObjectURL(new Blob([csv],{type:"text/csv"})); a.download="licenses.csv"; a.click();
  };

  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  // Synchronous lock, not just `syncing` state — `disabled={syncing}` only takes
  // effect on the next render, so a rapid double-click can fire two overlapping
  // sync passes before the button visually disables, each blind to the other's
  // in-flight LO_Users creates and each writing a duplicate row for the same person.
  const syncLockRef = useRef(false);
  const [dismissedLeavers, setDismissedLeavers] = useState([]);   // userIds handled
  const [dismissedDeptChanges, setDismissedDeptChanges] = useState([]);

  // ─── VENDOR SYNC ────────────────────────────────────────────────────────────
  // Pull products + per-user access from a vendor (Atlassian, Slack, …), build a
  // preview diff against the catalog and users, then apply on confirm. Reads only;
  // all writes reuse the existing saveApp/saveUser paths. Shared across providers
  // via SYNC_PROVIDERS — each one just supplies a fetcher and an ownsApp matcher.
  const [syncingKey, setSyncingKey] = useState(null);   // provider key currently fetching
  const [atlasApplying, setAtlasApplying] = useState(false);
  const [atlasError, setAtlasError] = useState(null);
  const [atlasPreview, setAtlasPreview] = useState(null);

  const previewProviderSync = async (provider) => {
    setSyncingKey(provider.key); setAtlasError(null); setAtlasPreview(null);
    try {
      const { products, users: srcUsers } = await provider.fetch(catalog);
      const productByKey    = Object.fromEntries(products.map(p => [p.key, p]));
      const productByTierId = Object.fromEntries(products.map(p => [p.tier.tierId, p]));

      // Catalog diff — new app (tier included), a new tier on an existing app, or
      // a price/billing change on a tier that already exists. Re-runs reconcile
      // prices: a tier whose cost or billing differs from the synced value is
      // listed as an update and applied on confirm (never silently overwritten).
      const newApps = [], newTiers = [], priceUpdates = [];
      for (const p of products) {
        const existing = catalog.find(a => a.id === p.appId);
        const appObj = { id:p.appId, name:p.name, category:p.category, icon:p.icon, isDefault:false, group:provider.label, tiers:[p.tier] };
        if (!existing) { newApps.push(appObj); continue; }
        const tier = existing.tiers.find(t => t.tierId === p.tier.tierId);
        if (!tier) { newTiers.push({ appId:p.appId, appName:existing.name, tier:p.tier }); continue; }
        if (tier.costPerUnit !== p.tier.costPerUnit || tier.billing !== p.tier.billing) {
          priceUpdates.push({ appId:p.appId, appName:existing.name, tierId:tier.tierId, label:tier.label,
            oldCost:tier.costPerUnit, newCost:p.tier.costPerUnit, oldBilling:tier.billing, newBilling:p.tier.billing });
        }
      }

      // Assignment diff — match vendor users to LicenseOps users by identity.
      // Default identity is email; a provider may override (GitHub matches on the
      // per-user `github` field, falling back to email). Unmatched accounts (no
      // directory user) are *orphans* — paid seats with no employee — and get their
      // own section. Matched-but-department-less users can't be assigned (existing
      // rule), so they're listed separately.
      const norm     = s => (s==null ? "" : String(s)).toLowerCase().trim();
      const loKeys   = provider.loKeys  || (u  => [u.email]);
      const srcKeys  = provider.srcKeys || (su => [su.email]);
      const loByKey  = new Map();   // identity → LO user (first wins)
      users.forEach(u => loKeys(u).map(norm).filter(Boolean).forEach(k => { if (!loByKey.has(k)) loByKey.set(k, u); }));
      const srcByKey = new Map();   // identity → vendor account (first wins)
      srcUsers.forEach(su => srcKeys(su).map(norm).filter(Boolean).forEach(k => { if (!srcByKey.has(k)) srcByKey.set(k, su); }));
      const findLo  = su => { for (const k of srcKeys(su).map(norm).filter(Boolean)) { const u = loByKey.get(k);   if (u)  return u;  } return null; };
      const findSrc = u  => { for (const k of loKeys(u).map(norm).filter(Boolean))   { const s = srcByKey.get(k); if (s)  return s;  } return null; };
      const assignmentsByUser = {};   // userId -> [tierId]
      const assignments = [];         // flat, for display/count
      const skippedNoDept = [], orphans = [];
      for (const su of srcUsers) {
        const lo    = findLo(su);
        const prods = su.products.map(k => productByKey[k]).filter(Boolean);
        if (!lo)          { orphans.push({ email:su.login||su.email||"", name:su.name, products:prods.map(p=>p.name), appIds:prods.map(p=>p.appId), estMonthly:prods.reduce((s,p)=>s+monthlyEq(p.tier),0) }); continue; }
        if (!hasDept(lo)) { skippedNoDept.push({ email:su.email, name:lo.name }); continue; }
        const toAdd = prods.map(p=>p.tier.tierId).filter(tid => !lo.tiers.includes(tid));
        if (!toAdd.length) continue;   // already has everything → nothing to do
        assignmentsByUser[lo.id] = [...(assignmentsByUser[lo.id]||[]), ...toAdd];
        toAdd.forEach(tid => assignments.push({ userId:lo.id, userName:lo.name, tierId:tid, productName:productByTierId[tid]?.name||tid }));
      }

      // Revocation drift (report-only) — provider-sourced licenses a LicenseOps
      // user still holds but the vendor no longer backs (access pulled, or the user
      // is gone from the vendor entirely). We never auto-remove; just surface them.
      const ownedTierIds = new Set(catalog.filter(provider.ownsApp).flatMap(a=>a.tiers.filter(t=>!t.visibilityOnly).map(t=>t.tierId)));
      products.forEach(p => ownedTierIds.add(p.tier.tierId));
      const revocations = [];
      for (const u of users) {
        const held = u.tiers.filter(tid => ownedTierIds.has(tid));
        if (!held.length) continue;
        const su = findSrc(u);
        const shouldHave = new Set(su ? su.products.map(k => productByKey[k]?.tier.tierId).filter(Boolean) : []);
        held.filter(tid => !shouldHave.has(tid)).forEach(tid => revocations.push({
          userId:u.id, userName:u.name, tierId:tid,
          productName: productByTierId[tid]?.name || allTiers.find(t=>t.tierId===tid)?.appName || tid,
          reason: su ? `Access removed in ${provider.label}` : `No longer in ${provider.label}`,
        }));
      }

      setAtlasPreview({
        providerLabel: provider.label,
        newApps, newTiers, priceUpdates, assignments, assignmentsByUser,
        revocations, orphans, skippedNoDept,
        unpriced: products.filter(p => !p.priced).map(p => p.name),
        productCount: products.length,
        userCount: srcUsers.length,
      });
    } catch (e) {
      setAtlasError(e.message);
    } finally {
      setSyncingKey(null);
    }
  };

  const applyAtlassianSync = async () => {
    if (!atlasPreview) return;
    setAtlasApplying(true);
    const { providerLabel, newApps, newTiers, priceUpdates, assignmentsByUser, assignments, orphans:previewOrphans } = atlasPreview;
    try {
      // Catalog: add new apps, graft new tiers, refresh changed prices — then save
      // each touched existing app exactly once.
      setCatalog(prev => {
        let next = [...prev];
        newApps.forEach(na => { if (!next.find(a => a.id === na.id)) { next = [...next, na]; saveApp(na).catch(console.error); } });
        newTiers.forEach(nt => { next = next.map(a => a.id !== nt.appId ? a : { ...a, tiers:[...a.tiers, nt.tier] }); });
        priceUpdates.forEach(pu => { next = next.map(a => a.id !== pu.appId ? a : { ...a, tiers:a.tiers.map(t => t.tierId!==pu.tierId ? t : { ...t, costPerUnit:pu.newCost, billing:pu.newBilling }) }); });
        new Set([...newTiers.map(n=>n.appId), ...priceUpdates.map(p=>p.appId)]).forEach(appId => {
          const app = next.find(a => a.id === appId); if (app) saveApp(app).catch(console.error);
        });
        return next;
      });
      // Users: merge assigned tierIds, one save per affected user. (Revocations are
      // report-only — never removed here.)
      setUsers(prev => prev.map(u => {
        const add = assignmentsByUser[u.id];
        if (!add || !add.length) return u;
        const merged = [...new Set([...u.tiers, ...add])];
        if (merged.length === u.tiers.length) return u;
        const updated = { ...u, tiers:merged };
        saveUser(updated).catch(console.error);
        return updated;
      }));
      // Log orphaned accounts so an app owner / admin can action them later.
      const { logged, failed } = await logOrphans(providerLabel, previewOrphans||[]);
      if (failed>0) { setAtlasPreview(null); setAtlasError(`Couldn't save ${failed} orphaned account${failed!==1?"s":""} — create a SharePoint list named "LO_Orphans" (columns: Title, Vendor, UserName, Products, AppIds, EstMonthly, Status, DetectedDate, Comment), then re-run the sync.`); return; }
      const a=newApps.length, t=newTiers.length, pu=priceUpdates.length, asg=assignments.length;
      toast(`${providerLabel} sync: ${a} app${a!==1?"s":""}, ${t} tier${t!==1?"s":""}, ${pu} price update${pu!==1?"s":""}, ${asg} assignment${asg!==1?"s":""}${logged?`, ${logged} orphan${logged!==1?"s":""} logged`:""}`);
      setAtlasPreview(null);
    } catch (e) {
      setAtlasError(e.message);
    } finally {
      setAtlasApplying(false);
    }
  };

  // Reclaim a leaver: remove the user (which unassigns all their licenses) from the system
  const reclaimLeaver = async (userId) => {
    await removeUser(userId).catch(console.error);
    setDismissedLeavers(p => [...p, userId]);
    toast("Leaver removed and licenses reclaimed");
  };
  const [cleanupConfirm, setCleanupConfirm] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  // Users with no/General department — candidates for cleanup
  const orphanUsers = useMemo(()=>users.filter(u => !u.dept || u.dept.trim()==="" || u.dept.trim().toLowerCase()==="general"), [users]);

  const cleanupOrphans = async () => {
    setCleaning(true);
    let removed = 0;
    for (const u of orphanUsers) {
      await removeUser(u.id).catch(console.error);
      removed++;
    }
    setCleaning(false);
    setCleanupConfirm(false);
    toast(`Removed ${removed} user${removed!==1?"s":""} with no department`);
  };

  const syncUsersFromEntra = async () => {
    if (syncLockRef.current) return;   // already running — ignore the re-click
    syncLockRef.current = true;
    setSyncing(true); setSyncResult(null);
    try {
      const allEntraUsers = await fetchEntraUsers();
      // Defensive: collapse any duplicate rows Graph hands back for the same person
      // (seen as a transient directory-replication artifact right after a user is
      // created) so one entraUser.id is never processed twice in this pass — the
      // `existing` check below is a snapshot taken once at the start of the sync, so
      // a repeat would read as "not found" both times and get pushed into local
      // state twice, even though saveUser's own id→spId map correctly collapses the
      // SharePoint writes to a single row.
      const seenEntraIds = new Set();
      const filtered = allEntraUsers
        .filter(u => (u.mail||u.userPrincipalName) && u.displayName && u.department && u.department.trim())
        .filter(u => { if (seenEntraIds.has(u.id)) return false; seenEntraIds.add(u.id); return true; });

      let added=0, updated=0, unchanged=0;
      const getEmail = u => u.mail || u.userPrincipalName;
      // Users with no department: persisted to LO_Orphans (vendor "Entra") after the
      // upsert loop below, so they survive the sync and show on the Orphans page.
      const noDeptUsers = allEntraUsers
        .filter(u => (u.mail||u.userPrincipalName) && u.displayName && (!u.department || !u.department.trim()))
        .map(u => ({ email:getEmail(u), name:u.displayName }));
      const skippedNoDept = noDeptUsers.length;

      // Build a set of active Entra identities for leaver detection
      const activeIds    = new Set(filtered.map(u => u.id));
      const activeEmails = new Set(filtered.map(u => getEmail(u).toLowerCase()));

      // Track department changes for review
      const deptChanges = [];

      for (const entraUser of filtered) {
        const email    = getEmail(entraUser);
        const dept     = entraUser.department.trim();
        const manager  = entraUser.manager?.displayName || entraUser.manager?.mail || entraUser.manager?.userPrincipalName || "";
        const existing = users.find(u => u.id === entraUser.id || u.email === email);

        if (existing) {
          const nameChanged    = existing.name !== entraUser.displayName;
          const deptChanged    = existing.dept !== dept;
          const emailChanged   = existing.email !== email;
          const managerChanged = (existing.manager || "") !== manager;
          if (deptChanged) {
            // Flag for review: which of their licenses are NOT in the new dept's default bundle
            const newDefaults = deptDefaults[dept] || [];
            const nonStandard = existing.tiers.filter(tid => !newDefaults.includes(tid));
            deptChanges.push({ userId:existing.id, name:existing.name, oldDept:existing.dept, newDept:dept, nonStandardTiers:nonStandard });
          }
          if (nameChanged || deptChanged || emailChanged || managerChanged) {
            const updatedUser = { ...existing, name:entraUser.displayName, email, dept, manager };
            setUsers(p => p.map(u => u.id === existing.id ? updatedUser : u));
            await saveUser(updatedUser);
            updated++;
          } else {
            unchanged++;
          }
        } else {
          const newUser = { id:entraUser.id, name:entraUser.displayName, email, dept, manager, tiers:[] };
          setUsers(p => [...p, newUser]);
          await saveUser(newUser);
          added++;
        }
      }

      // Leaver detection: users in LicenseOps who are no longer active in Entra
      const leavers = users.filter(u =>
        !activeIds.has(u.id) && !activeEmails.has((u.email||"").toLowerCase())
      ).map(u => ({ userId:u.id, name:u.name, email:u.email, dept:u.dept, tiers:u.tiers }));

      // Persist department-less users as orphans (and auto-resolve fixed ones)
      const { logged:orphansLogged, resolved:orphansResolved, failed:orphansFailed } = await syncEntraOrphans(orphans, noDeptUsers);

      setSyncResult({ added, updated, unchanged, skippedNoDept, orphansLogged, orphansResolved, orphansFailed, total:filtered.length, leavers, deptChanges });
    } catch(e) {
      setSyncResult({ error: e.message });
    } finally {
      setSyncing(false);
      syncLockRef.current = false;
    }
  };

  return (
    <div style={{ fontFamily:"'DM Sans','Segoe UI',sans-serif",background:"#F4F6FA",minHeight:"100vh",color:"#1A1F36" }}>
      {spLoading&&<div style={{ background:"#EFF6FF",borderBottom:"1px solid #BFDBFE",padding:"8px 28px",fontSize:12,color:"#2563EB",display:"flex",alignItems:"center",gap:8 }}><span style={{ animation:"spin 1s linear infinite",display:"inline-block" }}>↻</span> Syncing with SharePoint…</div>}
      {spError&&<div style={{ background:"#FEF2F2",borderBottom:"1px solid #FECACA",padding:"8px 28px",fontSize:12,color:"#DC2626",display:"flex",alignItems:"center",gap:8 }}>⚠ SharePoint sync error: {spError} — showing local data</div>}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        ::-webkit-scrollbar{width:5px}::-webkit-scrollbar-track{background:#F4F6FA}::-webkit-scrollbar-thumb{background:#CBD2E0;border-radius:3px}
        input,select,textarea{font-family:inherit}
        .nb{background:none;border:none;cursor:pointer;padding:9px 15px;border-radius:8px;font-family:inherit;font-size:13px;font-weight:500;color:#5A637A;transition:all .15s}
        .nb:hover{background:#F0F2F7;color:#374060}.nb.act{background:#EBF0FF;color:#1D4ED8;box-shadow:inset 0 -2px 0 #2563EB}
        .card{background:#FFFFFF;border:1px solid #E2E6F0;border-radius:14px;padding:22px}
        .btn{border:none;cursor:pointer;font-family:inherit;font-weight:600;border-radius:8px;transition:all .15s;font-size:13px}
        .btnP{background:#2563EB;color:#fff;padding:9px 18px}.btnP:hover{background:#1D4ED8}
        .btnG{background:#EEF1F7;color:#5A637A;padding:9px 16px}.btnG:hover{background:#E2E6F0;color:#374060}
        .btnSm{padding:5px 11px;font-size:11.5px;border-radius:6px}
        .inp{background:#F8F9FC;border:1px solid #D8DCE8;border-radius:8px;padding:9px 13px;color:#1A1F36;font-size:13px;outline:none;width:100%}
        .inp:focus{border-color:#2563EB}
        .tr:hover{background:#F8F9FC}
        .tog{width:36px;height:20px;border-radius:10px;border:none;cursor:pointer;position:relative;transition:background .2s}
        .tog::after{content:'';position:absolute;top:3px;width:14px;height:14px;background:white;border-radius:50%;transition:left .2s}
        .on{background:#2563EB}.on::after{left:19px}.off{background:#D8DCE8}.off::after{left:3px}
        .chip{display:inline-flex;align-items:center;background:#F0F2F7;border:1px solid #D8DCE8;border-radius:5px;padding:2px 7px;font-size:10.5px;color:#5A637A;margin:2px}
        .chip.def{border-color:#93AFEF;color:#1D4ED8;background:#EBF0FF}
        .modal-bg{position:fixed;inset:0;background:#00000088;display:flex;align-items:center;justify-content:center;z-index:100}
        .modal{background:#FFFFFF;border:1px solid #E2E6F0;border-radius:16px;padding:28px;min-width:420px;max-width:520px}
        .sl{font-size:10.5px;font-weight:700;letter-spacing:.1em;color:#8A93AA;margin-bottom:10px}
        .warn-box{background:#FFFBF0;border:1px solid #F5D680;border-radius:10px;padding:12px 16px;display:flex;align-items:center;gap:12px;margin-bottom:10px}
        @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
      `}</style>

      {/* NAV */}
      <div style={{ background:"#FFFFFF",borderBottom:"1px solid #E8EBF3",padding:"0 28px" }}>
        <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",maxWidth:1480,margin:"0 auto",height:54 }}>
          <div style={{ display:"flex",alignItems:"center",gap:10 }}>
            <img src={BRAND.logo} alt="Loqus" style={{ height:26,maxWidth:120,objectFit:"contain" }} />
            <span style={{ color:"#D8DCE8" }}>|</span>
            <span style={{ fontWeight:700,fontSize:14 }}>LicenseOps</span>
            <span style={{ color:"#8A93AA",fontSize:11 }}>· Microsoft Entra ID</span>
          </div>
          <nav style={{ display:"flex",gap:2 }}>
            {[
              ["dashboard","Dashboard",       true],
              ["users",    "Users & Licenses",true],
              ["catalog",  can(currentUser,"viewCatalog")?"App Catalog":"Managed Apps", can(currentUser,"viewCatalog")||ownsAnyApp],
              ["requests", "Requests",        can(currentUser,"submitRequest")||can(currentUser,"approveRequest")||isPeopleManager],
              ["depts",    "Dept Defaults",   can(currentUser,"viewDeptDefaults")],
              ["orphans",  "Orphans",         isITAdmin||(ownsAnyApp&&visibleOrphans.length>0)],
              ["reporting","Reporting",       isITAdmin||currentUser.role===ROLES.Finance],
              ["settings", "Settings",        can(currentUser,"editConfig")],
            ].filter(([,,s])=>s).map(([id,label])=>(
              <button key={id} className={`nb ${tab===id?"act":""}`} onClick={()=>setTab(id)}>
                {label}
                {id==="requests"&&approvablePending>0&&(can(currentUser,"approveRequest")||isPeopleManager)&&<span style={{ marginLeft:5,background:"#F59E0B",color:"#000",borderRadius:10,padding:"1px 6px",fontSize:9,fontWeight:800 }}>{approvablePending}</span>}
                {id==="orphans"&&openOrphanCount>0&&<span style={{ marginLeft:5,background:"#EF4444",color:"#fff",borderRadius:10,padding:"1px 6px",fontSize:9,fontWeight:800 }}>{openOrphanCount}</span>}
              </button>
            ))}
          </nav>
          <div style={{ display:"flex",alignItems:"center",gap:10 }}>
            <div style={{ display:"flex",alignItems:"center",gap:8,background:"#F8F9FC",border:"1px solid #E2E6F0",borderRadius:20,padding:"5px 12px 5px 6px" }}>
              <div style={{ width:26,height:26,borderRadius:"50%",background:"linear-gradient(135deg,#2563EB,#7C9EFF)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:"#fff" }}>{currentUser.avatar}</div>
              <div>
                <div style={{ fontSize:12,fontWeight:600,color:"#1A1F36",lineHeight:1.2 }}>{currentUser.name}</div>
                <div style={{ fontSize:10,color:ROLE_META[currentUser.role].color,fontWeight:700 }}>{ROLE_META[currentUser.role].label}</div>
              </div>
            </div>
            <button onClick={onSignOut} style={{ background:"none",border:"none",cursor:"pointer",color:"#8A93AA",fontSize:12,fontFamily:"inherit",padding:"4px 8px" }}>Sign out</button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth:1480,margin:"0 auto",padding:"28px" }}>

        {/* DASHBOARD */}
        {tab==="dashboard"&&<div>
          <h1 style={{ fontSize:20,fontWeight:700,marginBottom:4 }}>Overview</h1>
          <p style={{ color:"#8A93AA",fontSize:13,marginBottom:22 }}>License spend across all billing models</p>
          <div style={{ display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:13,marginBottom:22 }}>
            {[
              {label:"Monthly OpEx", value:eurInt(orgStats.opex),       sub:"recurring/month",   color:"#4A6FFF",icon:"↻"},
              {label:"Annual OpEx",  value:eurInt(orgStats.opexAnnual),  sub:"recurring/year",    color:"#7C5FFF",icon:"📅"},
              {label:"CapEx",        value:eurInt(orgStats.capex),       sub:"one-time deployed", color:"#10B981",icon:"⬛"},
              {label:"Total Users",  value:visibleUsers.length,           sub:isMgr?"in your organization":"active in Entra", color:"#F59E0B",icon:"👤"},
              {label:"Renewals ≤90d",value:renewalsSoon.length,          sub:"upcoming",          color:"#EF4444",icon:"⚠"},
            ].map(s=>(
              <div key={s.label} style={{ background:"#FFFFFF",border:"1px solid #E2E6F0",borderRadius:13,padding:"16px 18px" }}>
                <div style={{ fontSize:18,marginBottom:6 }}>{s.icon}</div>
                <div style={{ fontSize:24,fontWeight:700,fontFamily:"'DM Mono',monospace",color:s.color }}>{s.value}</div>
                <div style={{ fontSize:11,color:"#5A637A",marginTop:3 }}>{s.label}</div>
                <div style={{ fontSize:10,color:"#8A93AA",marginTop:2 }}>{s.sub}</div>
              </div>
            ))}
          </div>
          <div style={{ display:"grid",gridTemplateColumns:"1.6fr 1fr",gap:16 }}>
            <div className="card">
              <p className="sl">SPEND BY APP & TIER</p>
              <table style={{ width:"100%",borderCollapse:"collapse" }}>
                <thead><tr style={{ borderBottom:"1px solid #E2E6F0" }}>
                  {["App","Tier","Billing","Seats","Monthly","CapEx"].map(h=><th key={h} style={{ padding:"6px 8px",textAlign:"left",fontSize:10,color:"#8A93AA",fontWeight:700 }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {allTiers.filter(t=>visibleUsers.some(u=>u.tiers.includes(t.tierId))).sort((a,b)=>monthlyEq(b)*visibleUsers.filter(u=>u.tiers.includes(b.tierId)).length-monthlyEq(a)*visibleUsers.filter(u=>u.tiers.includes(a.tierId)).length).map(t=>{
                    const seats=visibleUsers.filter(u=>u.tiers.includes(t.tierId)).length;
                    // Flat licenses: show the viewer's portion (their seats ÷ all assignees) of the lump cost.
                    const flatFrac=t.seatModel==="flat"?(seatCounts[t.tierId]?seats/seatCounts[t.tierId]:0):1;
                    const mo=t.seatModel==="flat"?monthlyEq(t)*flatFrac:monthlyEq(t)*seats;
                    const cx=t.billing==="one-time"?(t.seatModel==="flat"?t.costPerUnit*flatFrac:t.costPerUnit*seats):0;
                    return (
                      <tr key={t.tierId} className="tr" style={{ borderBottom:"1px solid #F0F2F7" }}>
                        <td style={{ padding:"8px 8px",fontSize:12,fontWeight:600 }}>{t.appIcon} {t.appName}</td>
                        <td style={{ padding:"8px 8px",fontSize:11.5,color:"#5A637A" }}>{t.label}</td>
                        <td style={{ padding:"8px 8px" }}><BillingBadge b={t.billing}/></td>
                        <td style={{ padding:"8px 8px",fontFamily:"'DM Mono',monospace",fontSize:12 }}>{t.seatModel==="flat"?"flat":seats}</td>
                        <td style={{ padding:"8px 8px",fontFamily:"'DM Mono',monospace",fontSize:12,color:t.billing==="one-time"?"#C0C8D8":"#1D4ED8" }}>{t.billing==="one-time"?"—":eur(mo)}</td>
                        <td style={{ padding:"8px 8px",fontFamily:"'DM Mono',monospace",fontSize:12,color:"#10B981" }}>{cx>0?eur(cx):"—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ display:"flex",flexDirection:"column",gap:14 }}>
              <div className="card" style={{ flex:1 }}>
                <p className="sl">UPCOMING RENEWALS</p>
                {renewalsSoon.length===0?<p style={{ fontSize:12,color:"#8A93AA" }}>No renewals in 90 days.</p>:renewalsSoon.map(t=>{
                  const seats=visibleUsers.filter(u=>u.tiers.includes(t.tierId)).length, cost=t.seatModel==="flat"?t.costPerUnit:t.costPerUnit*seats, d=daysUntil(t.renewalDate);
                  return (
                    <div key={t.tierId} className="warn-box">
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:13,fontWeight:600 }}>{t.appName} — {t.label}</div>
                        <div style={{ fontSize:10.5,color:"#92650A",marginTop:2 }}>{fmtDate(t.renewalDate)} · {seats} seat{seats!==1?"s":""}</div>
                        <div style={{ fontSize:11.5,color:"#D97706",fontFamily:"'DM Mono',monospace",marginTop:3 }}>{eur(cost)}/yr</div>
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <div style={{ fontSize:20,fontWeight:700,color:d<=30?"#EF4444":"#F59E0B",fontFamily:"'DM Mono',monospace" }}>{d}d</div>
                        <div style={{ fontSize:10,color:"#92650A" }}>left</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="card">
                <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10 }}>
                  <p className="sl" style={{ marginBottom:0 }}>PENDING REQUESTS</p>
                  {pendingCount>0&&<button className="btn btnP btnSm" onClick={()=>setTab("requests")}>Review all</button>}
                </div>
                {visibleRequests.filter(r=>r.status==="Pending").slice(0,3).map(r=>{
                  const t=tierByIdDyn(r.tierId), u=users.find(u=>u.id===r.userId);
                  const ap=isUnattachedNewApp(r) ? { label:"New Software", color:APPR_COLOR["New Software"] } : routeRequest(appByIdDyn(r.appId),t?monthlyEq(t):0);
                  return (
                    <div key={r.id} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 0",borderBottom:"1px solid #E8EBF3" }}>
                      <div>
                        <div style={{ fontSize:12.5,fontWeight:600 }}>{isUnattachedNewApp(r) ? <>🆕 {r.newApp.name}{r.newApp.tier?` — ${r.newApp.tier}`:""}</> : <>{r.appId&&appByIdDyn(r.appId)?.icon} {t?.appName} — {t?.label}</>}</div>
                        <div style={{ fontSize:11,color:"#8A93AA",marginTop:2 }}>{u?.name} · {r.date}</div>
                      </div>
                      <Badge label={ap.label} bg={`${ap.color}22`} color={ap.color} small/>
                    </div>
                  );
                })}
                {pendingCount===0&&<p style={{ fontSize:12,color:"#8A93AA" }}>No pending requests.</p>}
              </div>
              {/* Seat utilization — tiers with a purchased-seat count: used vs free.
                  Org-wide (purchased seats are an org concern), so hidden from scoped managers. */}
              {!isMgr&&(()=>{
                const seated = allTiers.filter(t=>Number(t.seats)>0)
                  .map(t=>{ const used=seatCounts[t.tierId]||0, purchased=Number(t.seats), free=purchased-used; return {t,used,purchased,free}; })
                  .sort((a,b)=>(a.free)-(b.free));
                return (
                  <div className="card">
                    <p className="sl">SEAT UTILIZATION</p>
                    {seated.length===0&&<p style={{ fontSize:12,color:"#8A93AA" }}>No licenses have a purchased-seat count yet. Set one in App Catalog → Edit Tier.</p>}
                    {seated.map(({t,used,purchased,free})=>{
                      const col=free<0?"#EF4444":free===0?"#D97706":"#10B981", pct=Math.min(100,Math.round(used/purchased*100));
                      return (
                        <div key={t.tierId} style={{ padding:"8px 0",borderBottom:"1px solid #E8EBF3" }}>
                          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5 }}>
                            <span style={{ fontSize:12.5,fontWeight:600 }}>{t.appIcon} {t.appName} <span style={{ color:"#8A93AA",fontWeight:400 }}>{t.label}</span></span>
                            <span style={{ fontSize:12,fontFamily:"'DM Mono',monospace",color:col }}>{used}/{purchased} · {free<0?`${-free} over`:`${free} free`}</span>
                          </div>
                          <div style={{ height:6,background:"#EEF1F7",borderRadius:4,overflow:"hidden" }}><div style={{ width:`${pct}%`,height:"100%",background:col }}/></div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>}

        {/* USERS */}
        {tab==="users"&&<div>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20 }}>
            <div>
              <h1 style={{ fontSize:20,fontWeight:700,marginBottom:3 }}>Users & Licenses</h1>
              <p style={{ color:"#8A93AA",fontSize:13 }}>{isMgr?"Showing everyone in your organization (reports up to you)":"Manage per-user license assignments"}{" · "}<span style={{ color:"#2563EB" }}>Blue = dept default · Grey = add-on</span>
            {syncResult&&!syncResult.error&&<span style={{ marginLeft:10,color:"#10B981",fontWeight:600 }}>✓ {syncResult.added} added, {syncResult.updated} updated, {syncResult.unchanged} unchanged{syncResult.skippedNoDept>0?`, ${syncResult.skippedNoDept} without dept → Orphans${syncResult.orphansLogged>0?` (${syncResult.orphansLogged} new)`:""}`:""}{syncResult.orphansResolved>0?`, ${syncResult.orphansResolved} orphan${syncResult.orphansResolved!==1?"s":""} auto-resolved`:""}</span>}
            {syncResult&&!syncResult.error&&syncResult.orphansFailed>0&&<span style={{ marginLeft:10,color:"#EF4444" }}>⚠ {syncResult.orphansFailed} orphan{syncResult.orphansFailed!==1?"s":""} couldn't be saved — check the LO_Orphans list exists</span>}
            {syncResult?.error&&<span style={{ marginLeft:10,color:"#EF4444" }}>⚠ {syncResult.error}</span>}</p>
            </div>
            {can(currentUser,"editUsers")&&<div style={{ display:"flex",gap:8 }}>
              <button className="btn btnG" onClick={exportCSV}>↓ CSV</button>
              {orphanUsers.length>0&&<button className="btn btnG" onClick={()=>setCleanupConfirm(true)} style={{ color:"#EF4444" }}>
                🧹 Clean up ({orphanUsers.length})
              </button>}
              <button className="btn btnP" onClick={syncUsersFromEntra} disabled={syncing} style={{ display:"flex",alignItems:"center",gap:7 }}>
                <span style={{ display:"inline-block",animation:syncing?"spin 1s linear infinite":"none" }}>⟳</span>
                {syncing?"Syncing…":"Sync from Entra"}
              </button>
            </div>}
          </div>

          {/* ── SYNC REVIEW PANEL: leavers + department changes ── */}
          {syncResult&&!syncResult.error&&(() => {
            const activeLeavers = (syncResult.leavers||[]).filter(l => !dismissedLeavers.includes(l.userId));
            const activeDeptChanges = (syncResult.deptChanges||[]).filter(d => !dismissedDeptChanges.includes(d.userId));
            if (activeLeavers.length===0 && activeDeptChanges.length===0) return null;
            return (
              <div style={{ marginBottom:20 }}>
                {/* Leavers */}
                {activeLeavers.length>0&&(
                  <div style={{ background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:12,padding:"16px 18px",marginBottom:12 }}>
                    <div style={{ fontSize:13,fontWeight:700,color:"#DC2626",marginBottom:4 }}>⚠ {activeLeavers.length} leaver{activeLeavers.length!==1?"s":""} holding licenses</div>
                    <p style={{ fontSize:12,color:"#92400E",marginBottom:12 }}>These users are no longer active in Entra but still have licenses assigned. Reclaim to free up the licenses and remove them.</p>
                    {activeLeavers.map(l=>(
                      <div key={l.userId} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 12px",background:"#FFFFFF",border:"1px solid #FECACA",borderRadius:8,marginBottom:6 }}>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:13,fontWeight:600 }}>{l.name} <span style={{ color:"#8A93AA",fontWeight:400 }}>· {l.dept}</span></div>
                          <div style={{ fontSize:11,color:"#8A93AA",marginTop:2 }}>{l.email}</div>
                          <div style={{ display:"flex",flexWrap:"wrap",gap:4,marginTop:6 }}>
                            {l.tiers.map(tid=>{ const t=tierByIdDyn(tid); return t?<span key={tid} className="chip">{t.appIcon} {t.appName} {t.label}</span>:null; })}
                            {l.tiers.length===0&&<span style={{ fontSize:11,color:"#C0C8D8" }}>No licenses</span>}
                          </div>
                        </div>
                        <div style={{ display:"flex",gap:6,marginLeft:12 }}>
                          <button className="btn btnSm" style={{ background:"#EF4444",color:"#fff" }} onClick={()=>reclaimLeaver(l.userId)}>Reclaim & remove</button>
                          <button className="btn btnG btnSm" onClick={()=>setDismissedLeavers(p=>[...p,l.userId])}>Dismiss</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {/* Department changes */}
                {activeDeptChanges.length>0&&(
                  <div style={{ background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:12,padding:"16px 18px" }}>
                    <div style={{ fontSize:13,fontWeight:700,color:"#D97706",marginBottom:4 }}>🔄 {activeDeptChanges.length} department change{activeDeptChanges.length!==1?"s":""} to review</div>
                    <p style={{ fontSize:12,color:"#92400E",marginBottom:12 }}>These users moved departments. Check whether their current licenses still fit their new department.</p>
                    {activeDeptChanges.map(d=>(
                      <div key={d.userId} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 12px",background:"#FFFFFF",border:"1px solid #FDE68A",borderRadius:8,marginBottom:6 }}>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:13,fontWeight:600 }}>{d.name}</div>
                          <div style={{ fontSize:11,color:"#8A93AA",marginTop:2 }}>{d.oldDept} → <span style={{ color:"#D97706",fontWeight:600 }}>{d.newDept}</span></div>
                          {d.nonStandardTiers.length>0&&(
                            <div style={{ marginTop:6 }}>
                              <span style={{ fontSize:10.5,color:"#92400E",fontWeight:600 }}>Not in {d.newDept} defaults: </span>
                              {d.nonStandardTiers.map(tid=>{ const t=tierByIdDyn(tid); return t?<span key={tid} className="chip" style={{ borderColor:"#FDE68A",color:"#D97706",background:"#FFFBEB" }}>{t.appIcon} {t.appName} {t.label}</span>:null; })}
                            </div>
                          )}
                          {d.nonStandardTiers.length===0&&<div style={{ fontSize:11,color:"#10B981",marginTop:4 }}>✓ All current licenses fit the new department</div>}
                        </div>
                        <div style={{ display:"flex",gap:6,marginLeft:12 }}>
                          <button className="btn btnG btnSm" onClick={()=>{ setDeptFilter(d.newDept); setEditUser(d.userId); }}>Review</button>
                          <button className="btn btnG btnSm" onClick={()=>setDismissedDeptChanges(p=>[...p,d.userId])}>Dismiss</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          <div style={{ display:"flex",gap:10,marginBottom:16 }}>
            <input className="inp" placeholder="Search…" value={search} onChange={e=>setSearch(e.target.value)} style={{ maxWidth:260 }}/>
            <SearchableSelect style={{ maxWidth:160 }} placeholder="All" value={deptFilter} onChange={setDeptFilter} options={depts.map(d=>({ value:d, label:d }))} />
          </div>
          <div className="card" style={{ padding:0,overflow:"hidden" }}>
            <table style={{ width:"100%",borderCollapse:"collapse" }}>
              <thead><tr style={{ background:"#FFFFFF",borderBottom:"1px solid #E2E6F0" }}>
                {["User","Manager","Dept","Licenses","OpEx/mo","CapEx","Actions"].map(h=>{
                  const sortable = h!=="Actions", active = userSort.col===h;
                  return (
                    <th key={h} onClick={sortable?()=>toggleUserSort(h):undefined}
                      style={{ padding:"11px 16px",textAlign:"left",fontSize:10,color:active?"#2563EB":"#8A93AA",fontWeight:700,letterSpacing:".05em",cursor:sortable?"pointer":"default",userSelect:"none",whiteSpace:"nowrap" }}>
                      {h.toUpperCase()}{sortable&&<span style={{ marginLeft:4,opacity:active?1:.3 }}>{active&&userSort.dir==="desc"?"▼":"▲"}</span>}
                    </th>
                  );
                })}
              </tr></thead>
              <tbody>
                {sortedUsers.map(u=>{
                  const c=userCosts(u,allTiers,seatCounts);
                  return (
                    <Fragment key={u.spId || u.id}>
                      <tr className="tr" style={{ borderBottom:"1px solid #F0F2F7" }}>
                        <td style={{ padding:"12px 16px" }}>
                          <div style={{ fontWeight:600,fontSize:13.5 }}>{u.name}</div>
                          <div style={{ fontSize:11,color:"#8A93AA",marginTop:1 }}>{u.email}</div>
                        </td>
                        <td style={{ padding:"12px 16px" }}>{u.manager || "—"}</td>
                        <td style={{ padding:"12px 16px" }}>
                          <span onClick={()=>setDeptFilter(u.dept)} title="Filter by department"
                            style={{ background:"#F0F2F7",border:"1px solid #D8DCE8",borderRadius:6,padding:"3px 9px",fontSize:11,color:"#5A637A",cursor:"pointer",transition:"all .15s" }}
                            onMouseOver={e=>{e.currentTarget.style.background="#EBF0FF";e.currentTarget.style.color="#2563EB";e.currentTarget.style.borderColor="#93AFEF";}}
                            onMouseOut={e=>{e.currentTarget.style.background="#F0F2F7";e.currentTarget.style.color="#5A637A";e.currentTarget.style.borderColor="#D8DCE8";}}>
                            {u.dept}
                          </span>
                        </td>
                        <td style={{ padding:"12px 16px",maxWidth:340 }}>
                          {u.tiers.map(tid=>{ const t=tierByIdDyn(tid); if(!t) return null; return <span key={tid} className={`chip${isDefaultTier(u.dept,tid)?" def":""}`}>{t.appIcon} {t.appName} {t.label}</span>; })}
                        </td>
                        <td style={{ padding:"12px 16px" }}>
                          <Mono color="#1D4ED8">{eur(c.opex)}/mo</Mono>
                          <div style={{ fontSize:10,color:"#8A93AA",marginTop:1 }}>{eur(c.opex*12)}/yr</div>
                        </td>
                        <td style={{ padding:"12px 16px" }}><Mono color={c.capex>0?"#10B981":"#C0C8D8"}>{c.capex>0?eur(c.capex):"—"}</Mono></td>
                        <td style={{ padding:"12px 16px" }}>
                          <div style={{ display:"flex",gap:7 }}>
                            {can(currentUser,"assignLicenses")&&<button className="btn btnP btnSm" onClick={()=>setEditUser(editUser===u.id?null:u.id)}>{editUser===u.id?"Done":"Manage"}</button>}
                            {can(currentUser,"submitRequest")&&<button className="btn btnG btnSm" onClick={()=>openReqForm(u.id)}>+ Request</button>}
                            {!can(currentUser,"assignLicenses")&&!can(currentUser,"submitRequest")&&<span style={{ fontSize:11,color:"#C0C8D8" }}>View only</span>}
                          </div>
                        </td>
                      </tr>
                      {editUser===u.id&&(
                        <tr><td colSpan={7} style={{ padding:"0 16px 16px",background:"#F4F6FA" }}>
                          <div style={{ background:"#FFFFFF",border:"1px solid #E2E6F0",borderRadius:12,padding:"18px" }}>
                            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16 }}>
                              <span style={{ fontWeight:700,fontSize:14 }}>Licenses — {u.name}</span>
                              <div style={{ display:"flex",gap:20,fontFamily:"'DM Mono',monospace",fontSize:12 }}>
                                <span>OpEx <span style={{ color:"#1D4ED8",fontWeight:700 }}>{eur(c.opex)}/mo</span></span>
                                <span>CapEx <span style={{ color:"#10B981",fontWeight:700 }}>{eur(c.capex)}</span></span>
                              </div>
                            </div>
                            {/* GitHub identity — GitHub isn't SSO'd, so a member's account is matched
                                to this person by the username or email they use on GitHub (not their
                                corporate email). Used by the GitHub sync; editable by IT. */}
                            {can(currentUser,"editUsers")&&(
                              <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:16,paddingBottom:14,borderBottom:"1px solid #F0F2F7" }}>
                                <span style={{ fontSize:10,fontWeight:700,color:"#8A93AA",letterSpacing:".08em" }}>GITHUB</span>
                                <input className="inp" style={{ maxWidth:280 }} defaultValue={u.github||""} placeholder="GitHub username or email"
                                  onBlur={e=>{ const v=e.target.value.trim(); if(v!==(u.github||"")){ const updated={...u,github:v}; setUsers(p=>p.map(x=>x.id===u.id?updated:x)); saveUser(updated).catch(console.error); toast("GitHub identity saved"); } }}/>
                                <span style={{ fontSize:11,color:"#8A93AA" }}>used to match this person to their GitHub seat on sync</span>
                              </div>
                            )}
                            {[...new Set(allTiers.map(t=>t.category))].map(cat=>(
                              <div key={cat} style={{ marginBottom:16 }}>
                                <p style={{ fontSize:10,fontWeight:700,color:"#8A93AA",letterSpacing:".08em",marginBottom:8 }}>{cat.toUpperCase()}</p>
                                <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:8 }}>
                                  {allTiers.filter(t=>t.category===cat&&!t.visibilityOnly).map(t=>{
                                    const has=u.tiers.includes(t.tierId), def=isDefaultTier(u.dept,t.tierId);
                                    const isNonStd=t.type==="Non-Standard", hasReq=requests.some(r=>r.userId===u.id&&r.tierId===t.tierId&&r.status==="Pending");
                                    return (
                                      <div key={t.tierId} style={{ display:"flex",alignItems:"center",justifyContent:"space-between",background:has?"#EBF0FF":def?"#F0F5FF":"#F8F9FC",border:`1px solid ${has?"#93AFEF":def?"#B8CCEE":"#E2E6F0"}`,borderRadius:9,padding:"10px 14px" }}>
                                        <div>
                                          <div style={{ fontSize:12.5,fontWeight:600 }}>{t.appName} <span style={{ color:"#5A637A",fontWeight:400 }}>{t.label}</span></div>
                                          <div style={{ display:"flex",gap:5,alignItems:"center",marginTop:4 }}>
                                            <BillingBadge b={t.billing}/>
                                            <Mono color="#5A637A">{eur(t.costPerUnit)}{ul(t)}</Mono>
                                            {def&&<Badge label="Default" bg="#EBF0FF" color="#2563EB" small/>}
                                            {isNonStd&&!has&&<Badge label="Needs Request" bg="#FFFBEB" color="#D97706" small/>}
                                          </div>
                                        </div>
                                        {def||has?<button className={`tog ${has?"on":"off"}`} onClick={()=>toggleTier(u.id,t.tierId)}/>:hasReq?<Badge label="Pending" bg="#FFFBEB" color="#D97706" small/>:<button className="btn btnG btnSm" onClick={()=>openReqForm(u.id)} style={{ fontSize:11 }}>Request</button>}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        </td></tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>}

        {/* CATALOG */}
        {tab==="catalog"&&<div>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4 }}>
            <h1 style={{ fontSize:20,fontWeight:700 }}>{can(currentUser,"viewCatalog")?"App Catalog":"Managed Apps"}</h1>
            {can(currentUser,"editCatalog")&&<div style={{ display:"flex",gap:8 }}>
              {SYNC_PROVIDERS.map(p=>(
                <button key={p.key} className="btn btnG" onClick={()=>previewProviderSync(p)} disabled={!!syncingKey||atlasApplying} style={{ display:"flex",alignItems:"center",gap:7 }}>
                  {syncingKey===p.key&&<span style={{ animation:"spin 1s linear infinite",display:"inline-block" }}>↻</span>}
                  {syncingKey===p.key?`Reading ${p.label}…`:`Sync from ${p.label}`}
                </button>
              ))}
              <button className="btn btnP" onClick={()=>{setShowAddApp(!showAddApp);setAddTierAppId(null);}}>+ Add App</button>
            </div>}
          </div>
          <p style={{ color:"#8A93AA",fontSize:13,marginBottom:20 }}>Manage apps, tiers, billing models and pricing.</p>
          {showAddApp&&(
            <div className="card" style={{ marginBottom:18,border:"2px solid #DBEAFE" }}>
              <h3 style={{ fontWeight:700,fontSize:14,marginBottom:16 }}>New Application</h3>
              <div style={{ display:"grid",gridTemplateColumns:"2fr 1fr 1fr",gap:12 }}>
                <div><label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:5,fontWeight:700 }}>APP NAME *</label><input className="inp" placeholder="e.g. Figma" value={newApp.name} onChange={e=>setNewApp(p=>({...p,name:e.target.value}))}/></div>
                <div><label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:5,fontWeight:700 }}>CATEGORY</label><input className="inp" placeholder="e.g. Design" value={newApp.category} onChange={e=>setNewApp(p=>({...p,category:e.target.value}))} list="cats"/><datalist id="cats">{[...new Set(catalog.map(a=>a.category))].map(c=><option key={c} value={c}/>)}</datalist></div>
                <div><label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:5,fontWeight:700 }}>ICON</label><input className="inp" placeholder="📦" value={newApp.icon} onChange={e=>setNewApp(p=>({...p,icon:e.target.value}))} style={{ maxWidth:80 }}/></div>
              </div>
              <div style={{ marginTop:12 }}>
                <label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:5,fontWeight:700 }}>PARENT GROUP <span style={{ color:"#C0C8D8",fontWeight:400 }}>(optional)</span></label>
                <input className="inp" placeholder="e.g. Microsoft, Atlassian — leave blank for none" value={newApp.group||""} onChange={e=>setNewApp(p=>({...p,group:e.target.value}))} list="appgroups-new" style={{ maxWidth:360 }}/>
                <datalist id="appgroups-new">{groupOptions.map(g=><option key={g} value={g}/>)}</datalist>
              </div>
              <div style={{ marginTop:12 }}>
                <label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:5,fontWeight:700 }}>DESCRIPTION</label>
                <textarea className="inp" placeholder="What this app is for, who owns it, notes…" value={newApp.description} onChange={e=>setNewApp(p=>({...p,description:e.target.value}))} rows={3} style={{ resize:"vertical" }}/>
              </div>
              <div style={{ display:"flex",gap:10,marginTop:14 }}>
                <button className="btn btnP" onClick={submitNewApp}>Create App</button>
                <button className="btn btnG" onClick={()=>{setShowAddApp(false);setNewApp(BLANK_APP);}}>Cancel</button>
              </div>
            </div>
          )}
          <div style={{ display:"flex",gap:10,marginBottom:16 }}>
            <input className="inp" placeholder="Search apps…" value={licSearch} onChange={e=>setLicSearch(e.target.value)} style={{ maxWidth:280 }}/>
            <SearchableSelect style={{ maxWidth:200 }} placeholder="All categories" value={licCategory} onChange={setLicCategory}
              options={["All",...Array.from(new Set(catalog.map(a=>a.category).filter(Boolean))).sort()].map(c=>({ value:c, label:c==="All"?"All categories":c }))} />
          </div>
          <div style={{ display:"flex",flexDirection:"column",gap:12 }}>
            {catalogRows.map(row=>{
              if (row.type==="header") return (
                <div key={`grp:${row.name}`} onClick={()=>setCollapsedGroups(p=>({...p,[row.name]:!p[row.name]}))}
                  style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"11px 16px",cursor:"pointer",background:"#EEF2FB",border:"1px solid #DCE3F2",borderRadius:10 }}>
                  <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                    <span style={{ fontSize:12,color:"#5A637A" }}>{collapsedGroups[row.name]?"▶":"▼"}</span>
                    <span style={{ fontWeight:800,fontSize:12.5,letterSpacing:".04em",textTransform:"uppercase",color:"#2A3658" }}>{row.name}</span>
                  </div>
                  <div style={{ display:"flex",alignItems:"center",gap:14,fontSize:11,color:"#5A637A" }}>
                    <span>{row.appCount} app{row.appCount!==1?"s":""}</span>
                    <span>{row.tierCount} tier{row.tierCount!==1?"s":""}</span>
                    <Mono color="#1D4ED8">{eur(row.monthly)}/mo</Mono>
                  </div>
                </div>
              );
              const app = row.app;
              return (
              <div key={app.id} className="card" style={{ padding:0,overflow:"hidden" }}>
                <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 20px",cursor:"pointer",background:expandApp===app.id?"#F0F5FF":"" }} onClick={()=>setExpandApp(expandApp===app.id?null:app.id)}>
                  <div style={{ display:"flex",alignItems:"center",gap:12 }}>
                    <span style={{ fontSize:20 }}>{app.icon}</span>
                    <div>
                      <span style={{ fontWeight:700,fontSize:14 }}>{app.name}</span>
                      <span style={{ marginLeft:10,fontSize:11,color:"#8A93AA" }}>{app.category}</span>
                      {app.isDefault&&<Badge label="Dept Default" bg="#EBF0FF" color="#2563EB" small/>}
                    </div>
                  </div>
                  <div style={{ display:"flex",alignItems:"center",gap:10 }} onClick={e=>e.stopPropagation()}>
                    {app.tiers.length>0&&(()=>{ const total=app.tiers.reduce((s,t)=>s+monthlyEq(t),0); return (
                      <span title="Sum of all tiers' monthly-equivalent unit cost" style={{ textAlign:"right" }}>
                        <Mono color="#1D4ED8">{eur(total)}/mo</Mono>
                        <span style={{ fontSize:10,color:"#8A93AA",marginLeft:6 }}>all tiers</span>
                      </span>
                    ); })()}
                    <span style={{ fontSize:11,color:"#8A93AA" }}>{app.tiers.length} tier{app.tiers.length!==1?"s":""}</span>
                    {app.description&&<button className="btn btnG btnSm" title="Description" onClick={()=>setDescExpand(descExpand===app.id?null:app.id)} style={{ color:descExpand===app.id?"#2563EB":"#5A637A" }}>ⓘ</button>}
                    {can(currentUser,"assignLicenses")&&app.tiers.length>0&&<button className="btn btnP btnSm" onClick={()=>openBulkModal(app.id)}>⊞ Bulk Assign</button>}
                    {canEditApp(app)&&<>
                      <button className="btn btnG btnSm" onClick={()=>{setAddTierAppId(addTierAppId===app.id?null:app.id);setNewTier(BLANK_TIER);setExpandApp(app.id);}}>+ Tier</button>
                      <button className="btn btnG btnSm" onClick={e=>{ e.stopPropagation(); setEditAppId(editAppId===app.id?null:app.id); setEditAppData({name:app.name,category:app.category,icon:app.icon,description:app.description||"",group:app.group||"",approverId:app.approverId||"",approverName:app.approverName||""}); setExpandApp(app.id); }}>{editAppId===app.id?"Cancel":"Edit"}</button>
                    </>}
                    {can(currentUser,"editCatalog")&&<button className="btn btnSm" style={{ background:"#FEF2F2",color:"#EF4444" }} onClick={()=>setDeleteConfirm({type:"app",appId:app.id})}>Delete</button>}
                    <span style={{ color:"#8A93AA",fontSize:13 }}>{expandApp===app.id?"▲":"▼"}</span>
                  </div>
                </div>
                {descExpand===app.id&&app.description&&(
                  <div style={{ padding:"12px 20px",background:"#F8F9FC",borderTop:"1px solid #E8EBF3",fontSize:12.5,color:"#5A637A",whiteSpace:"pre-wrap",lineHeight:1.5 }}>{app.description}</div>
                )}
                {expandApp===app.id&&(
                  <div style={{ borderTop:"1px solid #E8EBF3" }}>
                    <table style={{ width:"100%",borderCollapse:"collapse" }}>
                      <thead><tr style={{ background:"#F8F9FC" }}>
                        {["Tier","Type","Billing","Seat Model","Unit Cost","Seats",...((can(currentUser,"viewSupplierDetails")||canEditApp(app))?["Supplier","Contract Ref","Renewal Owner","URL"]:[]),"Renewal Date",""].map(h=>(
                          <th key={h} style={{ padding:"8px 16px",textAlign:"left",fontSize:10,color:"#8A93AA",fontWeight:700 }}>{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {app.tiers.length===0&&<tr><td colSpan={12} style={{ padding:"16px",textAlign:"center",fontSize:12,color:"#8A93AA" }}>No tiers yet — add one below.</td></tr>}
                        {app.tiers.map(t=>{
                          const seats=users.filter(u=>u.tiers.includes(t.tierId)).length;
                          const isEditing=editTier?.appId===app.id&&editTier?.tierId===t.tierId;
                          return (
                            <Fragment key={t.tierId}>
                              <tr className="tr" style={{ borderBottom:isEditing?"none":"1px solid #F0F2F7" }}>
                                <td style={{ padding:"10px 16px",fontWeight:600,fontSize:13 }}>{t.label}{t.visibilityOnly&&<Badge label="Visibility only" bg="#F3F4F6" color="#6B7280" small/>}</td>
                                <td style={{ padding:"10px 16px" }}><Badge label={t.type} bg={t.type==="Standard"?"#EBF0FF":"#FFFBEB"} color={t.type==="Standard"?"#1D4ED8":"#D97706"} small/></td>
                                <td style={{ padding:"10px 16px" }}><BillingBadge b={t.billing}/></td>
                                <td style={{ padding:"10px 16px",fontSize:11.5,color:"#5A637A" }}>{t.seatModel==="flat"?"Flat":"Per seat"}</td>
                                <td style={{ padding:"10px 16px" }}>
                                  <Mono color="#1D4ED8">{eur(t.costPerUnit)}{ul(t)}</Mono>
                                  {t.seatModel==="flat"&&seats>0&&<div style={{ fontSize:10,color:"#8A93AA",marginTop:1 }}>{eur(monthlyEq(t)/seats)}/user/mo · ÷{seats}</div>}
                                </td>
                                <td style={{ padding:"10px 16px" }}>
                                  {(()=>{ const purchased=Number(t.seats)||0;
                                    // Visibility-only tier: not assigned to anyone, so "used" is meaningless —
                                    // show the purchased seats for the term as a plain count.
                                    if(t.visibilityOnly) return <span style={{ fontSize:11.5,color:"#5A637A" }}>{purchased?`${purchased} purchased`:<span style={{color:"#C0C8D8"}}>—</span>}</span>;
                                    if(!purchased) return <span style={{ fontSize:11.5,color:"#5A637A" }}>{seats} used</span>;
                                    const free=purchased-seats, col=free<0?"#EF4444":free===0?"#D97706":"#10B981";
                                    return (<div style={{ fontSize:11.5 }}><Mono color={col}>{seats}/{purchased}</Mono><div style={{ fontSize:10,color:col,marginTop:1 }}>{free<0?`${-free} over`:`${free} free`}</div></div>);
                                  })()}
                                </td>
                                {(can(currentUser,"viewSupplierDetails")||canEditApp(app))&&<>
                                  <td style={{ padding:"10px 16px",fontSize:12,color:"#5A637A" }}>{t.supplier||<span style={{color:"#C0C8D8"}}>—</span>}</td>
                                  <td style={{ padding:"10px 16px",fontSize:12,color:"#5A637A" }}>{t.contractRef||<span style={{color:"#C0C8D8"}}>—</span>}</td>
                                  <td style={{ padding:"10px 16px",fontSize:12,color:"#5A637A" }}>{t.renewalOwnerName||users.find(u=>u.id===t.renewalOwner)?.name||t.renewalOwner||<span style={{color:"#C0C8D8"}}>—</span>}</td>
                                  <td style={{ padding:"10px 16px",fontSize:12 }}>{t.purchaseUrl?<a href={t.purchaseUrl} target="_blank" rel="noreferrer" style={{ color:"#2563EB",fontSize:12,textDecoration:"none",fontWeight:500 }}>🔗 Open</a>:<span style={{color:"#C0C8D8"}}>—</span>}</td>
                                </>}
                                <td style={{ padding:"10px 16px",fontSize:11.5 }}>{t.renewalDate?<span style={{ color:daysUntil(t.renewalDate)<=30?"#EF4444":daysUntil(t.renewalDate)<=90?"#F59E0B":"#5A637A" }}>{fmtDate(t.renewalDate)}<br/><span style={{ fontSize:10 }}>{daysUntil(t.renewalDate)}d</span></span>:<span style={{color:"#C0C8D8"}}>—</span>}</td>
                                <td style={{ padding:"10px 16px" }}>
                                  <div style={{ display:"flex",gap:6 }}>
                                    {can(currentUser,"assignLicenses")&&!t.visibilityOnly&&<button className="btn btnP btnSm" onClick={()=>openInlineAssign(t.tierId)}>{assignTierId===t.tierId?"Close":"Assign"}</button>}
                                    {canEditApp(app)&&<>
                                      <button className="btn btnG btnSm" onClick={()=>{ setEditTier(isEditing?null:{appId:app.id,tierId:t.tierId}); setEditTierData({costPerUnit:t.costPerUnit,billing:t.billing,seatModel:t.seatModel,seats:t.seats??"",renewalDate:t.renewalDate||"",supplier:t.supplier||"",supplierContact:t.supplierContact||"",purchaseDate:t.purchaseDate||"",contractRef:t.contractRef||"",renewalOwner:t.renewalOwner||"",renewalOwnerName:t.renewalOwnerName||"",msSkuId:t.msSkuId||"",msSkuName:t.msSkuName||"",purchaseUrl:t.purchaseUrl||"",visibilityOnly:t.visibilityOnly||false}); }}>{isEditing?"Cancel":"Edit"}</button>
                                      <button className="btn btnSm" style={{ background:"#FEF2F2",color:"#EF4444" }} onClick={()=>setDeleteConfirm({type:"tier",appId:app.id,tierId:t.tierId})} disabled={seats>0} title={seats>0?"Remove all users first":""}>{seats>0?`${seats} assigned`:"Remove"}</button>
                                    </>}
                                  </div>
                                </td>
                              </tr>
                              {assignTierId===t.tierId&&(
                                <tr><td colSpan={12} style={{ padding:"0 16px 14px",background:"#F8F9FC" }}>
                                  <div style={{ background:"#FFFFFF",border:"1px solid #E2E6F0",borderRadius:10,padding:"16px" }}>
                                    <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12 }}>
                                      <p style={{ fontSize:11,fontWeight:700,color:"#8A93AA",letterSpacing:".06em" }}>ASSIGN {app.name.toUpperCase()} — {t.label.toUpperCase()} · {Object.values(assignSelected).filter(Boolean).length} selected</p>
                                      <div style={{ display:"flex",gap:8,alignItems:"center" }}>
                                        <input className="inp" placeholder="Search users…" value={assignSearch} onChange={e=>setAssignSearch(e.target.value)} style={{ width:200,padding:"6px 10px",fontSize:12 }}/>
                                        <button className="btn btnG btnSm" onClick={()=>{ const a={}; assignableUsers.forEach(u=>{a[u.id]=true;}); setAssignSelected(a); }}>All</button>
                                        <button className="btn btnG btnSm" onClick={()=>{ const a={}; assignableUsers.forEach(u=>{a[u.id]=false;}); setAssignSelected(a); }}>None</button>
                                      </div>
                                    </div>
                                    <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:8,marginBottom:14,maxHeight:280,overflowY:"auto" }}>
                                      {assignableUsers.filter(u=>u.name.toLowerCase().includes(assignSearch.toLowerCase())||u.dept.toLowerCase().includes(assignSearch.toLowerCase())).map(u=>{
                                        const checked=!!assignSelected[u.id];
                                        return (
                                          <label key={u.id} style={{ display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderRadius:8,border:`1.5px solid ${checked?"#93AFEF":"#E2E6F0"}`,background:checked?"#EFF6FF":"#F8F9FC",cursor:"pointer",transition:"all .1s" }}>
                                            <input type="checkbox" checked={checked} onChange={e=>setAssignSelected(p=>({...p,[u.id]:e.target.checked}))} style={{ width:15,height:15,accentColor:"#2563EB",cursor:"pointer" }}/>
                                            <div style={{ flex:1,minWidth:0 }}>
                                              <div style={{ fontSize:13,fontWeight:600,color:"#1A1F36",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{u.name}</div>
                                              <div style={{ fontSize:11,color:"#8A93AA" }}>{u.dept}</div>
                                            </div>
                                            {checked&&<span style={{ fontSize:10,fontWeight:700,color:"#2563EB",background:"#DBEAFE",borderRadius:10,padding:"1px 7px" }}>✓</span>}
                                          </label>
                                        );
                                      })}
                                    </div>
                                    <div style={{ display:"flex",gap:10,alignItems:"center",borderTop:"1px solid #E8EBF3",paddingTop:12 }}>
                                      <button className="btn btnP" onClick={()=>saveInlineAssign(t.tierId)}>Save Assignments</button>
                                      <button className="btn btnG" onClick={()=>setAssignTierId(null)}>Cancel</button>
                                      <span style={{ fontSize:12,color:"#8A93AA",marginLeft:4 }}>{Object.values(assignSelected).filter(Boolean).length} of {assignableUsers.length} users selected</span>
                                    </div>
                                  </div>
                                </td></tr>
                              )}
                              {isEditing&&(
                                <tr><td colSpan={12} style={{ padding:"0 16px 16px",background:"#F8F9FC" }}>
                                  <div style={{ background:"#FFFFFF",border:"1px solid #E2E6F0",borderRadius:10,padding:"16px" }}>
                                    <p style={{ fontSize:10.5,fontWeight:700,color:"#8A93AA",letterSpacing:".06em",marginBottom:12 }}>EDIT TIER — {t.label}</p>
                                    {/* Pricing fields */}
                                    <p style={{ fontSize:10,fontWeight:700,color:"#8A93AA",letterSpacing:".06em",marginBottom:8,marginTop:4 }}>PRICING</p>
                                    <div style={{ display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:12,marginBottom:16 }}>
                                      <div>
                                        <label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>UNIT COST *</label>
                                        <input className="inp" type="number" min="0" step="0.01" value={editTierData.costPerUnit||""} onChange={e=>setEditTierData(p=>({...p,costPerUnit:parseFloat(e.target.value)||0}))}/>
                                      </div>
                                      <div>
                                        <label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>BILLING</label>
                                        <select className="inp" value={editTierData.billing||"monthly"} onChange={e=>setEditTierData(p=>({...p,billing:e.target.value}))}>
                                          <option value="monthly">Monthly</option>
                                          <option value="annual">Annual</option>
                                          <option value="one-time">One-Time</option>
                                        </select>
                                      </div>
                                      <div>
                                        <label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>SEAT MODEL</label>
                                        <select className="inp" value={editTierData.seatModel||"per-seat"} onChange={e=>setEditTierData(p=>({...p,seatModel:e.target.value}))}>
                                          <option value="per-seat">Per Seat</option>
                                          <option value="flat">Flat / Org-wide</option>
                                        </select>
                                      </div>
                                      <div>
                                        <label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>
                                          RENEWAL DATE{editTierData.billing==="monthly"&&<span style={{ color:"#C0C8D8",fontWeight:400,marginLeft:4 }}>(N/A)</span>}
                                        </label>
                                        <input className="inp" type="date" value={editTierData.renewalDate||""} onChange={e=>setEditTierData(p=>({...p,renewalDate:e.target.value}))} disabled={editTierData.billing==="monthly"} style={{ opacity:editTierData.billing==="monthly"?0.4:1 }}/>
                                      </div>
                                      <div>
                                        <label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>PURCHASED SEATS</label>
                                        <input className="inp" type="number" min="0" step="1" placeholder="e.g. 50" value={editTierData.seats??""} onChange={e=>setEditTierData(p=>({...p,seats:e.target.value}))}/>
                                      </div>
                                    </div>
                                    <label style={{ display:"flex",alignItems:"flex-start",gap:8,marginBottom:16,cursor:"pointer" }}>
                                      <input type="checkbox" checked={!!editTierData.visibilityOnly} onChange={e=>setEditTierData(p=>({...p,visibilityOnly:e.target.checked}))} style={{ marginTop:2 }}/>
                                      <span style={{ fontSize:11.5,color:"#5A637A" }}><b style={{ color:"#374060" }}>Visibility only</b> — track this tier's purchased seats, price &amp; renewal for cost reporting, but never assign users to it or include it in a sync. Use this for the second billing term of a product (e.g. the monthly seats alongside an annual tier).</span>
                                    </label>
                                    <p style={{ fontSize:10,fontWeight:700,color:"#8A93AA",letterSpacing:".06em",marginBottom:8 }}>MICROSOFT LICENSE MAPPING</p>
                                    <div style={{ marginBottom:16,maxWidth:360 }}>
                                      <label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>MICROSOFT PRODUCT <span style={{ color:"#C0C8D8",fontWeight:400 }}>(for Sync from Microsoft)</span></label>
                                      <SearchableSelect
                                        placeholder="Not a Microsoft license"
                                        value={editTierData.msSkuId||""}
                                        onChange={val=>{ setEditTierData(p=>({...p,msSkuId:val,msSkuName:MS_SKU_NAME[val]||""})); }}
                                        options={[{ value:"", label:"— Not a Microsoft license —" }, ...MS_SKUS.map(s=>({ value:s.id, label:s.name, sortKey:s.name }))]}
                                      />
                                      <p style={{ fontSize:11,color:"#8A93AA",marginTop:5 }}>Map this tier to a Microsoft SKU so "Sync from Microsoft" assigns it to whoever holds that license in the M365 portal.</p>
                                    </div>
                                    <p style={{ fontSize:10,fontWeight:700,color:"#8A93AA",letterSpacing:".06em",marginBottom:8 }}>SUPPLIER & PURCHASE DETAILS</p>
                                    <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12 }}>
                                      {[["supplier","SUPPLIER NAME","e.g. Microsoft"],["supplierContact","SUPPLIER CONTACT","e.g. account@supplier.com"],["purchaseDate","PURCHASE DATE","","date"],["contractRef","CONTRACT REF","e.g. MSFT-2026-0042"],["purchaseUrl","PORTAL URL","e.g. https://admin.microsoft.com"]].map(([f,l,ph,ty])=>(
                                        <div key={f}><label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>{l}</label><input className="inp" type={ty||"text"} placeholder={ph} value={editTierData[f]||""} onChange={e=>setEditTierData(p=>({...p,[f]:e.target.value}))}/></div>
                                      )).concat([
                                        <div key="renewalOwner">
                                          <label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>RENEWAL OWNER</label>
                                          <SearchableSelect
                                            placeholder="Select user…"
                                            value={editTierData.renewalOwner||""}
                                            onChange={val=>{ const u=users.find(x=>x.id===val); setEditTierData(p=>({...p,renewalOwner:val,renewalOwnerName:u?u.name:""})); }}
                                            options={users.map(u=>({ value:u.id, label:`${u.name} — ${u.dept}`, sortKey:u.name }))}
                                          />
                                        </div>
                                      ])}
                                    </div>
                                    <div style={{ display:"flex",gap:10,marginTop:14 }}>
                                      <button className="btn btnP" onClick={()=>saveTierDetails(app.id,t.tierId)}>Save Details</button>
                                      <button className="btn btnG" onClick={()=>setEditTier(null)}>Cancel</button>
                                    </div>
                                  </div>
                                </td></tr>
                              )}
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                    {/* Edit App panel */}
                    {editAppId===app.id&&(
                      <div style={{ padding:"16px 20px",background:"#F0F5FF",borderTop:"1px solid #DBEAFE" }}>
                        <p style={{ fontSize:11,fontWeight:700,color:"#2563EB",marginBottom:12,letterSpacing:".06em" }}>EDIT APPLICATION</p>
                        <div style={{ display:"grid",gridTemplateColumns:"2fr 1fr 1fr",gap:12,alignItems:"end" }}>
                          <div>
                            <label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>APP NAME *</label>
                            <input className="inp" value={editAppData.name||""} onChange={e=>setEditAppData(p=>({...p,name:e.target.value}))}/>
                          </div>
                          <div>
                            <label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>CATEGORY</label>
                            <input className="inp" value={editAppData.category||""} onChange={e=>setEditAppData(p=>({...p,category:e.target.value}))} list="cats"/>
                          </div>
                          <div>
                            <label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>ICON</label>
                            <input className="inp" value={editAppData.icon||""} onChange={e=>setEditAppData(p=>({...p,icon:e.target.value}))} style={{ maxWidth:80 }}/>
                          </div>
                        </div>
                        <div style={{ marginTop:12,maxWidth:360 }}>
                          <label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>PARENT GROUP <span style={{ color:"#C0C8D8",fontWeight:400 }}>(optional)</span></label>
                          <input className="inp" placeholder="e.g. Microsoft, Atlassian — leave blank for none" value={editAppData.group||""} onChange={e=>setEditAppData(p=>({...p,group:e.target.value}))} list="appgroups-edit"/>
                          <datalist id="appgroups-edit">{groupOptions.map(g=><option key={g} value={g}/>)}</datalist>
                        </div>
                        <div style={{ marginTop:12 }}>
                          <label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>DESCRIPTION</label>
                          <textarea className="inp" placeholder="What this app is for, who owns it, notes…" value={editAppData.description||""} onChange={e=>setEditAppData(p=>({...p,description:e.target.value}))} rows={3} style={{ resize:"vertical" }}/>
                        </div>
                        {can(currentUser,"editCatalog")&&<div style={{ marginTop:12,maxWidth:420 }}>
                          <label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>DESIGNATED APPROVER (optional)</label>
                          <SearchableSelect
                            placeholder="No designated approver — route by cost"
                            value={editAppData.approverId||""}
                            onChange={val=>{ const p=entraPeople.find(x=>x.id===val); setEditAppData(d=>({...d,approverId:val,approverName:p?p.displayName:""})); }}
                            options={[{ value:"", label:"— No designated approver (route by cost) —" }, ...entraPeople.map(p=>({ value:p.id, label:`${p.displayName} (${p.mail||p.userPrincipalName})`, sortKey:p.displayName }))]}
                          />
                          <p style={{ fontSize:11,color:"#8A93AA",marginTop:5 }}>If set, all requests for this app route to this person for approval, overriding the cost thresholds.</p>
                        </div>}
                        <div style={{ display:"flex",gap:10,marginTop:12 }}>
                          <button className="btn btnP" onClick={()=>saveEditApp(app.id)}>Save Changes</button>
                          <button className="btn btnG" onClick={()=>{setEditAppId(null);setEditAppData({});}}>Cancel</button>
                        </div>
                      </div>
                    )}

                    {addTierAppId===app.id&&(
                      <div style={{ padding:"16px 20px",background:"#F8F9FC",borderTop:"1px solid #E8EBF3" }}>
                        <p style={{ fontSize:11,fontWeight:700,color:"#8A93AA",marginBottom:12,letterSpacing:".06em" }}>ADD TIER TO {app.name.toUpperCase()}</p>
                        <div style={{ display:"grid",gridTemplateColumns:"1.5fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr",gap:10,alignItems:"end" }}>
                          <div><label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>TIER NAME *</label><input className="inp" placeholder="e.g. Professional" value={newTier.label} onChange={e=>setNewTier(p=>({...p,label:e.target.value}))}/></div>
                          <div><label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>TYPE</label><select className="inp" value={newTier.type} onChange={e=>setNewTier(p=>({...p,type:e.target.value}))}><option>Standard</option><option>Non-Standard</option></select></div>
                          <div><label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>BILLING</label><select className="inp" value={newTier.billing} onChange={e=>setNewTier(p=>({...p,billing:e.target.value}))}><option value="monthly">Monthly</option><option value="annual">Annual</option><option value="one-time">One-Time</option></select></div>
                          <div><label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>SEAT MODEL</label><select className="inp" value={newTier.seatModel} onChange={e=>setNewTier(p=>({...p,seatModel:e.target.value}))}><option value="per-seat">Per Seat</option><option value="flat">Flat / Org-wide</option></select></div>
                          <div><label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>COST ({newTier.billing==="monthly"?"/mo":newTier.billing==="annual"?"/yr":"one-time"}) *</label><input className="inp" type="number" min="0" step="0.01" placeholder="0.00" value={newTier.costPerUnit} onChange={e=>setNewTier(p=>({...p,costPerUnit:e.target.value}))}/></div>
                          <div><label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>SEATS</label><input className="inp" type="number" min="0" step="1" placeholder="e.g. 50" value={newTier.seats} onChange={e=>setNewTier(p=>({...p,seats:e.target.value}))}/></div>
                          <div>
                            <label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>
                              RENEWAL DATE{newTier.billing==="monthly"&&<span style={{ color:"#C0C8D8",fontWeight:400,marginLeft:4 }}>(N/A for monthly)</span>}
                            </label>
                            <input className="inp" type="date" value={newTier.renewalDate} onChange={e=>setNewTier(p=>({...p,renewalDate:e.target.value}))} disabled={newTier.billing==="monthly"} style={{ opacity:newTier.billing==="monthly"?0.4:1 }}/>
                          </div>
                          <div>
                            <label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>RENEWAL OWNER</label>
                            <SearchableSelect
                              placeholder="Select…"
                              value={newTier.renewalOwner||""}
                              onChange={val=>{ const u=users.find(x=>x.id===val); setNewTier(p=>({...p,renewalOwner:val,renewalOwnerName:u?u.name:""})); }}
                              options={users.map(u=>({ value:u.id, label:`${u.name} — ${u.dept}`, sortKey:u.name }))}
                            />
                          </div>
                        </div>
                        <label style={{ display:"flex",alignItems:"flex-start",gap:8,marginTop:12,cursor:"pointer" }}>
                          <input type="checkbox" checked={!!newTier.visibilityOnly} onChange={e=>setNewTier(p=>({...p,visibilityOnly:e.target.checked}))} style={{ marginTop:2 }}/>
                          <span style={{ fontSize:11.5,color:"#5A637A" }}><b style={{ color:"#374060" }}>Visibility only</b> — track purchased seats, price &amp; renewal for cost reporting, but never assign users or sync it (use for a second billing term, e.g. monthly alongside annual).</span>
                        </label>
                        <div style={{ display:"flex",gap:10,marginTop:12 }}>
                          <button className="btn btnP" onClick={()=>submitNewTier(app.id)}>Add Tier</button>
                          <button className="btn btnG" onClick={()=>{setAddTierAppId(null);setNewTier(BLANK_TIER);}}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              );
            })}
            {catalogRows.length===0&&(
              <div style={{ textAlign:"center",padding:40,color:"#8A93AA",fontSize:13 }}>No apps found. <button className="btn btnP btnSm" style={{ marginLeft:10 }} onClick={()=>setShowAddApp(true)}>Add one</button></div>
            )}
          </div>
        </div>}

        {/* REQUESTS */}
        {tab==="requests"&&<div>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20 }}>
            <div><h1 style={{ fontSize:20,fontWeight:700,marginBottom:3 }}>Request Log</h1><p style={{ color:"#8A93AA",fontSize:13 }}>Non-standard & add-on software requests with tiered approval routing</p></div>
            {can(currentUser,"submitRequest")&&<button className="btn btnP" onClick={()=>{setNewReq({userId:"",appId:"",tierId:"",justification:""});setShowReqForm(!showReqForm);}}>+ New Request</button>}
          </div>
          <div style={{ display:"flex",gap:10,marginBottom:18 }}>
            {[["Auto-Approve","< €15/mo — instant","#10B981"],["Manager","€15–€50/mo — 1 approver","#F59E0B"],["IT + Finance","> €50/mo — 2 approvers","#EF4444"]].map(([l,sub,c])=>(
              <div key={l} style={{ background:"#FFFFFF",border:"1px solid #E2E6F0",borderRadius:9,padding:"9px 14px",display:"flex",alignItems:"center",gap:8 }}>
                <div style={{ width:8,height:8,borderRadius:"50%",background:c,flexShrink:0 }}/>
                <div><div style={{ fontSize:12,fontWeight:600,color:c }}>{l}</div><div style={{ fontSize:10.5,color:"#8A93AA" }}>{sub}</div></div>
              </div>
            ))}
          </div>
          {showReqForm&&(
            <div className="card" style={{ marginBottom:20 }}>
              <h3 style={{ fontWeight:700,fontSize:15,marginBottom:16 }}>Submit Software Request</h3>
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:13 }}>
                <div><label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:6,fontWeight:700 }}>USER</label>
                  <SearchableSelect
                    placeholder="Select user…"
                    value={newReq.userId}
                    onChange={val=>setNewReq(p=>({...p,userId:val,appId:"",tierId:""}))}
                    options={requestableUsers.map(u=>({ value:u.id, label:`${u.name} — ${u.dept}`, sortKey:u.name }))}
                  /></div>
                <div><label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:6,fontWeight:700 }}>APPLICATION</label>
                  <SearchableSelect
                    placeholder="Select app…"
                    value={newReq.appId}
                    onChange={val=>setNewReq(p=>({...p,appId:val,tierId:""}))}
                    options={[{ value:NEW_APP_ID, label:"➕ New software (not in the list)…", sortKey:"" }, ...catalog.map(a=>({ value:a.id, label:`${a.icon} ${a.name}`, sortKey:a.name }))]}
                  /></div>
                {newReq.appId===NEW_APP_ID ? (
                  <>
                    <div><label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:6,fontWeight:700 }}>APPLICATION NAME *</label>
                      <input className="inp" placeholder="e.g. Figma" value={newReq.newAppName||""} onChange={e=>setNewReq(p=>({...p,newAppName:e.target.value}))}/></div>
                    <div><label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:6,fontWeight:700 }}>TIER / PLAN <span style={{ color:"#C0C8D8",fontWeight:400 }}>(if known)</span></label>
                      <input className="inp" placeholder="e.g. Professional" value={newReq.newAppTier||""} onChange={e=>setNewReq(p=>({...p,newAppTier:e.target.value}))}/></div>
                    <div><label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:6,fontWeight:700 }}>OPTIONS / ADD-ONS <span style={{ color:"#C0C8D8",fontWeight:400 }}>(optional)</span></label>
                      <input className="inp" placeholder="e.g. Dev Mode seat, 2 editors" value={newReq.newAppOptions||""} onChange={e=>setNewReq(p=>({...p,newAppOptions:e.target.value}))}/></div>
                    <div><label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:6,fontWeight:700 }}>LINK TO PRODUCT <span style={{ color:"#C0C8D8",fontWeight:400 }}>(optional)</span></label>
                      <input className="inp" type="url" placeholder="https://…" value={newReq.newAppLink||""} onChange={e=>setNewReq(p=>({...p,newAppLink:e.target.value}))}/></div>
                  </>
                ) : (
                  <div><label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:6,fontWeight:700 }}>TIER</label>
                    <SearchableSelect
                      placeholder="Select tier…"
                      disabled={!newReq.appId}
                      value={newReq.tierId}
                      onChange={val=>setNewReq(p=>({...p,tierId:val}))}
                      options={((newReq.appId&&appByIdDyn(newReq.appId)?.tiers)||[]).filter(t=>!t.visibilityOnly).map(t=>({ value:t.tierId, label:`${t.label} — ${eur(t.costPerUnit)}${ul(t)}` }))}
                    /></div>
                )}
                <div style={{ gridColumn:"1/-1" }}><label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:6,fontWeight:700 }}>BUSINESS JUSTIFICATION{newReq.appId===NEW_APP_ID&&" *"}</label><input className="inp" placeholder="Why is this software needed?" value={newReq.justification} onChange={e=>setNewReq(p=>({...p,justification:e.target.value}))}/></div>
              </div>
              {newReq.appId===NEW_APP_ID&&(
                <div style={{ marginTop:14,padding:"10px 14px",borderRadius:9,background:`${APPR_COLOR["New Software"]}11`,border:`1px solid ${APPR_COLOR["New Software"]}44`,display:"flex",alignItems:"center",gap:10 }}>
                  <div style={{ width:8,height:8,borderRadius:"50%",background:APPR_COLOR["New Software"] }}/>
                  <span style={{ fontSize:12.5,fontWeight:600,color:APPR_COLOR["New Software"] }}>New Software</span>
                  <span style={{ fontSize:12,color:"#5A637A" }}>— stays pending until IT adds the app to the catalog and attaches it to this request</span>
                </div>
              )}
              {newReq.tierId&&(()=>{
                const t=tierByIdDyn(newReq.tierId), app=t&&appByIdDyn(t.appId), ap=routeRequest(app,t?monthlyEq(t):0);
                const desc = ap.label==="Designated Approver" ? `Approved by ${app?.approverName||"the app owner"}`
                  : ap.label==="Auto-Approve" ? `Automatic approval (under €${config.autoMax}/mo)`
                  : ap.label==="Manager" ? `Manager approval (€${config.autoMax}–€${config.mgrMax}/mo)`
                  : `IT & Finance approval (over €${config.mgrMax}/mo)`;
                return <div style={{ marginTop:14,padding:"10px 14px",borderRadius:9,background:`${APPR_COLOR[ap.label]}11`,border:`1px solid ${APPR_COLOR[ap.label]}44`,display:"flex",alignItems:"center",gap:10 }}>
                  <div style={{ width:8,height:8,borderRadius:"50%",background:APPR_COLOR[ap.label] }}/>
                  <span style={{ fontSize:12.5,fontWeight:600,color:APPR_COLOR[ap.label] }}>{ap.label}</span>
                  <span style={{ fontSize:12,color:"#5A637A" }}>— {desc}</span>
                </div>;
              })()}
              <div style={{ display:"flex",gap:10,marginTop:16 }}>
                <button className="btn btnP" onClick={submitRequest}>Submit Request</button>
                <button className="btn btnG" onClick={()=>setShowReqForm(false)}>Cancel</button>
              </div>
            </div>
          )}
          <div className="card" style={{ padding:0 }}>
            <table style={{ width:"100%",borderCollapse:"collapse" }}>
              <thead><tr style={{ background:"#FFFFFF",borderBottom:"1px solid #E2E6F0" }}>
                {["User","App & Tier","Billing","Cost","Approval Level","Justification","Date","Status",""].map(h=><th key={h} style={{ padding:"11px 14px",textAlign:"left",fontSize:10,color:"#8A93AA",fontWeight:700 }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {visibleRequests.map(r=>{
                  const u=users.find(u=>u.id===r.userId), t=tierByIdDyn(r.tierId), ap=r.approvalLevel, eq=t?monthlyEq(t):0;
                  return (
                    <tr key={r.id} className="tr" style={{ borderBottom:"1px solid #F0F2F7" }}>
                      <td style={{ padding:"11px 14px" }}><div style={{ fontWeight:600,fontSize:13 }}>{u?.name}</div><div style={{ fontSize:10.5,color:"#8A93AA" }}>{u?.dept}</div></td>
                      <td style={{ padding:"11px 14px" }}>
                        {t ? <>
                          <div style={{ fontSize:13,fontWeight:600 }}>{t.appIcon} {t.appName}</div><div style={{ fontSize:11,color:"#5A637A" }}>{t.label}</div>
                        </> : r.newApp ? <>
                          <div style={{ fontSize:13,fontWeight:600 }}>🆕 {r.newApp.name}</div>
                          <div style={{ fontSize:11,color:"#5A637A" }}>{[r.newApp.tier,r.newApp.options].filter(Boolean).join(" · ")||"—"}</div>
                          {r.newApp.link&&<a href={r.newApp.link} target="_blank" rel="noreferrer" style={{ fontSize:11,color:"#2563EB",textDecoration:"none" }}>Product link ↗</a>}
                        </> : null}
                      </td>
                      <td style={{ padding:"11px 14px" }}>{t&&<BillingBadge b={t.billing}/>}</td>
                      <td style={{ padding:"11px 14px" }}>
                        <Mono color="#1D4ED8">{t?`${eur(t.costPerUnit)}${ul(t)}`:isUnattachedNewApp(r)?"—":""}</Mono>
                        {t&&t.billing!=="one-time"&&<div style={{ fontSize:10,color:"#8A93AA" }}>≈{eur(eq)}/mo</div>}
                      </td>
                      <td style={{ padding:"11px 14px" }}><Badge label={ap} bg={`${APPR_COLOR[ap]||"#888"}22`} color={APPR_COLOR[ap]||"#888"} small/></td>
                      <td style={{ padding:"11px 14px",fontSize:11.5,color:"#5A637A",maxWidth:200 }}><div style={{ overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{r.justification||"—"}</div></td>
                      <td style={{ padding:"11px 14px",fontSize:11,color:"#8A93AA" }}>{r.date}</td>
                      <td style={{ padding:"11px 14px" }}><Badge label={r.status} bg={`${STATUS_COLOR[r.status]}22`} color={STATUS_COLOR[r.status]||"#888"} small/>{r.note&&<div style={{ fontSize:10,color:"#8A93AA",marginTop:2 }}>{r.note}</div>}</td>
                      <td style={{ padding:"11px 14px" }}>
                        {r.status==="Pending"&&(isUnattachedNewApp(r) ? (
                          // Not in the catalog yet — no tier to assign, so approval is
                          // blocked until IT adds the app and attaches it.
                          <div style={{ display:"flex",gap:6,alignItems:"center" }}>
                            {can(currentUser,"editCatalog")&&<button className="btn btnP btnSm" onClick={()=>openAttachModal(r)}>+ Add to catalog</button>}
                            {canApproveReq(r)&&<button className="btn btnSm" style={{ background:"#FEF2F2",color:"#EF4444" }} onClick={()=>denyRequest(r.id)}>✕</button>}
                            {!can(currentUser,"editCatalog")&&<span style={{ fontSize:10.5,color:"#8A93AA",whiteSpace:"nowrap" }}>Awaiting IT catalog review</span>}
                          </div>
                        ) : canApproveReq(r)&&(
                          <div style={{ display:"flex",gap:6 }}>
                            <button className="btn btnSm" style={{ background:"#DCFCE7",color:"#16A34A" }} onClick={()=>setApproveModal({reqId:r.id,note:""})}>✓</button>
                            <button className="btn btnSm" style={{ background:"#FEF2F2",color:"#EF4444" }} onClick={()=>denyRequest(r.id)}>✕</button>
                          </div>
                        ))}
                      </td>
                    </tr>
                  );
                })}
                {visibleRequests.length===0&&<tr><td colSpan={9} style={{ padding:40,textAlign:"center",color:"#8A93AA" }}>No requests yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>}

        {/* DEPT DEFAULTS */}
        {tab==="depts"&&<DeptDefaultsTab
          allDepts={allDepts} deptDefaults={deptDefaults} users={visibleUsers}
          allTiers={allTiers} seatCounts={seatCounts} tierByIdDyn={tierByIdDyn}
          saveDeptDefault={saveDeptDefault} removeDeptDefault={removeDeptDefault}
          canEdit={can(currentUser,"editDeptDefaults")}
          onDeptClick={dept=>{ setDeptFilter(dept); setTab("users"); }}
        />}

        {tab==="orphans"&&<div>
          <h1 style={{ fontSize:20,fontWeight:700,marginBottom:4 }}>Orphaned Accounts</h1>
          <p style={{ color:"#8A93AA",fontSize:13,marginBottom:20 }}>
            Vendor seats found during a sync that match no LicenseOps user, plus Entra users with no department set — likely ex-employees, externals or service accounts.
            {isITAdmin?" Showing all orphans across vendors.":" Showing orphans for the apps you manage."}
          </p>
          {orphanProducts.length>0&&(
            <div style={{ display:"flex",gap:10,marginBottom:16,alignItems:"center" }}>
              <SearchableSelect style={{ maxWidth:240 }} placeholder="All products" value={orphanProduct} onChange={setOrphanProduct}
                options={["All",...orphanProducts].map(p=>({ value:p, label:p==="All"?"All products":p }))} />
              {orphanProduct!=="All"&&<span style={{ fontSize:12,color:"#8A93AA" }}>{filteredOrphans.length} of {visibleOrphans.length}</span>}
            </div>
          )}
          <div className="card" style={{ padding:0,overflow:"hidden" }}>
            <table style={{ width:"100%",borderCollapse:"collapse" }}>
              <thead><tr style={{ background:"#FFFFFF",borderBottom:"1px solid #E2E6F0" }}>
                {["Account","Vendor","Products","Est. €/mo","Detected","Comment","Status",""].map(h=><th key={h} style={{ padding:"11px 16px",textAlign:"left",fontSize:10,color:"#8A93AA",fontWeight:700,letterSpacing:".05em" }}>{h.toUpperCase()}</th>)}
              </tr></thead>
              <tbody>
                {filteredOrphans.map(o=>{
                  const resolved=o.status==="Resolved";
                  return (
                    <tr key={o.spId} className="tr" style={{ borderBottom:"1px solid #F0F2F7",opacity:resolved?0.55:1 }}>
                      <td style={{ padding:"12px 16px" }}><div style={{ fontWeight:600,fontSize:13 }}>{o.name||o.email}</div><div style={{ fontSize:11,color:"#8A93AA" }}>{o.vendor==="GitHub"&&ghProfileUrl(o.email) ? <a href={ghProfileUrl(o.email)} target="_blank" rel="noreferrer" style={{ color:"#2563EB",textDecoration:"none" }}>{o.email} ↗</a> : o.email}</div></td>
                      <td style={{ padding:"12px 16px",fontSize:12.5 }}>{o.vendor}</td>
                      <td style={{ padding:"12px 16px",fontSize:12,color:"#5A637A" }}>{o.products||"—"}</td>
                      <td style={{ padding:"12px 16px" }}><Mono color="#D97706">{eur(o.estMonthly)}</Mono></td>
                      <td style={{ padding:"12px 16px",fontSize:12,color:"#8A93AA" }}>{o.date||"—"}</td>
                      <td style={{ padding:"12px 16px",minWidth:180 }}>
                        <input className="inp" style={{ padding:"6px 10px",fontSize:12 }} placeholder="Add comment…" defaultValue={o.comment}
                          onBlur={e=>{ const v=e.target.value.trim(); if(v!==(o.comment||"")) saveOrphanComment(o.spId,v).catch(()=>toast("Couldn't save comment — add a \"Comment\" column to the LO_Orphans list")); }}
                          onKeyDown={e=>{ if(e.key==="Enter") e.target.blur(); }} />
                      </td>
                      <td style={{ padding:"12px 16px" }}><Badge label={resolved?"Resolved":"Open"} bg={resolved?"#ECFDF5":"#FEF2F2"} color={resolved?"#10B981":"#EF4444"} small/></td>
                      <td style={{ padding:"12px 16px" }}>{!resolved&&<button className="btn btnG btnSm" onClick={()=>resolveOrphan(o)}>Mark resolved</button>}</td>
                    </tr>
                  );
                })}
                {filteredOrphans.length===0&&<tr><td colSpan={8} style={{ padding:40,textAlign:"center",color:"#8A93AA",fontSize:13 }}>{visibleOrphans.length===0?"No orphaned accounts. They appear here after a sync finds vendor seats with no matching user, or Entra users with no department.":"No orphaned accounts match this product filter."}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>}

        {tab==="reporting"&&<div>
          <h1 style={{ fontSize:20,fontWeight:700,marginBottom:4 }}>Reporting</h1>
          <p style={{ color:"#8A93AA",fontSize:13,marginBottom:20 }}>Recent license changes and their monthly cost impact. History starts from when activity logging was enabled — earlier changes aren't recorded.</p>
          {activity===null&&<p style={{ color:"#8A93AA",fontSize:13 }}>Loading…</p>}
          {activity!==null&&activity.length===0&&<div className="card" style={{ textAlign:"center",padding:40,color:"#8A93AA",fontSize:13 }}>No changes recorded yet. License assignments and removals will appear here going forward.</div>}
          {activity!==null&&activity.length>0&&(()=>{
            const rows = activity.map(ev=>{ const t=tierByIdDyn(ev.tierId); const unit=t?monthlyEq(t):0; const flat=t?.seatModel==="flat"; const delta=flat?0:(ev.action==="Added"?unit:-unit); return {ev,t,unit,flat,delta}; });
            const net = rows.reduce((s,r)=>s+r.delta,0);
            const added=rows.filter(r=>r.ev.action==="Added").length, removed=rows.length-added;
            return (<>
              <div style={{ display:"flex",gap:12,marginBottom:18,flexWrap:"wrap" }}>
                <div className="card" style={{ flex:1,minWidth:180 }}><div style={{ fontSize:10,fontWeight:700,color:"#8A93AA",letterSpacing:".08em" }}>NET MONTHLY CHANGE</div><div style={{ fontSize:22,fontWeight:700,color:net>0?"#EF4444":net<0?"#10B981":"#1A1F36",marginTop:4 }}>{net>0?"+":""}{eur(net)}/mo</div><div style={{ fontSize:10.5,color:"#8A93AA",marginTop:2 }}>per-seat licenses · last {rows.length} change{rows.length!==1?"s":""}</div></div>
                <div className="card" style={{ flex:1,minWidth:120 }}><div style={{ fontSize:10,fontWeight:700,color:"#8A93AA",letterSpacing:".08em" }}>ADDED</div><div style={{ fontSize:22,fontWeight:700,color:"#1A1F36",marginTop:4 }}>{added}</div></div>
                <div className="card" style={{ flex:1,minWidth:120 }}><div style={{ fontSize:10,fontWeight:700,color:"#8A93AA",letterSpacing:".08em" }}>REMOVED</div><div style={{ fontSize:22,fontWeight:700,color:"#1A1F36",marginTop:4 }}>{removed}</div></div>
              </div>
              <div className="card" style={{ padding:0,overflow:"hidden" }}>
                <table style={{ width:"100%",borderCollapse:"collapse" }}>
                  <thead><tr style={{ background:"#FFFFFF",borderBottom:"1px solid #E2E6F0" }}>
                    {["When","User","Change","App & Tier","Δ €/mo","By"].map(h=><th key={h} style={{ padding:"11px 16px",textAlign:"left",fontSize:10,color:"#8A93AA",fontWeight:700,letterSpacing:".05em" }}>{h.toUpperCase()}</th>)}
                  </tr></thead>
                  <tbody>
                    {rows.map(r=>{ const add=r.ev.action==="Added"; return (
                      <tr key={r.ev.id} className="tr" style={{ borderBottom:"1px solid #F0F2F7" }}>
                        <td style={{ padding:"11px 16px",fontSize:11.5,color:"#8A93AA" }}>{r.ev.when?fmtDate(r.ev.when):"—"}</td>
                        <td style={{ padding:"11px 16px",fontSize:13,fontWeight:600 }}>{r.ev.userName||"—"}</td>
                        <td style={{ padding:"11px 16px" }}><Badge label={add?"Added":"Removed"} bg={add?"#ECFDF5":"#FEF2F2"} color={add?"#10B981":"#EF4444"} small/></td>
                        <td style={{ padding:"11px 16px",fontSize:12.5 }}>{r.t?<span>{r.t.appIcon} {r.t.appName} <span style={{ color:"#8A93AA" }}>{r.t.label}</span></span>:<span style={{ color:"#8A93AA" }}>{r.ev.tierId}</span>}{r.flat&&<Badge label="flat" bg="#F3F4F6" color="#6B7280" small/>}</td>
                        <td style={{ padding:"11px 16px" }}>{r.flat?<span style={{ fontSize:11,color:"#8A93AA" }}>shared</span>:<Mono color={add?"#EF4444":"#10B981"}>{add?"+":"−"}{eur(r.unit)}</Mono>}</td>
                        <td style={{ padding:"11px 16px",fontSize:11.5,color:"#5A637A" }}>{r.ev.by||"—"}</td>
                      </tr>
                    ); })}
                  </tbody>
                </table>
              </div>
              <p style={{ fontSize:11,color:"#8A93AA",marginTop:10 }}>Flat-priced licenses are listed but excluded from the net — their cost is fixed regardless of how many people hold them.</p>
            </>);
          })()}
        </div>}

        {tab==="settings"&&can(currentUser,"editConfig")&&<SettingsTab config={config} saveConfig={saveConfig} />}
      </div>

      {/* APPROVE MODAL */}
      {approveModal&&(
        <div className="modal-bg" onClick={()=>setApproveModal(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <h3 style={{ fontWeight:700,fontSize:16,marginBottom:6 }}>Approve Request</h3>
            <p style={{ fontSize:13,color:"#5A637A",marginBottom:16 }}>Add an optional note. The license will be automatically assigned.</p>
            <label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:6,fontWeight:700 }}>NOTE (optional)</label>
            <textarea className="inp" rows={3} placeholder="e.g. Approved for Q3 project work…" value={approveModal.note} onChange={e=>setApproveModal(p=>({...p,note:e.target.value}))} style={{ resize:"vertical" }}/>
            <div style={{ display:"flex",gap:10,marginTop:16 }}>
              <button className="btn btnP" onClick={()=>approveRequest(approveModal.reqId,approveModal.note)}>Confirm Approve</button>
              <button className="btn btnG" onClick={()=>setApproveModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ADD-TO-CATALOG (attach new-software request) MODAL */}
      {attachModal&&(()=>{
        const req=requests.find(r=>r.id===attachModal.reqId), reqUser=users.find(u=>u.id===req?.userId);
        return (
          <div className="modal-bg" onClick={()=>setAttachModal(null)}>
            <div style={{ background:"#FFFFFF",border:"1px solid #E2E6F0",borderRadius:16,padding:"28px",width:"90%",maxWidth:680,maxHeight:"85vh",overflowY:"auto" }} onClick={e=>e.stopPropagation()}>
              <h3 style={{ fontWeight:700,fontSize:16,marginBottom:6 }}>Add to Catalog & Attach</h3>
              <p style={{ fontSize:13,color:"#5A637A",marginBottom:16 }}>Creates the app (with one tier) in the catalog and attaches it to this request, so it can then be approved as normal.</p>
              {req?.newApp&&(
                <div style={{ background:"#F8F9FC",border:"1px solid #E2E6F0",borderRadius:10,padding:"12px 14px",marginBottom:16,fontSize:12.5,color:"#374060" }}>
                  <div style={{ fontSize:10,fontWeight:700,color:"#8A93AA",letterSpacing:".06em",marginBottom:6 }}>REQUESTED{reqUser?` BY ${reqUser.name.toUpperCase()}`:""}</div>
                  <div><b>{req.newApp.name}</b>{req.newApp.tier&&<> — {req.newApp.tier}</>}</div>
                  {req.newApp.options&&<div style={{ marginTop:3 }}>Options: {req.newApp.options}</div>}
                  {req.newApp.link&&<div style={{ marginTop:3 }}><a href={req.newApp.link} target="_blank" rel="noreferrer" style={{ color:"#2563EB",textDecoration:"none" }}>{req.newApp.link} ↗</a></div>}
                  {req.justification&&<div style={{ marginTop:3,color:"#5A637A" }}>“{req.justification}”</div>}
                </div>
              )}
              <div style={{ display:"grid",gridTemplateColumns:"2fr 1fr 1fr",gap:12,marginBottom:12 }}>
                <div><label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>APP NAME *</label>
                  <input className="inp" value={attachModal.name} onChange={e=>setAttachModal(p=>({...p,name:e.target.value}))}/></div>
                <div><label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>CATEGORY</label>
                  <input className="inp" placeholder="e.g. Design" value={attachModal.category} onChange={e=>setAttachModal(p=>({...p,category:e.target.value}))} list="attach-cats"/>
                  <datalist id="attach-cats">{[...new Set(catalog.map(a=>a.category))].map(c=><option key={c} value={c}/>)}</datalist></div>
                <div><label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>ICON</label>
                  <input className="inp" value={attachModal.icon} onChange={e=>setAttachModal(p=>({...p,icon:e.target.value}))}/></div>
              </div>
              <div style={{ marginBottom:12 }}>
                <label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>DESCRIPTION</label>
                <textarea className="inp" placeholder="What this app is for, who owns it, notes…" value={attachModal.description} onChange={e=>setAttachModal(p=>({...p,description:e.target.value}))} rows={2} style={{ resize:"vertical" }}/>
              </div>
              <p style={{ fontSize:10,fontWeight:700,color:"#8A93AA",letterSpacing:".06em",marginBottom:8 }}>TIER (THE ONE BEING REQUESTED)</p>
              <div style={{ display:"grid",gridTemplateColumns:"1.5fr 1fr 1fr 1fr 1fr",gap:12 }}>
                <div><label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>TIER NAME</label>
                  <input className="inp" placeholder="e.g. Professional" value={attachModal.tierLabel} onChange={e=>setAttachModal(p=>({...p,tierLabel:e.target.value}))}/></div>
                <div><label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>TYPE</label>
                  <select className="inp" value={attachModal.tierType} onChange={e=>setAttachModal(p=>({...p,tierType:e.target.value}))}><option>Standard</option><option>Non-Standard</option></select></div>
                <div><label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>BILLING</label>
                  <select className="inp" value={attachModal.billing} onChange={e=>setAttachModal(p=>({...p,billing:e.target.value}))}><option value="monthly">Monthly</option><option value="annual">Annual</option><option value="one-time">One-Time</option></select></div>
                <div><label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>SEAT MODEL</label>
                  <select className="inp" value={attachModal.seatModel} onChange={e=>setAttachModal(p=>({...p,seatModel:e.target.value}))}><option value="per-seat">Per Seat</option><option value="flat">Flat / Org-wide</option></select></div>
                <div><label style={{ fontSize:10,color:"#8A93AA",display:"block",marginBottom:4,fontWeight:700 }}>COST ({attachModal.billing==="monthly"?"/mo":attachModal.billing==="annual"?"/yr":"one-time"}) *</label>
                  <input className="inp" type="number" min="0" step="0.01" placeholder="0.00" value={attachModal.costPerUnit} onChange={e=>setAttachModal(p=>({...p,costPerUnit:e.target.value}))}/></div>
              </div>
              {attachModal.costPerUnit!==""&&(()=>{
                const c=parseFloat(attachModal.costPerUnit)||0, eq=attachModal.billing==="monthly"?c:attachModal.billing==="annual"?c/12:0;
                const ap=approvalTier(eq);
                return <p style={{ fontSize:11.5,color:"#5A637A",marginTop:10 }}>Once attached, this request will route as <b style={{ color:ap.color }}>{ap.label}</b> ({ap.threshold}) — approval stays manual.</p>;
              })()}
              <div style={{ display:"flex",gap:10,marginTop:16 }}>
                <button className="btn btnP" onClick={attachNewApp} disabled={!attachModal.name.trim()||attachModal.costPerUnit===""}>Add to Catalog & Attach</button>
                <button className="btn btnG" onClick={()=>setAttachModal(null)}>Cancel</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* BULK ASSIGN MODAL */}
      {bulkModal&&(()=>{
        const app=catalog.find(a=>a.id===bulkModal.appId), tier=app?.tiers.find(t=>t.tierId===bulkTierId);
        return (
          <div className="modal-bg" onClick={()=>setBulkModal(null)}>
            <div style={{ background:"#FFFFFF",border:"1px solid #E2E6F0",borderRadius:16,padding:"28px",width:"90%",maxWidth:680,maxHeight:"85vh",display:"flex",flexDirection:"column" }} onClick={e=>e.stopPropagation()}>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:18 }}>
                <div><h3 style={{ fontWeight:700,fontSize:16,marginBottom:4 }}>Bulk Assign — {app?.name}</h3><p style={{ fontSize:13,color:"#8A93AA" }}>Select a tier, then tick the users to assign</p></div>
                <button onClick={()=>setBulkModal(null)} style={{ background:"none",border:"none",cursor:"pointer",fontSize:18,color:"#8A93AA",lineHeight:1 }}>✕</button>
              </div>
              <div style={{ marginBottom:18 }}>
                <label style={{ fontSize:10,fontWeight:700,color:"#8A93AA",display:"block",marginBottom:6 }}>SELECT TIER</label>
                <div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>
                  {app?.tiers.filter(t=>!t.visibilityOnly).map(t=>{ const active=bulkTierId===t.tierId; return (
                    <button key={t.tierId} onClick={()=>onBulkTierChange(t.tierId)} style={{ padding:"8px 16px",borderRadius:8,border:`1.5px solid ${active?"#2563EB":"#E2E6F0"}`,background:active?"#EFF6FF":"#F8F9FC",cursor:"pointer",fontFamily:"inherit",fontWeight:active?700:500,fontSize:13,color:active?"#2563EB":"#374060",transition:"all .15s" }}>
                      {t.label} <span style={{ fontSize:11,fontWeight:400,color:active?"#3B82F6":"#8A93AA" }}>{eur(t.costPerUnit)}{ul(t)}</span>
                    </button>
                  ); })}
                </div>
              </div>
              {tier&&(<>
                <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10 }}>
                  <span style={{ fontSize:12,fontWeight:600,color:"#374060" }}>{Object.values(bulkSelected).filter(Boolean).length} of {assignableUsers.length} users selected</span>
                  <div style={{ display:"flex",gap:8 }}>
                    <button className="btn btnG btnSm" onClick={()=>{ const a={}; assignableUsers.forEach(u=>{a[u.id]=true;}); setBulkSelected(a); }}>Select All</button>
                    <button className="btn btnG btnSm" onClick={()=>{ const a={}; assignableUsers.forEach(u=>{a[u.id]=false;}); setBulkSelected(a); }}>Clear All</button>
                  </div>
                </div>
                <div style={{ overflowY:"auto",flex:1,marginBottom:18 }}>
                  {[...new Set(assignableUsers.map(u=>u.dept))].sort().map(dept=>(
                    <div key={dept} style={{ marginBottom:14 }}>
                      <div style={{ fontSize:10,fontWeight:700,color:"#8A93AA",letterSpacing:".06em",marginBottom:6,display:"flex",alignItems:"center",gap:8 }}>
                        {dept.toUpperCase()}
                        <button onClick={()=>{ const a={...bulkSelected}; assignableUsers.filter(u=>u.dept===dept).forEach(u=>{a[u.id]=true;}); setBulkSelected(a); }} style={{ background:"none",border:"none",cursor:"pointer",fontSize:10,color:"#2563EB",fontFamily:"inherit",fontWeight:700 }}>Select all</button>
                      </div>
                      <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:7 }}>
                        {assignableUsers.filter(u=>u.dept===dept).map(u=>{ const checked=!!bulkSelected[u.id], isDef=isDefaultTier(u.dept,bulkTierId); return (
                          <label key={u.id} style={{ display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderRadius:8,border:`1.5px solid ${checked?"#93AFEF":"#E2E6F0"}`,background:checked?"#EFF6FF":"#F8F9FC",cursor:"pointer" }}>
                            <input type="checkbox" checked={checked} onChange={e=>setBulkSelected(p=>({...p,[u.id]:e.target.checked}))} style={{ width:15,height:15,accentColor:"#2563EB",cursor:"pointer" }}/>
                            <div style={{ flex:1,minWidth:0 }}>
                              <div style={{ fontSize:13,fontWeight:600,color:"#1A1F36",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{u.name}</div>
                              <div style={{ fontSize:11,color:"#8A93AA" }}>{u.email}</div>
                            </div>
                            {isDef&&<span style={{ fontSize:10,color:"#2563EB",fontWeight:700,background:"#DBEAFE",borderRadius:10,padding:"1px 6px",flexShrink:0 }}>Default</span>}
                            {!isDef&&checked&&<span style={{ fontSize:10,color:"#2563EB",flexShrink:0 }}>✓</span>}
                          </label>
                        ); })}
                      </div>
                    </div>
                  ))}
                </div>
              </>)}
              <div style={{ display:"flex",gap:10,borderTop:"1px solid #E8EBF3",paddingTop:16 }}>
                <button className="btn btnP" onClick={saveBulkAssign}>Save Assignments</button>
                <button className="btn btnG" onClick={()=>setBulkModal(null)}>Cancel</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ATLASSIAN SYNC ERROR */}
      {atlasError&&(
        <div className="modal-bg" onClick={()=>setAtlasError(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <h3 style={{ fontWeight:700,fontSize:16,marginBottom:8 }}>Sync failed</h3>
            <p style={{ fontSize:13,color:"#DC2626",marginBottom:16,wordBreak:"break-word" }}>{atlasError}</p>
            <div style={{ display:"flex",gap:10 }}>
              <button className="btn btnG" onClick={()=>setAtlasError(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ATLASSIAN SYNC PREVIEW / CONFIRM */}
      {atlasPreview&&(()=>{
        const { providerLabel, newApps, newTiers, priceUpdates, assignments, revocations, orphans, skippedNoDept, unpriced, productCount, userCount } = atlasPreview;
        // "Nothing to apply" also accounts for orphans — applying logs them, so an
        // orphans-only preview must keep the button enabled.
        const nothing = !newApps.length && !newTiers.length && !priceUpdates.length && !assignments.length && !orphans.length;
        const orphanCost = orphans.reduce((s,o)=>s+o.estMonthly,0);
        return (
          <div className="modal-bg" onClick={()=>!atlasApplying&&setAtlasPreview(null)}>
            <div style={{ background:"#FFFFFF",border:"1px solid #E2E6F0",borderRadius:16,padding:"28px",width:"90%",maxWidth:680,maxHeight:"85vh",display:"flex",flexDirection:"column" }} onClick={e=>e.stopPropagation()}>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16 }}>
                <div>
                  <h3 style={{ fontWeight:700,fontSize:16,marginBottom:4 }}>{providerLabel} Sync — Preview</h3>
                  <p style={{ fontSize:13,color:"#8A93AA" }}>Read {productCount} product{productCount!==1?"s":""} and {userCount} user{userCount!==1?"s":""} from {providerLabel}. Nothing is saved until you apply.</p>
                </div>
                <button onClick={()=>setAtlasPreview(null)} style={{ background:"none",border:"none",cursor:"pointer",fontSize:18,color:"#8A93AA",lineHeight:1 }}>✕</button>
              </div>

              <div style={{ overflowY:"auto",flex:1,marginBottom:18,display:"flex",flexDirection:"column",gap:16 }}>
                {nothing&&!revocations.length&&!orphans.length&&<p style={{ fontSize:13,color:"#5A637A" }}>Everything is already in sync — no new apps, tiers, price changes or assignments.</p>}
                {nothing&&(!!revocations.length||!!orphans.length)&&<p style={{ fontSize:13,color:"#5A637A" }}>No changes to apply, but there are items below worth reviewing.</p>}

                {!!newApps.length&&(
                  <div>
                    <div style={{ fontSize:10,fontWeight:700,letterSpacing:".06em",color:"#8A93AA",marginBottom:8 }}>NEW APPS ({newApps.length})</div>
                    {newApps.map(a=>(
                      <div key={a.id} style={{ display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:"#F8F9FC",border:"1px solid #E2E6F0",borderRadius:8,marginBottom:6 }}>
                        <span style={{ fontSize:16 }}>{a.icon}</span>
                        <div style={{ flex:1 }}><div style={{ fontSize:13,fontWeight:600 }}>{a.name}</div><div style={{ fontSize:11,color:"#8A93AA" }}>{a.category}</div></div>
                        <span style={{ fontSize:12,color:"#374060" }}>{eur(a.tiers[0].costPerUnit)}{ul(a.tiers[0])}</span>
                      </div>
                    ))}
                  </div>
                )}

                {!!newTiers.length&&(
                  <div>
                    <div style={{ fontSize:10,fontWeight:700,letterSpacing:".06em",color:"#8A93AA",marginBottom:8 }}>NEW TIERS ON EXISTING APPS ({newTiers.length})</div>
                    {newTiers.map(nt=>(
                      <div key={nt.tier.tierId} style={{ display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:"#F8F9FC",border:"1px solid #E2E6F0",borderRadius:8,marginBottom:6 }}>
                        <div style={{ flex:1,fontSize:13 }}><b>{nt.appName}</b> — {nt.tier.label}</div>
                        <span style={{ fontSize:12,color:"#374060" }}>{eur(nt.tier.costPerUnit)}{ul(nt.tier)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {!!priceUpdates.length&&(
                  <div>
                    <div style={{ fontSize:10,fontWeight:700,letterSpacing:".06em",color:"#8A93AA",marginBottom:8 }}>PRICE UPDATES ({priceUpdates.length})</div>
                    {priceUpdates.map(pu=>(
                      <div key={pu.tierId} style={{ display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:"#F8F9FC",border:"1px solid #E2E6F0",borderRadius:8,marginBottom:6 }}>
                        <div style={{ flex:1,fontSize:13 }}><b>{pu.appName}</b> — {pu.label}</div>
                        <span style={{ fontSize:12,color:"#8A93AA",textDecoration:"line-through" }}>{eur(pu.oldCost)}{ul({billing:pu.oldBilling})}</span>
                        <span style={{ fontSize:12,color:"#8A93AA" }}>→</span>
                        <span style={{ fontSize:12,fontWeight:600,color:"#2563EB" }}>{eur(pu.newCost)}{ul({billing:pu.newBilling})}</span>
                      </div>
                    ))}
                  </div>
                )}

                {!!assignments.length&&(
                  <div>
                    <div style={{ fontSize:10,fontWeight:700,letterSpacing:".06em",color:"#8A93AA",marginBottom:8 }}>ASSIGNMENTS ({assignments.length})</div>
                    <div style={{ maxHeight:180,overflowY:"auto",background:"#F8F9FC",border:"1px solid #E2E6F0",borderRadius:8,padding:"8px 12px" }}>
                      {assignments.map((a,i)=>(
                        <div key={i} style={{ fontSize:12.5,color:"#374060",padding:"3px 0" }}>{a.userName} <span style={{ color:"#8A93AA" }}>→</span> {a.productName}</div>
                      ))}
                    </div>
                  </div>
                )}

                {!!unpriced.length&&(
                  <div style={{ background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:8,padding:"10px 12px",fontSize:12,color:"#92400E" }}>
                    ⚠ No price on file for: {unpriced.join(", ")}. Imported at €0 — set the cost in the catalog after applying.
                  </div>
                )}

                {!!revocations.length&&(
                  <div>
                    <div style={{ fontSize:10,fontWeight:700,letterSpacing:".06em",color:"#B45309",marginBottom:8 }}>REVOCATION DRIFT ({revocations.length}) — report only</div>
                    <div style={{ background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:8,padding:"8px 12px",marginBottom:6,fontSize:11.5,color:"#92400E" }}>
                      Assigned in LicenseOps but no longer backed by {providerLabel}. Not changed automatically — unassign manually in Users &amp; Licenses if appropriate.
                    </div>
                    <div style={{ maxHeight:150,overflowY:"auto",background:"#F8F9FC",border:"1px solid #E2E6F0",borderRadius:8,padding:"8px 12px" }}>
                      {revocations.map((r,i)=>(
                        <div key={i} style={{ fontSize:12,padding:"3px 0",display:"flex",justifyContent:"space-between",gap:10 }}>
                          <span style={{ color:"#374060" }}>{r.userName} <span style={{ color:"#8A93AA" }}>·</span> {r.productName}</span>
                          <span style={{ color:"#8A93AA",flexShrink:0 }}>{r.reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!!orphans.length&&(
                  <div>
                    <div style={{ fontSize:10,fontWeight:700,letterSpacing:".06em",color:"#B45309",marginBottom:8 }}>ORPHANED ATLASSIAN ACCOUNTS ({orphans.length}) — ~{eur(orphanCost)}/mo</div>
                    <div style={{ background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:8,padding:"8px 12px",marginBottom:6,fontSize:11.5,color:"#92400E" }}>
                      Have {providerLabel} product access but match no LicenseOps user (ex-employees, externals or service accounts). Potential wasted spend — review in {providerLabel} admin.
                    </div>
                    <div style={{ maxHeight:170,overflowY:"auto",background:"#F8F9FC",border:"1px solid #E2E6F0",borderRadius:8,padding:"8px 12px" }}>
                      {orphans.map((o,i)=>(
                        <div key={i} style={{ fontSize:12,padding:"4px 0",display:"flex",justifyContent:"space-between",gap:10 }}>
                          <span style={{ color:"#374060",minWidth:0 }}><span style={{ fontWeight:600 }}>{o.name||o.email}</span>{providerLabel==="GitHub"&&ghProfileUrl(o.email)&&<a href={ghProfileUrl(o.email)} target="_blank" rel="noreferrer" style={{ color:"#2563EB",textDecoration:"none",marginLeft:6 }}>@{o.email} ↗</a>} <span style={{ color:"#8A93AA" }}>· {o.products.join(", ")}</span></span>
                          <span style={{ color:"#8A93AA",flexShrink:0 }}>~{eur(o.estMonthly)}/mo</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!!skippedNoDept.length&&(
                  <div>
                    <div style={{ fontSize:10,fontWeight:700,letterSpacing:".06em",color:"#8A93AA",marginBottom:8 }}>SKIPPED — NO DEPARTMENT ({skippedNoDept.length})</div>
                    <div style={{ maxHeight:120,overflowY:"auto",background:"#F8F9FC",border:"1px solid #E2E6F0",borderRadius:8,padding:"8px 12px" }}>
                      {skippedNoDept.map((s,i)=>(
                        <div key={i} style={{ fontSize:12,padding:"3px 0",color:"#374060" }}>{s.name||s.email}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display:"flex",gap:10,borderTop:"1px solid #E8EBF3",paddingTop:16 }}>
                <button className="btn btnP" onClick={applyAtlassianSync} disabled={nothing||atlasApplying}>{atlasApplying?"Applying…":(!newApps.length&&!newTiers.length&&!priceUpdates.length&&!assignments.length&&orphans.length)?`Log ${orphans.length} orphan${orphans.length!==1?"s":""}`:"Apply Sync"}</button>
                <button className="btn btnG" onClick={()=>setAtlasPreview(null)} disabled={atlasApplying}>Cancel</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* CLEANUP CONFIRM MODAL */}
      {cleanupConfirm&&(
        <div className="modal-bg" onClick={()=>setCleanupConfirm(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <h3 style={{ fontWeight:700,fontSize:16,marginBottom:8 }}>Clean up users with no department</h3>
            <p style={{ fontSize:13,color:"#5A637A",marginBottom:14 }}>
              This will permanently remove {orphanUsers.length} user{orphanUsers.length!==1?"s":""} that have no department (or "General") set in Entra. Any licenses currently assigned to them will be unassigned.
            </p>
            <div style={{ maxHeight:180,overflowY:"auto",background:"#F8F9FC",border:"1px solid #E2E6F0",borderRadius:8,padding:"10px 12px",marginBottom:16 }}>
              {orphanUsers.map(u=>(
                <div key={u.id} style={{ fontSize:12,color:"#5A637A",padding:"3px 0" }}>{u.name} <span style={{ color:"#C0C8D8" }}>· {u.email}</span></div>
              ))}
            </div>
            <div style={{ display:"flex",gap:10 }}>
              <button className="btn btnSm" style={{ background:"#EF4444",color:"#fff",padding:"9px 18px",fontSize:13 }} onClick={cleanupOrphans} disabled={cleaning}>
                {cleaning?"Removing…":`Yes, remove ${orphanUsers.length}`}
              </button>
              <button className="btn btnG" onClick={()=>setCleanupConfirm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* TOAST */}
      {assignToast&&(
        <div style={{ position:"fixed",bottom:28,left:"50%",transform:"translateX(-50%)",background:"#1A1F36",color:"#FFFFFF",padding:"11px 22px",borderRadius:10,fontSize:13,fontWeight:600,boxShadow:"0 4px 20px #00000030",zIndex:200,display:"flex",alignItems:"center",gap:8 }}>
          <span style={{ color:"#10B981" }}>✓</span> {assignToast}
        </div>
      )}

      {/* DELETE CONFIRM MODAL */}
      {deleteConfirm&&(
        <div className="modal-bg" onClick={()=>setDeleteConfirm(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <h3 style={{ fontWeight:700,fontSize:16,marginBottom:8 }}>{deleteConfirm.type==="app"?"Delete Application":"Remove Tier"}</h3>
            <p style={{ fontSize:13,color:"#5A637A",marginBottom:20 }}>
              {deleteConfirm.type==="app"?`This will permanently delete "${catalog.find(a=>a.id===deleteConfirm.appId)?.name}" and all its tiers. Any users assigned to these tiers will have them removed.`:`This will permanently remove the "${catalog.find(a=>a.id===deleteConfirm.appId)?.tiers.find(t=>t.tierId===deleteConfirm.tierId)?.label}" tier.`}
            </p>
            <div style={{ display:"flex",gap:10 }}>
              <button className="btn btnSm" style={{ background:"#EF4444",color:"#fff",padding:"9px 18px",fontSize:13 }} onClick={()=>deleteConfirm.type==="app"?deleteApp(deleteConfirm.appId):deleteTier(deleteConfirm.appId,deleteConfirm.tierId)}>Yes, Delete</button>
              <button className="btn btnG" onClick={()=>setDeleteConfirm(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}